// CARO. Casa de estilo — The Edit (editorial) articles.
// One article per Edit category. Editorial, on-brand copy: calm, sophisticated,
// warm. No medical/health claims, no invented product facts, no controversy.
// Served as the source of truth for The Edit (see worker/routes/content.ts).

import type { Article } from '@/types';

export const EDIT_CATEGORIES = [
  { slug: 'estilo', label: 'Estilo' },
  { slug: 'belleza', label: 'Belleza' },
  { slug: 'bienestar', label: 'Bienestar' },
  { slug: 'casa', label: 'Casa' },
  { slug: 'mujer-40', label: 'Mujer 40+' },
] as const;

/**
 * Public slug of each article in EN and FR (`/en/the-edit/post/<en>`,
 * `/fr/the-edit/article/<fr>`), keyed by its Spanish `slug` (the logical key:
 * OG image, relatedProductSlugs, ARTICLE_REDIRECTS). Lowercase ASCII
 * `[a-z0-9-]`, unique across every locale. The single source of these slugs:
 * kept as one small table (not inside each article) because the client needs
 * it to build links and the articles' bodies must stay out of its bundle. A new
 * article gets its row here too.
 */
export const ARTICLE_SLUGS: Record<string, { en: string; fr: string }> = {
  'blue-monday-no-existe-cansancio-de-enero': { en: 'blue-monday-january-tiredness', fr: 'blue-monday-fatigue-de-janvier' },
  'vestirse-de-fiesta-sin-disfrazarse': { en: 'holiday-dressing-without-a-costume', fr: 'tenue-de-fete-sans-se-deguiser' },
  'hamed-ouattara-el-metal-que-cambia-de-vida': { en: 'hamed-ouattara-metal-with-a-second-life', fr: 'hamed-ouattara-le-metal-qui-change-de-vie' },
  'forma-de-cara-gafas-peinado-y-contouring': { en: 'face-shape-glasses-hair-and-contouring', fr: 'forme-du-visage-lunettes-coiffure-contouring' },
  'retinol-o-bakuchiol': { en: 'retinol-or-bakuchiol', fr: 'retinol-ou-bakuchiol' },
  'morfologia-las-seis-siluetas': { en: 'body-shape-the-six-silhouettes', fr: 'morphologie-les-six-silhouettes' },
  'guia-de-regalos-con-intencion': { en: 'gift-guide-with-intention', fr: 'guide-cadeaux-avec-intention' },
  'asesoria-de-imagen-color-silueta-y-rostro': { en: 'image-consulting-colour-silhouette-face', fr: 'conseil-en-image-couleur-silhouette-visage' },
  'como-combinar-una-chaqueta-verde-en-otono': { en: 'how-to-wear-a-green-jacket-in-autumn', fr: 'comment-porter-une-veste-verte-en-automne' },
  'bolso-rojo-como-llevar-el-color-de-la-temporada': { en: 'red-bag-colour-of-the-season', fr: 'sac-rouge-couleur-de-la-saison' },
  'manchas-despues-del-verano': { en: 'dark-spots-after-summer', fr: 'taches-apres-l-ete' },
  'estilo-mujer-que-evoluciona': { en: 'style-for-the-woman-who-evolves', fr: 'style-femme-qui-evolue' },
  'belleza-reconocerse-y-cuidarse': { en: 'beauty-skin-that-changes', fr: 'beaute-peau-qui-change' },
  'bienestar-sin-obsesion': { en: 'wellbeing-without-obsession', fr: 'bien-etre-sans-obsession' },
  'la-casa-como-extension-del-estilo': { en: 'home-as-an-extension-of-style', fr: 'maison-prolongement-du-style' },
  'mujer-40-la-edad-de-la-intencion': { en: 'women-40-the-age-of-intention', fr: 'femmes-40-l-age-de-l-intention' },
};

export const SEED_ARTICLES: Article[] = [
  {
    id: 'edit-bienestar-blue-monday',
    slug: 'blue-monday-no-existe-cansancio-de-enero',
    title: 'El Blue Monday no existe. El cansancio de enero, sí.',
    excerpt:
      'El lunes 18 te dirán que es el día más triste del año. No lo es. Pero enero cansa, y cuidarse con calma tiene sentido: tres cuidados pequeños, sin propósitos imposibles.',
    category: 'bienestar',
    author: 'Carolina Collaer',
    date: '2027-01-15',
    publishAt: '2027-01-15T08:00:00+01:00',
    heroImage: null,
    body: `El lunes 18 de enero lo vas a leer en todas partes: «hoy es el día más triste del año».

Te lo digo antes de que llegue: no lo es.

El famoso Blue Monday nació en 2005 como una campaña publicitaria de un canal de televisión de viajes británico, que quería vender vacaciones de invierno. Se presentó con una «fórmula» que mezclaba el tiempo, las deudas y los propósitos incumplidos. La ciencia nunca la ha respaldado.

Así que no, tu ánimo no depende del calendario.

## Lo que sí es real

Ahora bien, que el Blue Monday sea un invento no significa que enero sea fácil.

Hay menos luz. Hace frío. Volvemos a la rutina después de semanas de mesas largas, viajes y poco descanso. Y llega la cuesta de enero, con sus facturas y sus cuentas.

Es normal sentirse cansada. No es un fracaso ni una señal de que algo va mal. Es enero.

La pregunta no es cómo sobrevivir al «día más triste del año», sino cómo cuidarte durante un mes que pide más calma.

## Tres cuidados pequeños para enero

### 1. La piel: calmar, no corregir

Después de las fiestas y con el frío, la piel suele pedir lo mismo que nosotras: descanso.

No es momento de probar diez activos nuevos. Es momento de calmar y reforzar.

- La **Squalane & Bakuchiol Sleeping Pack** de Ondo Beauty 36.5, una mascarilla de noche que la marca formula para reforzar la barrera cutánea mientras duermes.
- Una **mascarilla de tejido calmante**, como la **Probiotics & Cica Water**, con agua de centella asiática y probióticos. La marca la indica también para piel sensible.
- Y, por la mañana, protección solar. También en invierno: la luz del día sigue ahí aunque el cielo esté gris.

### 2. El armario: mirar antes de comprar

Enero es el mes de las rebajas, y también el de comprar para animarse.

Antes de llenar el carrito, abre el armario. Saca lo que no te pones, recupera lo que habías olvidado y piensa qué te falta de verdad.

Una pieza bien elegida vale más que cinco gangas que no te representan. Y si no sabes por dónde empezar, para eso existe la asesoría de imagen de CARO.

### 3. El ritmo: propósitos que puedas mantener

No hace falta cambiar de vida el lunes.

Elige una sola cosa pequeña: caminar con luz de día, acostarte un poco antes, reservar un rato para ti cada semana. Lo que puedas sostener en febrero vale más que lo que prometas el uno de enero.

Cuidarse no debería convertirse en otra obligación.

## Lo que no haría

No compraría para animarme.

No empezaría diez hábitos nuevos el mismo lunes.

Y no me sentiría culpable por estar cansada en enero.

## Una nota sincera

El cansancio de enero pasa. Si la tristeza o el desánimo se alargan durante semanas, no es cosa del calendario, y merece hablarlo con un profesional. Pedir ayuda también es cuidarse.

## Preguntas frecuentes

### ¿De dónde viene el Blue Monday?

De una campaña publicitaria de 2005 de un canal de viajes británico. Se presentó como un cálculo científico, pero no tiene base científica y se ha desmentido muchas veces.

### ¿Por qué cuesta tanto enero?

Por una suma de cosas muy reales: menos horas de luz, el frío, la vuelta a la rutina y los gastos de las fiestas. No hace falta un día concreto para notarlo.

### ¿Por dónde empiezo si estoy cansada?

Por algo pequeño. Un gesto de cuidado que puedas repetir, en la piel, en el armario o en tu ritmo, vale más que un gran plan que abandones en una semana.`,
    seoTitle: 'El Blue Monday no existe. El cansancio de enero, sí · CARO. Casa de estilo',
    seoDescription:
      'El Blue Monday nació como campaña publicitaria, sin base científica. El cansancio de enero sí es real: tres cuidados pequeños para la piel, el armario y el ritmo.',
    relatedProductSlugs: [
      'ondo-squalane-bakuchiol-sleeping-pack',
      'ondo-probiotics-cica-water-soothing-mask',
      'meisani-aloe-lujah-sunscreen-serum-spf50',
    ],
    i18n: {
      en: {
        title: 'Blue Monday doesn’t exist. January tiredness does.',
        excerpt:
          'On Monday the 18th you’ll be told it is the saddest day of the year. It isn’t. But January is tiring, and gentle self-care makes sense: three small habits, no impossible resolutions.',
        body: `On Monday 18 January you’ll read it everywhere: “today is the saddest day of the year”.

Let me tell you before it arrives: it isn’t.

The famous Blue Monday was born in 2005 as an advertising campaign for a British travel TV channel that wanted to sell winter holidays. It came with a “formula” mixing the weather, debt and broken resolutions. Science has never backed it.

So no, your mood doesn’t depend on the calendar.

## What is real

That said, Blue Monday being made up doesn’t mean January is easy.

There is less light. It is cold. We go back to our routine after weeks of long meals, travel and little rest. And the January squeeze arrives, with its bills and its sums.

It is normal to feel tired. It isn’t a failure or a sign that something is wrong. It is January.

The question isn’t how to survive “the saddest day of the year”, but how to look after yourself through a month that asks for more calm.

## Three small habits for January

### 1. Your skin: soothe, don’t correct

After the holidays and with the cold, skin often asks for the same thing we do: rest.

This isn’t the time to try ten new actives. It is the time to soothe and strengthen.

- Ondo Beauty 36.5’s **Squalane & Bakuchiol Sleeping Pack**, an overnight mask the brand formulates to strengthen the skin barrier while you sleep.
- A **soothing sheet mask**, such as the **Probiotics & Cica Water**, with centella asiatica water and probiotics. The brand also recommends it for sensitive skin.
- And, in the morning, sun protection. In winter too: daylight is still there even when the sky is grey.

### 2. Your wardrobe: look before you buy

January is sales month, and also the month of buying to cheer yourself up.

Before you fill the basket, open your wardrobe. Take out what you don’t wear, rediscover what you had forgotten and think about what you really need.

One well-chosen piece is worth more than five bargains that don’t represent you. And if you don’t know where to start, that is what CARO’s image consulting is for.

### 3. Your rhythm: resolutions you can keep

There is no need to change your life on Monday.

Choose one small thing: walking in daylight, going to bed a little earlier, keeping some time for yourself each week. What you can keep up in February is worth more than what you promise on the first of January.

Self-care shouldn’t become another obligation.

## What I wouldn’t do

I wouldn’t shop to cheer myself up.

I wouldn’t start ten new habits on the same Monday.

And I wouldn’t feel guilty for being tired in January.

## An honest note

January tiredness passes. If sadness or low mood lasts for weeks, it isn’t the calendar, and it is worth talking to a professional. Asking for help is self-care too.

## Frequently asked questions

### Where does Blue Monday come from?

From a 2005 advertising campaign for a British travel channel. It was presented as a scientific calculation, but it has no scientific basis and has been debunked many times.

### Why is January so hard?

Because of a mix of very real things: fewer hours of daylight, the cold, the return to routine and the cost of the holidays. You don’t need a specific day to feel it.

### Where do I start if I’m tired?

With something small. One caring gesture you can repeat, for your skin, your wardrobe or your rhythm, is worth more than a big plan you drop within a week.`,
        seoTitle: 'Blue Monday doesn’t exist. January tiredness does · CARO. Casa de estilo',
        seoDescription:
          'Blue Monday began as an advertising campaign with no scientific basis. January tiredness is real: three small habits for your skin, your wardrobe and your rhythm.',
      },
      fr: {
        title: 'Le Blue Monday n’existe pas. La fatigue de janvier, si.',
        excerpt:
          'Lundi 18, on vous dira que c’est le jour le plus triste de l’année. Ce n’est pas le cas. Mais janvier fatigue, et prendre soin de soi avec calme a du sens : trois petits soins, sans résolutions impossibles.',
        body: `Le lundi 18 janvier, vous le lirez partout : « aujourd’hui, c’est le jour le plus triste de l’année ».

Je vous le dis avant qu’il n’arrive : ce n’est pas le cas.

Le fameux Blue Monday est né en 2005 d’une campagne publicitaire pour une chaîne de télévision britannique consacrée au voyage, qui voulait vendre des vacances d’hiver. Il était présenté avec une « formule » mêlant la météo, les dettes et les bonnes résolutions abandonnées. La science ne l’a jamais validée.

Alors non, votre humeur ne dépend pas du calendrier.

## Ce qui est réel

Cela dit, que le Blue Monday soit une invention ne veut pas dire que janvier soit facile.

Il y a moins de lumière. Il fait froid. Nous reprenons la routine après des semaines de longs repas, de voyages et de peu de repos. Et arrivent les comptes de janvier, avec leurs factures.

Il est normal de se sentir fatiguée. Ce n’est ni un échec ni le signe que quelque chose ne va pas. C’est janvier.

La question n’est pas de savoir comment survivre au « jour le plus triste de l’année », mais comment prendre soin de vous pendant un mois qui demande plus de calme.

## Trois petits soins pour janvier

### 1. La peau : apaiser, pas corriger

Après les fêtes et avec le froid, la peau demande souvent la même chose que nous : du repos.

Ce n’est pas le moment d’essayer dix nouveaux actifs. C’est le moment d’apaiser et de renforcer.

- Le **Squalane & Bakuchiol Sleeping Pack** d’Ondo Beauty 36.5, un masque de nuit que la marque formule pour renforcer la barrière cutanée pendant votre sommeil.
- Un **masque en tissu apaisant**, comme le **Probiotics & Cica Water**, à l’eau de centella asiatica et aux probiotiques. La marque le recommande aussi pour les peaux sensibles.
- Et, le matin, une protection solaire. En hiver aussi : la lumière du jour est là, même quand le ciel est gris.

### 2. La garde-robe : regarder avant d’acheter

Janvier est le mois des soldes, et aussi celui des achats pour se remonter le moral.

Avant de remplir le panier, ouvrez votre armoire. Sortez ce que vous ne portez pas, retrouvez ce que vous aviez oublié et réfléchissez à ce qui vous manque vraiment.

Une pièce bien choisie vaut plus que cinq bonnes affaires qui ne vous ressemblent pas. Et si vous ne savez pas par où commencer, c’est à cela que sert le conseil en image de CARO.

### 3. Le rythme : des résolutions que vous pouvez tenir

Inutile de changer de vie lundi.

Choisissez une seule petite chose : marcher à la lumière du jour, vous coucher un peu plus tôt, garder un moment pour vous chaque semaine. Ce que vous pourrez tenir en février vaut plus que ce que vous promettez le premier janvier.

Prendre soin de soi ne devrait pas devenir une obligation de plus.

## Ce que je ne ferais pas

Je n’achèterais pas pour me remonter le moral.

Je ne commencerais pas dix nouvelles habitudes le même lundi.

Et je ne me sentirais pas coupable d’être fatiguée en janvier.

## Une note sincère

La fatigue de janvier passe. Si la tristesse ou le découragement durent plusieurs semaines, ce n’est pas une question de calendrier, et cela mérite d’en parler à un professionnel. Demander de l’aide, c’est aussi prendre soin de soi.

## Questions fréquentes

### D’où vient le Blue Monday ?

D’une campagne publicitaire de 2005 pour une chaîne de voyage britannique. Il a été présenté comme un calcul scientifique, mais il n’a aucune base scientifique et a été démenti à de nombreuses reprises.

### Pourquoi janvier est-il si difficile ?

À cause d’un ensemble de choses bien réelles : moins d’heures de lumière, le froid, le retour à la routine et les dépenses des fêtes. Pas besoin d’un jour précis pour le ressentir.

### Par où commencer si je suis fatiguée ?

Par quelque chose de petit. Un geste de soin que vous pouvez répéter, pour votre peau, votre garde-robe ou votre rythme, vaut plus qu’un grand projet abandonné au bout d’une semaine.`,
        seoTitle: 'Le Blue Monday n’existe pas. La fatigue de janvier, si · CARO. Casa de estilo',
        seoDescription:
          'Le Blue Monday est né d’une campagne publicitaire, sans base scientifique. La fatigue de janvier est réelle : trois petits soins pour la peau, la garde-robe et le rythme.',
      },
    },
  },
  {
    id: 'edit-mujer-40-fiestas-sin-disfrazarse',
    slug: 'vestirse-de-fiesta-sin-disfrazarse',
    title: 'Vestirse de fiesta sin disfrazarse',
    excerpt:
      'Comidas, cenas y Nochevieja. Cómo vestirte para las fiestas con lo que ya tienes, una pieza que brille y una piel preparada, sin comprar nada para una sola noche.',
    category: 'mujer-40',
    author: 'Carolina Collaer',
    date: '2026-12-19',
    publishAt: '2026-12-19T08:00:00+01:00',
    heroImage: '/media/caro/mujer-40-fiestas-sin-disfrazarse-carolina-collaer-1200.webp',
    ogImage: '/media/caro/og/edit-vestirse-de-fiesta-sin-disfrazarse-22554ddb.jpg',
    body: `Diciembre llega con su calendario lleno: la comida de empresa, la cena con amigas, la familia, Nochevieja.

Y con él, una tentación muy conocida: comprar algo «de fiesta» que llevaremos una sola noche y que después se quedará en el armario, esperando una ocasión que nunca llega.

Yo propongo otra cosa. La fiesta no se compra: se construye con lo que ya tienes, una pieza que brille y mucha intención.

## La regla: una sola pieza protagonista

Brillo, color o textura, pero no todo a la vez. Una pieza protagonista y el resto, tranquilo.

Así no te sientes disfrazada, y el look sigue siendo tuyo.

## Tres ocasiones, tres maneras

### La comida familiar: color y textura

El **jersey de cuello Perkins verde**, de punto grueso con relieve y un sutil brillo, con el pantalón de siempre. Es festivo sin ser solemne, y cómodo para una sobremesa larga.

### La cena con amigas: el rojo como acento

Una base negra que ya tengas, un pantalón o un vestido sencillo, y el **bolso de charol rojo**. Un solo punto de color basta para que el look diga «hoy es una noche especial».

### Nochevieja: el brillo en los detalles

Negro de arriba abajo, el **bolso de charol negro** y una **pulsera Lind** de cordón trenzado y piedras naturales, con su piedra central de borde dorado. El brillo, en los accesorios. La elegancia, en todo lo demás.

## La piel también se prepara

Antes de una noche especial, propongo dedicar un momento a la piel. Sin complicaciones:

- **Por la mañana**, unas gotas del **Glow Drops** de Meisani, un sérum de vitamina C que la marca indica para la falta de luminosidad, siempre con protección solar.
- **Por la tarde**, una mascarilla de tejido hidratante, como la **Hyaluronic Acid & Algae** de Ondo Beauty 36.5. La marca indica dejarla actuar de 10 a 15 minutos y masajear después el sérum restante, sin aclarar.

Quince minutos para ti, antes de salir, también forman parte del look.

## Lo que no haría

No compraría un vestido para una sola noche.

No estrenaría zapatos el mismo día de la fiesta.

Y no me vestiría para parecer más joven. Me vestiría para parecerme a mí, en mi mejor versión.

## Si necesitas ayuda

A veces el problema no es la ropa, sino saber qué hacer con ella. Para eso existe la asesoría de imagen de CARO: ordenar lo que ya tienes, descubrir qué te representa y vestirte con intención, también en las fiestas.

## Preguntas frecuentes

### ¿El brillo tiene edad?

No. La clave no es la edad, sino la dosis: una pieza que brilla tiene más elegancia que un look entero de lentejuelas.

### ¿Y si no tengo nada «de fiesta»?

Seguramente tienes más de lo que crees. Una base negra o neutra, bien elegida, se convierte en un look de fiesta con un solo accesorio.

### ¿Llegará mi pedido antes de las fiestas?

Algunas piezas están en reasortimiento y su ficha lo indica. Si necesitas algo para una fecha concreta, escríbenos antes de pedirlo y te decimos cuándo puede llegar.`,
    seoTitle: 'Vestirse de fiesta sin disfrazarse · CARO. Casa de estilo',
    seoDescription:
      'Cómo vestirte para las fiestas de diciembre sin comprar nada para una sola noche: una pieza protagonista, tres ocasiones y una piel preparada.',
    relatedProductSlugs: [
      'surkana-jersey-cuello-perkins-relieve-verde',
      'surkana-bolso-hombro-charol-rojo',
      'surkana-bolso-hombro-charol-negro',
      'pulsera-lind-16lind202',
      'ondo-hyaluronic-acid-algae-moisture-boosting-mask',
      'meisani-glow-drops-vitamin-c-serum',
    ],
    i18n: {
      en: {
        title: 'Dressing up for the holidays without a costume',
        excerpt:
          'Lunches, dinners and New Year’s Eve. How to dress for the holidays with what you already own, one piece that shines and well-prepared skin, without buying anything for a single night.',
        body: `December arrives with a full calendar: the office lunch, dinner with friends, family, New Year’s Eve.

And with it, a familiar temptation: buying something “for a party” that we will wear for a single night and that will then stay in the wardrobe, waiting for an occasion that never comes.

I suggest something else. A party look isn’t bought: it is built from what you already own, one piece that shines and plenty of intention.

## The rule: a single statement piece

Shine, colour or texture, but not all at once. One statement piece and everything else calm.

That way you don’t feel in costume, and the look is still yours.

## Three occasions, three ways

### The family lunch: colour and texture

The **green Perkins-collar jumper**, in chunky knit with a relief pattern and a subtle sheen, with your usual trousers. Festive without being solemn, and comfortable for a long afternoon at the table.

### Dinner with friends: red as the accent

A black base you already own, trousers or a simple dress, and the **red patent bag**. A single touch of colour is enough for the look to say “tonight is special”.

### New Year’s Eve: shine in the details

Black from head to toe, the **black patent bag** and a **Lind bracelet** in braided cord and natural stones, with its central stone and golden rim. The shine in the accessories. The elegance in everything else.

## Your skin gets ready too

Before a special night, I suggest taking a moment for your skin. Nothing complicated:

- **In the morning**, a few drops of Meisani’s **Glow Drops**, a vitamin C serum the brand recommends for dullness, always with sunscreen.
- **In the afternoon**, a moisturising sheet mask, such as Ondo Beauty 36.5’s **Hyaluronic Acid & Algae**. The brand recommends leaving it on for 10 to 15 minutes and then massaging in the remaining serum, without rinsing.

Fifteen minutes for yourself before going out are part of the look too.

## What I wouldn’t do

I wouldn’t buy a dress for a single night.

I wouldn’t wear new shoes for the first time on the night of the party.

And I wouldn’t dress to look younger. I would dress to look like myself, at my best.

## If you need help

Sometimes the problem isn’t the clothes, but knowing what to do with them. That is what CARO’s image consulting is for: sorting what you already own, discovering what represents you and dressing with intention, at party time too.

## Frequently asked questions

### Does shine have an age?

No. The key isn’t age but the dose: one piece that shines is more elegant than a whole sequinned look.

### What if I have nothing “for a party”?

You probably have more than you think. A well-chosen black or neutral base becomes a party look with a single accessory.

### Will my order arrive before the holidays?

Some pieces are restocking and their page says so. If you need something for a specific date, write to us before ordering and we will tell you when it can arrive.`,
        seoTitle: 'Dressing up for the holidays without a costume · CARO. Casa de estilo',
        seoDescription:
          'How to dress for the December holidays without buying anything for a single night: one statement piece, three occasions and well-prepared skin.',
      },
      fr: {
        title: 'S’habiller pour les fêtes sans se déguiser',
        excerpt:
          'Déjeuners, dîners et Saint-Sylvestre. Comment s’habiller pour les fêtes avec ce que vous avez déjà, une pièce qui brille et une peau préparée, sans rien acheter pour une seule soirée.',
        body: `Décembre arrive avec un agenda bien rempli : le déjeuner d’entreprise, le dîner entre amies, la famille, la Saint-Sylvestre.

Et avec lui, une tentation bien connue : acheter quelque chose « de fête » que nous porterons un seul soir et qui restera ensuite dans l’armoire, à attendre une occasion qui ne vient jamais.

Je propose autre chose. La fête ne s’achète pas : elle se construit avec ce que vous avez déjà, une pièce qui brille et beaucoup d’intention.

## La règle : une seule pièce forte

De la brillance, de la couleur ou de la texture, mais pas tout à la fois. Une pièce forte, et le reste en retenue.

Ainsi, vous ne vous sentez pas déguisée, et la tenue reste la vôtre.

## Trois occasions, trois façons

### Le déjeuner en famille : couleur et texture

Le **pull col Perkins vert**, en grosse maille avec relief et une subtile brillance, avec votre pantalon habituel. Festif sans être solennel, et confortable pour une longue fin de repas.

### Le dîner entre amies : le rouge en accent

Une base noire que vous avez déjà, un pantalon ou une robe simple, et le **sac en vernis rouge**. Une seule touche de couleur suffit pour que la tenue dise « ce soir est spécial ».

### La Saint-Sylvestre : la brillance dans les détails

Du noir de la tête aux pieds, le **sac en vernis noir** et un **bracelet Lind** en cordon tressé et pierres naturelles, avec sa pierre centrale au bord doré. La brillance, dans les accessoires. L’élégance, dans tout le reste.

## La peau aussi se prépare

Avant une soirée spéciale, je vous propose de prendre un moment pour votre peau. Sans complication :

- **Le matin**, quelques gouttes du **Glow Drops** de Meisani, un sérum à la vitamine C que la marque recommande pour le manque d’éclat, toujours avec une protection solaire.
- **L’après-midi**, un masque en tissu hydratant, comme le **Hyaluronic Acid & Algae** d’Ondo Beauty 36.5. La marque recommande de le laisser poser 10 à 15 minutes, puis de masser le sérum restant, sans rincer.

Quinze minutes pour vous avant de sortir font aussi partie de la tenue.

## Ce que je ne ferais pas

Je n’achèterais pas une robe pour une seule soirée.

Je n’étrennerais pas de chaussures le soir même de la fête.

Et je ne m’habillerais pas pour paraître plus jeune. Je m’habillerais pour me ressembler, dans ma meilleure version.

## Si vous avez besoin d’aide

Parfois, le problème n’est pas les vêtements, mais savoir quoi en faire. C’est à cela que sert le conseil en image de CARO : trier ce que vous avez déjà, découvrir ce qui vous représente et vous habiller avec intention, y compris pour les fêtes.

## Questions fréquentes

### La brillance a-t-elle un âge ?

Non. La clé n’est pas l’âge, mais la dose : une pièce qui brille a plus d’élégance qu’une tenue entièrement pailletée.

### Et si je n’ai rien « de fête » ?

Vous avez sûrement plus que vous ne le pensez. Une base noire ou neutre, bien choisie, devient une tenue de fête avec un seul accessoire.

### Ma commande arrivera-t-elle avant les fêtes ?

Certaines pièces sont en réassort et leur fiche l’indique. Si vous avez besoin de quelque chose pour une date précise, écrivez-nous avant de commander et nous vous dirons quand cela peut arriver.`,
        seoTitle: 'S’habiller pour les fêtes sans se déguiser · CARO. Casa de estilo',
        seoDescription:
          'Comment s’habiller pour les fêtes de décembre sans rien acheter pour une seule soirée : une pièce forte, trois occasions et une peau préparée.',
      },
    },
  },
  {
    id: 'edit-casa-hamed-ouattara',
    slug: 'hamed-ouattara-el-metal-que-cambia-de-vida',
    title: 'Hamed Ouattara: el metal que cambia de vida',
    excerpt:
      'Bidones de aceite que se convierten en piezas de diseño. La historia del creador de Burkina Faso detrás de dos de las piezas de Casa, y cómo vivir con ellas.',
    category: 'casa',
    author: 'Carolina Collaer',
    date: '2026-12-05',
    publishAt: '2026-12-05T08:00:00+01:00',
    heroImage: null,
    body: `Hay objetos que se compran y objetos que se adoptan.

Los primeros llenan un espacio. Los segundos lo cambian, porque traen consigo una historia, un oficio y una manera de mirar el mundo.

Las piezas de Hamed Ouattara pertenecen a esta segunda familia.

## Quién es

Hamed Ouattara nació en Ouagadougou, Burkina Faso, en 1971. Estudió diseño en la ENSCI de París y trabaja desde su estudio en Ouagadougou, junto a artesanos locales a los que el taller también forma.

Su obra se expone internacionalmente en galerías de diseño.

## El material: bidones que cambian de vida

Lo que hace única su obra es el punto de partida. Hamed trabaja sobre todo con **bidones de aceite industriales recuperados**: los corta, los aplana y les da forma de nuevo, a mano, con muy poca maquinaria.

Soldadura, remaches, plegado. Técnicas de taller que convierten un material desechado en mobiliario y objetos con presencia escultórica.

Una de sus colecciones más conocidas se llama Bolibana, una palabra que, según su galería, evoca en bambara «el final inusual de un viaje, una transformación». Creo que no hay mejor manera de resumir su trabajo.

## La inspiración: la arquitectura del Sahel

Sus formas beben de la arquitectura sudano-saheliana, la de barro de ciudades como Djenné, Tombuctú o Bobo-Dioulasso. Volúmenes rotundos, fachadas frontales y una geometría que protege.

Por eso sus piezas tienen algo de arquitectura en miniatura. No son solo muebles: son pequeños edificios para vivir dentro de casa.

## Las piezas que hemos elegido

- El **taburete Deni** (2025), hecho con bidones de aceite de motor y chapa metálica. Mide 48 cm de alto, 32 de ancho y 32 de fondo.
- Las **tassabas**, un conjunto de tres piezas de tres tamaños (de 29 a 40 cm) y colores variados.

Al ser trabajo artesanal y en colores variados, cada pieza es distinta. Si quieres ver la tuya antes de decidir, escríbenos y te enviamos fotos.

## Cómo vivir con ellas

No las pondría en una vitrina. Una pieza así pide estar cerca, a la vista y a mano.

El taburete funciona en muchos sitios: junto al sofá, a los pies de la cama o en la entrada, como ese punto de color y textura que hace distinto un rincón conocido.

Las tassabas se pueden mostrar juntas, jugando con sus tres tamaños, o repartidas por la casa.

## Lo que no haría

No la rodearía de muchos objetos. Una pieza con carácter necesita aire alrededor.

No la trataría como decoración de temporada. Es para quedarse.

Y no la elegiría solo porque combine con el sofá. Elígela porque te cuenta algo.

## Preguntas frecuentes

### ¿Todas las piezas son iguales?

No. Son piezas artesanales y en colores variados, así que cada una es distinta. Escríbenos si quieres ver fotos de la pieza concreta.

### ¿Cómo llegan?

En una caja de protección, con un envío propio por pieza que se indica en su ficha.

### ¿Encajan en una casa de estilo clásico?

Sí. Una pieza contemporánea con historia convive muy bien con muebles heredados o clásicos. Es precisamente ese contraste el que le da carácter a una casa.`,
    seoTitle: 'Hamed Ouattara: el metal que cambia de vida · CARO. Casa de estilo',
    seoDescription:
      'Hamed Ouattara, diseñador de Burkina Faso que transforma bidones de aceite en piezas de diseño: su historia, su inspiración saheliana y cómo vivir con sus piezas.',
    relatedProductSlugs: ['hamed-ouattara-taburete', 'hamed-ouattara-tassaba-set'],
    i18n: {
      en: {
        title: 'Hamed Ouattara: metal with a second life',
        excerpt:
          'Oil drums turned into design pieces. The story of the Burkina Faso designer behind two of our Casa pieces, and how to live with them.',
        body: `Some objects are bought and some are adopted.

The first fill a space. The second change it, because they bring with them a story, a craft and a way of seeing the world.

Hamed Ouattara’s pieces belong to the second family.

## Who he is

Hamed Ouattara was born in Ouagadougou, Burkina Faso, in 1971. He studied design at ENSCI in Paris and works from his studio in Ouagadougou, alongside local artisans whom the workshop also trains.

His work is exhibited internationally in design galleries.

## The material: drums with a second life

What makes his work unique is the starting point. Hamed works mainly with **reclaimed industrial oil drums**: he cuts them, flattens them and reshapes them by hand, with very little machinery.

Welding, riveting, folding. Workshop techniques that turn discarded material into furniture and objects with a sculptural presence.

One of his best-known collections is called Bolibana, a word that, according to his gallery, evokes in Bamana “the unusual end of a journey, a transformation”. I can think of no better way to sum up his work.

## The inspiration: the architecture of the Sahel

His forms draw on Sudano-Sahelian architecture, the earthen architecture of cities such as Djenné, Timbuktu or Bobo-Dioulasso. Bold volumes, frontal façades and a protective geometry.

That is why his pieces feel like miniature architecture. They are not just furniture: they are small buildings to live with indoors.

## The pieces we chose

- The **Deni stool** (2025), made from engine oil drums and sheet metal. It is 48 cm high, 32 wide and 32 deep.
- The **tassabas**, a set of three pieces in three sizes (from 29 to 40 cm) and assorted colours.

Being handcrafted and in assorted colours, each piece is different. If you would like to see yours before deciding, write to us and we will send you photos.

## How to live with them

I wouldn’t put them in a display cabinet. A piece like this wants to be close, in sight and within reach.

The stool works in many places: next to the sofa, at the foot of the bed or in the hallway, as the touch of colour and texture that makes a familiar corner different.

The tassabas can be shown together, playing with their three sizes, or spread around the house.

## What I wouldn’t do

I wouldn’t surround it with lots of objects. A piece with character needs space around it.

I wouldn’t treat it as seasonal decoration. It is here to stay.

And I wouldn’t choose it just because it matches the sofa. Choose it because it tells you something.

## Frequently asked questions

### Are all the pieces the same?

No. They are handcrafted and come in assorted colours, so each one is different. Write to us if you would like photos of the specific piece.

### How do they arrive?

In a protective box, with their own per-piece shipping shown on their page.

### Do they suit a classic home?

Yes. A contemporary piece with a story sits very well with inherited or classic furniture. It is precisely that contrast that gives a home its character.`,
        seoTitle: 'Hamed Ouattara: metal with a second life · CARO. Casa de estilo',
        seoDescription:
          'Hamed Ouattara, the Burkina Faso designer who turns oil drums into design pieces: his story, his Sahelian inspiration and how to live with his work.',
      },
      fr: {
        title: 'Hamed Ouattara : le métal qui change de vie',
        excerpt:
          'Des bidons d’huile devenus pièces de design. L’histoire du créateur burkinabè derrière deux pièces de Casa, et comment vivre avec elles.',
        body: `Il y a des objets qu’on achète et des objets qu’on adopte.

Les premiers remplissent un espace. Les seconds le transforment, parce qu’ils apportent avec eux une histoire, un savoir-faire et une façon de regarder le monde.

Les pièces de Hamed Ouattara appartiennent à cette seconde famille.

## Qui il est

Hamed Ouattara est né à Ouagadougou, au Burkina Faso, en 1971. Il a étudié le design à l’ENSCI à Paris et travaille depuis son studio à Ouagadougou, aux côtés d’artisans locaux que l’atelier forme aussi.

Son œuvre est exposée à l’international dans des galeries de design.

## La matière : des bidons qui changent de vie

Ce qui rend son travail unique, c’est le point de départ. Hamed travaille surtout avec des **bidons d’huile industriels récupérés** : il les découpe, les aplatit et leur redonne forme, à la main, avec très peu de machines.

Soudure, rivetage, pliage. Des techniques d’atelier qui transforment un matériau mis au rebut en mobilier et en objets à la présence sculpturale.

L’une de ses collections les plus connues s’appelle Bolibana, un mot qui, selon sa galerie, évoque en bambara « la fin inhabituelle d’un voyage, une transformation ». Je ne vois pas de meilleure façon de résumer son travail.

## L’inspiration : l’architecture du Sahel

Ses formes puisent dans l’architecture soudano-sahélienne, celle en terre de villes comme Djenné, Tombouctou ou Bobo-Dioulasso. Des volumes francs, des façades frontales et une géométrie protectrice.

C’est pourquoi ses pièces ont quelque chose d’une architecture miniature. Ce ne sont pas seulement des meubles : ce sont de petits édifices à vivre à l’intérieur de la maison.

## Les pièces que nous avons choisies

- Le **tabouret Deni** (2025), réalisé avec des bidons d’huile moteur et de la tôle. Il mesure 48 cm de haut, 32 de large et 32 de profondeur.
- Les **tassabas**, un ensemble de trois pièces de trois tailles (de 29 à 40 cm) et de couleurs variées.

Comme il s’agit d’un travail artisanal aux couleurs variées, chaque pièce est différente. Si vous souhaitez voir la vôtre avant de décider, écrivez-nous et nous vous enverrons des photos.

## Comment vivre avec elles

Je ne les mettrais pas dans une vitrine. Une pièce comme celle-ci demande à être proche, visible et à portée de main.

Le tabouret fonctionne à bien des endroits : à côté du canapé, au pied du lit ou dans l’entrée, comme cette touche de couleur et de texture qui rend différent un coin familier.

Les tassabas peuvent se montrer ensemble, en jouant avec leurs trois tailles, ou réparties dans la maison.

## Ce que je ne ferais pas

Je ne l’entourerais pas de nombreux objets. Une pièce de caractère a besoin d’air autour d’elle.

Je ne la traiterais pas comme une décoration de saison. Elle est là pour rester.

Et je ne la choisirais pas seulement parce qu’elle s’accorde avec le canapé. Choisissez-la parce qu’elle vous raconte quelque chose.

## Questions fréquentes

### Toutes les pièces sont-elles identiques ?

Non. Ce sont des pièces artisanales aux couleurs variées, chacune est donc différente. Écrivez-nous si vous souhaitez des photos de la pièce précise.

### Comment arrivent-elles ?

Dans une caisse de protection, avec une livraison propre à chaque pièce, indiquée sur sa fiche.

### Conviennent-elles à un intérieur classique ?

Oui. Une pièce contemporaine chargée d’histoire s’accorde très bien avec des meubles hérités ou classiques. C’est justement ce contraste qui donne du caractère à une maison.`,
        seoTitle: 'Hamed Ouattara : le métal qui change de vie · CARO. Casa de estilo',
        seoDescription:
          'Hamed Ouattara, designer burkinabè qui transforme des bidons d’huile en pièces de design : son histoire, son inspiration sahélienne et comment vivre avec ses pièces.',
      },
    },
  },
  {
    id: 'edit-estilo-forma-de-cara',
    slug: 'forma-de-cara-gafas-peinado-y-contouring',
    title: 'Forma de cara: cómo reconocer la tuya y qué gafas, peinado y contouring le van',
    excerpt:
      'Las siete formas de cara (ovalada, redonda, cuadrada, rectangular, diamante, corazón y triangular), cómo reconocer la tuya con el pelo recogido y qué monturas, peinados, pendientes y contouring la acompañan.',
    category: 'estilo',
    author: 'Carolina Collaer',
    date: '2026-11-28',
    publishAt: '2026-11-28T08:00:00+01:00',
    heroImage: '/media/caro/edit/forma-de-cara-siete-formas-croquis-1200.webp',
    ogImage: '/media/caro/og/edit-forma-de-cara-gafas-peinado-y-contouring-1cdb0284.jpg',
    body: `Unas gafas pueden cambiar una cara más que un corte de pelo. Y unos pendientes, un flequillo o un toque de sombra bajo el pómulo siguen la misma lógica: acompañan la forma del rostro o van contra ella.

El visagismo es la parte de la asesoría de imagen que estudia esa forma. En [la guía de asesoría de imagen](/the-edit/post/asesoria-de-imagen-color-silueta-y-rostro) vimos un ejemplo de gafas y otro de contouring; hoy repasamos las siete formas de cara, una a una.

## Cómo reconocer tu forma de cara

Recógete el pelo, ponte frente al espejo con buena luz y mira tres cosas:

- **La parte más ancha**: la frente, los pómulos, la mandíbula, o más o menos igual.
- **El largo**: si tu rostro es claramente más largo que ancho o casi tan largo como ancho.
- **La mandíbula y el mentón**: angulosos, suaves y redondeados, o estrechos y en punta.

Son las tres preguntas del [test de forma de cara de CARO](/estudio-de-estilo/forma-de-cara): un minuto, sin fotos, y te propone tu forma con su ficha PDF gratis y anónima. Después puedes probar monturas en el probador del juego.

![Las siete formas de rostro del visagismo: ovalado, redondo, cuadrado, rectangular, triangular, triángulo invertido y diamante](/media/caro/edit/asesoria-de-imagen-7-formas-de-rostro-visagismo-es-1200.webp "Las siete formas de cara. El contorno es el de una cara real; la línea de puntos marca la forma geométrica que la resume. La cara de corazón aparece con su otro nombre, triángulo invertido.")

## El principio: aportar lo que falta

La regla de base es sencilla: lo que llevas cerca de la cara aporta lo que el rostro no tiene. Ángulos a un rostro de líneas curvas, curvas a uno anguloso, anchura a uno alargado, altura a uno corto. El maquillaje hace lo mismo con dos herramientas: la luz adelanta y la sombra hunde.

## Las siete formas, una a una

### Cara ovalada

Algo más larga que ancha, con la frente un poco más ancha que la mandíbula y contornos suaves. Es el rostro de referencia: casi todo le favorece, así que se elige por el efecto. Monturas tan anchas como el rostro, rectangulares para dar carácter u ojo de gato para elevar la mirada; casi cualquier corte, y en contouring solo una sombra ligera bajo los pómulos. [La cara ovalada, en detalle](/estudio-de-estilo/forma-de-cara/cara-ovalada).

### Cara redonda

Casi tan ancha como larga, con pómulos llenos y mandíbula suave. Le dan estructura las verticales y los ángulos: monturas rectangulares o cuadradas, algo más anchas que el rostro; volumen arriba, raya al lado y largos por debajo del mentón; pendientes largos y escotes en V. La sombra baja por los laterales, de las sienes a la mandíbula. [La cara redonda, en detalle](/estudio-de-estilo/forma-de-cara/cara-redonda).

### Cara cuadrada

Frente, pómulos y mandíbula de anchura parecida, con la mandíbula marcada. Las curvas suavizan: monturas redondas u ovaladas de perfil fino; ondas, capas alrededor del rostro y flequillo ladeado; aros y pendientes redondeados. La sombra va a las esquinas de la frente y a los ángulos de la mandíbula. [La cara cuadrada, en detalle](/estudio-de-estilo/forma-de-cara/cara-cuadrada).

### Cara rectangular

Claramente más larga que ancha, con frente, pómulos y mandíbula alineados. La equilibran las horizontales: monturas grandes, cuadradas o de aviador, con el puente bajo; flequillo y volumen a los lados, no arriba; pendientes de botón, cuellos altos y escotes barco. La sombra va al nacimiento del pelo y a los lados del mentón. [La cara rectangular, en detalle](/estudio-de-estilo/forma-de-cara/cara-rectangular).

### Cara de diamante

Pómulos anchos y altos, con la frente y la mandíbula más estrechas y el mentón fino. Se trata de dar anchura arriba y abajo: monturas de ojo de gato u ovaladas, con la parte de arriba marcada; flequillo o volumen en la frente y volumen a la altura del mentón; pendientes en lágrima. Sombra suave en la parte más ancha de los pómulos. [La cara de diamante, en detalle](/estudio-de-estilo/forma-de-cara/cara-diamante).

### Cara de corazón

Frente ancha, pómulos marcados y mentón estrecho, a veces en punta: un triángulo invertido. Se aligera arriba y se da presencia abajo: monturas de aviador, ovaladas o redondas, con más peso en la parte de abajo; flequillo ladeado o de cortina y volumen a la altura del mentón; pendientes más anchos abajo. Sombra en las sienes y bajo los pómulos. [La cara de corazón, en detalle](/estudio-de-estilo/forma-de-cara/cara-corazon).

### Cara triangular

La mandíbula es más ancha que la frente: es la contraria de la de corazón, así que se da presencia arriba. Monturas de ojo de gato o rectangulares, con la parte de arriba marcada; volumen en la coronilla y en las sienes, largos por debajo de la mandíbula; pendientes cortos y escotes en V. Sombra a lo largo de la mandíbula. [La cara triangular, en detalle](/estudio-de-estilo/forma-de-cara/cara-triangular).

## Antes de elegir montura

- **La escala cuenta tanto como la forma.** Unos rasgos finos se pierden detrás de una montura gruesa, aunque la forma sea la correcta; unos rasgos marcados aguantan más grosor.
- **La montura no tapa las cejas** ni sobrepasa la parte más ancha de la cara.
- **El color de la montura es un color junto a tu cara**, como en la colorimetría: si conoces tu estación, elige dentro de ella. El [test de colorimetría](/estudio-de-estilo/colorimetria) te da una primera pista.

## Lo que no haría

- No elegiría unas gafas solo con la tabla: me las probaría, de frente y de perfil, con la luz de todos los días.
- No haría un contouring de tutorial para diario. Basta un toque de sombra mate muy difuminado: si se ve la raya, sobra producto.
- No renunciaría a un corte que me encanta porque «no toca». La regla orienta; tu estilo decide.

## Y si quieres una mirada profesional

El test te ayuda a ponerle nombre a tu forma. En una sesión, Carolina mira tu rostro en persona, con tus gafas y tus pendientes, y lo cruza con tu color y tu silueta. Lo tienes todo en [los servicios de asesoría de imagen](/caro/servicios).

## Preguntas frecuentes

### ¿Y si mi cara está entre dos formas?

Es lo más normal: pocas caras son una figura geométrica perfecta. Quédate con la forma más cercana y toma ideas de la otra; a menudo las recomendaciones van en la misma dirección.

### ¿Las gafas de sol siguen las mismas reglas?

Sí: la lógica de la forma es la misma. Como suelen ser más grandes, aquí manda la escala: que acompañen el ancho de tu rostro sin desbordarlo.

### ¿Necesito una foto para hacer el test?

No. Son tres preguntas frente al espejo, con el pelo recogido, sin fotos y sin registro, y la ficha PDF es gratis y anónima.`,
    seoTitle: 'Forma de cara: gafas, peinado, contouring · CARO. Casa de estilo',
    seoDescription:
      'Cara ovalada, redonda, cuadrada, rectangular, diamante, corazón o triangular: cómo saber cuál es la tuya y qué gafas, peinados y contouring le van. Test gratis.',
    relatedProductSlugs: [],
    endLinks: ['face', 'services'],
    i18n: {
      en: {
        title: 'Face shape: how to recognise yours and which glasses, hair and contouring suit it',
        excerpt:
          'The seven face shapes (oval, round, square, oblong, diamond, heart and wide base), how to recognise yours with your hair tied back, and which frames, hairstyles, earrings and contouring go with it.',
        body: `A pair of glasses can change a face more than a haircut. And earrings, a fringe or a touch of shadow under the cheekbone follow the same logic: they either go with the shape of the face or against it.

Face shape analysis is the part of image consulting that studies that shape. In [our image consulting guide](/the-edit/post/asesoria-de-imagen-color-silueta-y-rostro) we saw one example of glasses and one of contouring; today we go through the seven face shapes, one by one.

## How to recognise your face shape

Tie your hair back, stand in front of the mirror in good light and look at three things:

- **The widest part**: the forehead, the cheekbones, the jaw, or roughly even.
- **The length**: whether your face is clearly longer than it is wide, or almost as long as it is wide.
- **The jaw and chin**: angular, soft and rounded, or narrow and pointed.

Those are the three questions of [CARO’s face shape quiz](/estudio-de-estilo/forma-de-cara): one minute, no photos, and it suggests your shape with its PDF, free and anonymous. Then you can try frames in the game’s fitting room.

![The seven face shapes: oval, round, square, rectangular, triangular, inverted triangle and diamond](/media/caro/edit/asesoria-de-imagen-7-formas-de-rostro-visagismo-en-1200.webp "The seven face shapes. The outline is that of a real face; the dotted line marks the geometric shape that sums it up. The heart face appears under its other name, inverted triangle.")

## The principle: bring what is missing

The basic rule is simple: what you wear near your face brings what the face doesn’t have. Angles to a curved face, curves to an angular one, width to a long one, height to a short one. Make-up does the same with two tools: light brings forward and shadow recedes.

## The seven shapes, one by one

### Oval face

Slightly longer than it is wide, with the forehead a little wider than the jaw and soft contours. It is the reference face: almost everything suits it, so you choose by effect. Frames as wide as the face, rectangular for character or cat-eye to lift the eyes; almost any haircut, and for contouring just a light shadow under the cheekbones. [The oval face in detail](/estudio-de-estilo/forma-de-cara/cara-ovalada).

### Round face

Almost as wide as it is long, with full cheeks and a soft jaw. Verticals and angles give it structure: rectangular or square frames, slightly wider than the face; volume on top, a side parting and lengths below the chin; long earrings and V-necks. The shadow runs down the sides, from temples to jaw. [The round face in detail](/estudio-de-estilo/forma-de-cara/cara-redonda).

### Square face

Forehead, cheekbones and jaw of similar width, with a strong jaw. Curves soften it: round or oval frames with a fine profile; waves, layers around the face and a side-swept fringe; hoops and rounded earrings. The shadow goes to the corners of the forehead and the angles of the jaw. [The square face in detail](/estudio-de-estilo/forma-de-cara/cara-cuadrada).

### Oblong face

Clearly longer than it is wide, with forehead, cheekbones and jaw in line. Horizontals balance it: large frames, square or aviator, with a low bridge; a fringe and volume at the sides, not on top; stud earrings, high necks and boat necklines. The shadow goes to the hairline and the sides of the chin. [The oblong face in detail](/estudio-de-estilo/forma-de-cara/cara-rectangular).

### Diamond face

Wide, high cheekbones, with a narrower forehead and jaw and a fine chin. The idea is to add width above and below: cat-eye or oval frames, with a defined top line; a fringe or volume at the forehead and volume at chin level; teardrop earrings. A soft shadow on the widest part of the cheekbones. [The diamond face in detail](/estudio-de-estilo/forma-de-cara/cara-diamante).

### Heart face

A wide forehead, defined cheekbones and a narrow chin, sometimes pointed: an inverted triangle. Lighten the top and give presence to the bottom: aviator, oval or round frames, with more weight below; a side-swept or curtain fringe and volume at chin level; earrings that widen at the bottom. Shadow at the temples and under the cheekbones. [The heart face in detail](/estudio-de-estilo/forma-de-cara/cara-corazon).

### Wide-base face

The jaw is wider than the forehead: the opposite of the heart, so presence goes on top. Cat-eye or rectangular frames, with a defined top line; volume at the crown and temples, lengths below the jaw; short or stud earrings and V-necks. Shadow along the jaw. [The wide-base face in detail](/estudio-de-estilo/forma-de-cara/cara-triangular).

## Before you choose a frame

- **Scale matters as much as shape.** Fine features get lost behind a thick frame, even when the shape is right; strong features carry more thickness.
- **The frame doesn’t hide the eyebrows** or go beyond the widest part of the face.
- **The colour of the frame is a colour next to your face**, just as in colour analysis: if you know your season, choose within it. The [colour analysis quiz](/estudio-de-estilo/colorimetria) gives you a first clue.

## What I wouldn’t do

- I wouldn’t choose glasses from the chart alone: I would try them on, from the front and in profile, in everyday light.
- I wouldn’t do tutorial-style contouring every day. A touch of matte shadow, well blended, is enough: if you can see the line, there is too much product.
- I wouldn’t give up a haircut I love because it is “not for me”. The rule guides; your style decides.

## And if you’d like a professional eye

The quiz helps you put a name to your shape. In a session, Carolina looks at your face in person, with your glasses and your earrings, and crosses it with your colouring and your silhouette. You’ll find everything in [our image consulting services](/caro/servicios).

## Frequently asked questions

### What if my face is between two shapes?

That is perfectly normal: few faces are a perfect geometric figure. Go with the closest shape and borrow ideas from the other; the recommendations often point the same way.

### Do sunglasses follow the same rules?

Yes: the logic of shape is the same. As they tend to be bigger, scale leads here: they should follow the width of your face without overflowing it.

### Do I need a photo for the quiz?

No. It is three questions in front of the mirror, with your hair tied back, no photos and no sign-up, and the PDF is free and anonymous.`,
        seoTitle: 'Face shapes: glasses, hair, contouring · CARO. Casa de estilo',
        seoDescription:
          'Oval, round, square, oblong, diamond, heart or wide-base face: how to tell which one is yours and which glasses, hairstyles and contouring suit it. Free quiz.',
      },
      fr: {
        title: 'Forme du visage : la reconnaître et choisir lunettes, coiffure et contouring',
        excerpt:
          'Les sept formes de visage (ovale, rond, carré, rectangulaire, losange, cœur et base large), comment reconnaître la vôtre les cheveux attachés et quelles montures, coiffures, boucles d’oreilles et quel contouring l’accompagnent.',
        body: `Une paire de lunettes peut changer un visage plus qu’une coupe de cheveux. Et des boucles d’oreilles, une frange ou une touche d’ombre sous la pommette suivent la même logique : elles accompagnent la forme du visage ou vont contre elle.

Le visagisme est la partie du conseil en image qui étudie cette forme. Dans [notre guide du conseil en image](/the-edit/post/asesoria-de-imagen-color-silueta-y-rostro), nous avons vu un exemple de lunettes et un de contouring ; aujourd’hui, nous passons en revue les sept formes de visage, une par une.

## Comment reconnaître la forme de votre visage

Attachez vos cheveux, placez-vous devant le miroir avec une bonne lumière et regardez trois choses :

- **La partie la plus large** : le front, les pommettes, la mâchoire, ou à peu près pareil.
- **La longueur** : votre visage est-il nettement plus long que large, ou presque aussi long que large ?
- **La mâchoire et le menton** : anguleux, doux et arrondis, ou étroits et pointus.

Ce sont les trois questions du [test de forme de visage de CARO](/estudio-de-estilo/forma-de-cara) : une minute, sans photo, et il vous propose votre forme avec sa fiche PDF gratuite et anonyme. Vous pouvez ensuite essayer des montures dans le jeu.

![Les sept formes de visage : ovale, rond, carré, rectangulaire, triangulaire, pyramide inversée et losange](/media/caro/edit/asesoria-de-imagen-7-formas-de-rostro-visagismo-fr-1200.webp "Les sept formes de visage. Le contour est celui d’un vrai visage ; la ligne de points marque la forme géométrique qui le résume. Le visage cœur y figure sous son autre nom, pyramide inversée.")

## Le principe : apporter ce qui manque

La règle de base est simple : ce que vous portez près du visage apporte ce que le visage n’a pas. Des angles à un visage aux lignes courbes, des courbes à un visage anguleux, de la largeur à un visage long, de la hauteur à un visage court. Le maquillage fait de même avec deux outils : la lumière avance et l’ombre creuse.

## Les sept formes, une par une

### Visage ovale

Un peu plus long que large, avec le front légèrement plus large que la mâchoire et des contours doux. C’est le visage de référence : presque tout lui va, on choisit donc selon l’effet. Des montures aussi larges que le visage, rectangulaires pour le caractère ou papillon pour relever le regard ; presque toutes les coupes, et en contouring une ombre légère sous les pommettes. [Le visage ovale en détail](/estudio-de-estilo/forma-de-cara/cara-ovalada).

### Visage rond

Presque aussi large que long, avec des pommettes pleines et une mâchoire douce. Les verticales et les angles le structurent : montures rectangulaires ou carrées, un peu plus larges que le visage ; du volume en haut, une raie sur le côté et des longueurs sous le menton ; pendants d’oreilles et décolletés en V. L’ombre descend sur les côtés, des tempes à la mâchoire. [Le visage rond en détail](/estudio-de-estilo/forma-de-cara/cara-redonda).

### Visage carré

Front, pommettes et mâchoire de largeur proche, avec une mâchoire marquée. Les courbes l’adoucissent : montures rondes ou ovales au profil fin ; ondulations, dégradé autour du visage et frange de côté ; créoles et boucles arrondies. L’ombre va aux coins du front et aux angles de la mâchoire. [Le visage carré en détail](/estudio-de-estilo/forma-de-cara/cara-cuadrada).

### Visage rectangulaire

Nettement plus long que large, avec front, pommettes et mâchoire alignés. Les horizontales l’équilibrent : grandes montures, carrées ou aviateur, avec un pont bas ; une frange et du volume sur les côtés, pas en haut ; puces d’oreilles, cols montants et encolures bateau. L’ombre va à la racine des cheveux et sur les côtés du menton. [Le visage rectangulaire en détail](/estudio-de-estilo/forma-de-cara/cara-rectangular).

### Visage losange

Des pommettes larges et hautes, avec un front et une mâchoire plus étroits et un menton fin. Il s’agit d’apporter de la largeur en haut et en bas : montures papillon ou ovales, au haut marqué ; une frange ou du volume sur le front et du volume à hauteur du menton ; boucles en goutte. Une ombre douce sur la partie la plus large des pommettes. [Le visage losange en détail](/estudio-de-estilo/forma-de-cara/cara-diamante).

### Visage cœur

Un front large, des pommettes marquées et un menton étroit, parfois pointu : une pyramide inversée. On allège le haut et on donne de la présence au bas : montures aviateur, ovales ou rondes, avec plus de poids en bas ; frange de côté ou rideau et volume à hauteur du menton ; boucles plus larges en bas. Une ombre sur les tempes et sous les pommettes. [Le visage cœur en détail](/estudio-de-estilo/forma-de-cara/cara-corazon).

### Visage base large

La mâchoire est plus large que le front : c’est l’inverse du cœur, on donne donc de la présence en haut. Montures papillon ou rectangulaires, au haut marqué ; du volume au sommet du crâne et aux tempes, des longueurs sous la mâchoire ; boucles courtes ou puces et décolletés en V. Une ombre le long de la mâchoire. [Le visage base large en détail](/estudio-de-estilo/forma-de-cara/cara-triangular).

## Avant de choisir une monture

- **L’échelle compte autant que la forme.** Des traits fins se perdent derrière une monture épaisse, même de la bonne forme ; des traits marqués supportent plus d’épaisseur.
- **La monture ne cache pas les sourcils** et ne dépasse pas la partie la plus large du visage.
- **La couleur de la monture est une couleur près de votre visage**, comme en colorimétrie : si vous connaissez votre saison, choisissez dedans. Le [test de colorimétrie](/estudio-de-estilo/colorimetria) vous donne une première piste.

## Ce que je ne ferais pas

- Je ne choisirais pas des lunettes seulement avec le tableau : je les essaierais, de face et de profil, à la lumière de tous les jours.
- Je ne ferais pas un contouring de tutoriel au quotidien. Une touche d’ombre mate bien estompée suffit : si l’on voit le trait, il y a trop de produit.
- Je ne renoncerais pas à une coupe que j’adore parce qu’elle « ne va pas ». La règle oriente ; votre style décide.

## Et si vous souhaitez un regard professionnel

Le test vous aide à nommer votre forme. En séance, Carolina regarde votre visage en personne, avec vos lunettes et vos boucles d’oreilles, et le croise avec votre couleur et votre silhouette. Vous trouverez tout dans [nos services de conseil en image](/caro/servicios).

## Questions fréquentes

### Et si mon visage est entre deux formes ?

C’est tout à fait normal : peu de visages sont une figure géométrique parfaite. Gardez la forme la plus proche et empruntez des idées à l’autre ; les recommandations vont souvent dans le même sens.

### Les lunettes de soleil suivent-elles les mêmes règles ?

Oui : la logique de la forme est la même. Comme elles sont souvent plus grandes, c’est l’échelle qui compte ici : elles accompagnent la largeur de votre visage sans la déborder.

### Faut-il une photo pour faire le test ?

Non. Ce sont trois questions devant le miroir, les cheveux attachés, sans photo et sans inscription, et la fiche PDF est gratuite et anonyme.`,
        seoTitle: 'Forme du visage : lunettes et coiffure · CARO. Casa de estilo',
        seoDescription:
          'Visage ovale, rond, carré, rectangulaire, losange, cœur ou base large : comment reconnaître le vôtre et quelles lunettes, coiffures et contouring lui vont.',
      },
    },
  },
  {
    id: 'edit-belleza-retinol-o-bakuchiol',
    slug: 'retinol-o-bakuchiol',
    title: 'Retinol o bakuchiol: cómo elegiría yo',
    excerpt:
      'Es una de las preguntas que más me hacen. No hay una respuesta universal, pero sí una manera sensata de decidir según tu piel y tu ritmo.',
    category: 'belleza',
    author: 'Carolina Collaer',
    date: '2026-11-21',
    publishAt: '2026-11-21T08:00:00+01:00',
    heroImage: null,
    body: `¿Retinol o bakuchiol? Es una de las preguntas que más escucho.

Y entiendo la duda. Uno tiene fama de eficaz y exigente; el otro, de suave y natural. Entre medias, muchísima información contradictoria.

No soy dermatóloga, y no voy a decirte cuál necesita tu piel. Lo que sí puedo hacer es contarte cómo lo decidiría yo, con sentido común y sin prisas.

## Qué es cada uno

El **retinol** es un derivado de la vitamina A y uno de los activos más conocidos de la cosmética para los signos de la edad. Funciona, pero pide paciencia: hay que introducirlo poco a poco.

El **bakuchiol** es un activo de origen vegetal que las marcas presentan como una alternativa natural al retinol. Suele elegirse cuando se busca algo más suave.

No son lo mismo. Por eso no hay un ganador: hay una elección según tu piel.

## Cómo elegiría

### Retinol, si…

- Tu piel lo tolera bien o quieres probarlo con calma.
- Puedes ser constante con una introducción gradual.
- Vas a usar protección solar todos los días, sin excepciones.

### Bakuchiol, si…

- Tu piel es sensible o reacciona con facilidad.
- Prefieres empezar por algo más suave.
- Buscas un gesto de noche sencillo, sin calendario.

Si estás embarazada o en periodo de lactancia, consulta con tu médico antes de usar retinol.

## Si eliges retinol: cómo empezar

El **Blue Elixir Retinol Serum** de Meisani lleva retinol puro al 0,1%, junto con niacinamida y agua de aloe. La marca indica:

- Usarlo solo de noche, con unas gotas sobre la piel limpia y seca.
- Empezar 2 o 3 veces por semana y aumentar la frecuencia según la tolerancia.
- Usar protección solar durante el día.

Por eso lo acompañaría siempre del **Aloe-Lujah Sunscreen Serum SPF50+** por la mañana.

## Si eliges bakuchiol: el gesto de noche

La **Squalane & Bakuchiol Sleeping Pack** de Ondo Beauty 36.5 es una mascarilla de noche que combina bakuchiol, escualano y niacinamida. La marca la indica también para piel madura y sensible. Se aplica una capa fina sobre la piel limpia y se deja actuar toda la noche.

Y para el contorno, la **crema de ojos de péptidos y bakuchiol**, mañana y noche, después del sérum.

## ¿Y los dos?

No los mezclaría al principio. Si empiezas con retinol, una opción sensata es alternar: las noches de retinol, retinol; las demás, la sleeping pack. Así la piel descansa sin que pierdas el ritmo.

Si tienes dudas sobre cómo combinarlos en tu caso, consulta con un dermatólogo.

## Lo que no haría

No empezaría con retinol todas las noches. La prisa es la mejor manera de abandonarlo.

No usaría retinol sin protección solar al día siguiente.

Y no compararía mi piel con la de nadie. La rutina que funciona es la que puedes mantener.

## Preguntas frecuentes

### ¿El bakuchiol hace lo mismo que el retinol?

Son activos distintos. Las marcas presentan el bakuchiol como alternativa al retinol, pero no es un sustituto idéntico. Por eso conviene elegir según tu piel y no según la moda.

### ¿Puedo usar retinol en verano?

Sí, con protección solar diaria, como indica la marca. Aun así, el otoño y el invierno son un buen momento para empezar con calma.

### ¿Qué hago si mi piel se irrita?

Reduce la frecuencia o haz una pausa. Si la irritación continúa, deja de usarlo y consulta a un dermatólogo.`,
    seoTitle: 'Retinol o bakuchiol: cómo elegiría yo · CARO. Casa de estilo',
    seoDescription:
      'Retinol o bakuchiol: qué es cada uno, cómo elegir según tu piel y cómo introducir el retinol poco a poco, con protección solar cada día.',
    relatedProductSlugs: [
      'meisani-blue-elixir-retinol-serum',
      'ondo-squalane-bakuchiol-sleeping-pack',
      'ondo-peptide-complex-bakuchiol-eye-cream',
      'meisani-aloe-lujah-sunscreen-serum-spf50',
    ],
    i18n: {
      en: {
        title: 'Retinol or bakuchiol: how I would choose',
        excerpt:
          'It is one of the questions I am asked most. There is no universal answer, but there is a sensible way to decide based on your skin and your pace.',
        body: `Retinol or bakuchiol? It is one of the questions I hear most.

And I understand the doubt. One has a reputation for being effective and demanding; the other, for being gentle and natural. In between, a great deal of contradictory information.

I am not a dermatologist, and I won’t tell you which one your skin needs. What I can do is tell you how I would decide, with common sense and without rushing.

## What each one is

**Retinol** is a vitamin A derivative and one of the best-known actives in skincare for the signs of ageing. It works, but it asks for patience: it has to be introduced gradually.

**Bakuchiol** is a plant-based active that brands present as a natural alternative to retinol. It is often chosen when something gentler is wanted.

They are not the same. That is why there is no winner: there is a choice based on your skin.

## How I would choose

### Retinol, if…

- Your skin tolerates it well, or you want to try it calmly.
- You can be consistent with a gradual introduction.
- You will wear sunscreen every day, without exception.

### Bakuchiol, if…

- Your skin is sensitive or reacts easily.
- You prefer to start with something gentler.
- You want a simple night step, without a schedule.

If you are pregnant or breastfeeding, ask your doctor before using retinol.

## If you choose retinol: how to start

Meisani’s **Blue Elixir Retinol Serum** contains 0.1% pure retinol, with niacinamide and aloe water. The brand recommends:

- Using it only at night, a few drops on clean, dry skin.
- Starting 2 or 3 times a week and increasing the frequency according to tolerance.
- Wearing sun protection during the day.

That is why I would always pair it with **Aloe-Lujah Sunscreen Serum SPF50+** in the morning.

## If you choose bakuchiol: the night step

Ondo Beauty 36.5’s **Squalane & Bakuchiol Sleeping Pack** is an overnight mask combining bakuchiol, squalane and niacinamide. The brand also recommends it for mature and sensitive skin. Apply a thin layer on clean skin and leave it on overnight.

And for the eye area, the **peptide and bakuchiol eye cream**, morning and night, after your serum.

## And both?

I wouldn’t mix them at first. If you start with retinol, a sensible option is to alternate: retinol on retinol nights; the sleeping pack on the others. Your skin rests without you losing the rhythm.

If you are unsure how to combine them in your case, see a dermatologist.

## What I wouldn’t do

I wouldn’t start with retinol every night. Rushing is the surest way to give it up.

I wouldn’t use retinol without sun protection the next day.

And I wouldn’t compare my skin with anyone else’s. The routine that works is the one you can keep up.

## Frequently asked questions

### Does bakuchiol do the same as retinol?

They are different actives. Brands present bakuchiol as an alternative to retinol, but it is not an identical substitute. That is why it is worth choosing for your skin, not for the trend.

### Can I use retinol in summer?

Yes, with daily sun protection, as the brand recommends. Even so, autumn and winter are a good time to start calmly.

### What should I do if my skin gets irritated?

Reduce the frequency or take a break. If the irritation continues, stop using it and see a dermatologist.`,
        seoTitle: 'Retinol or bakuchiol: how I would choose · CARO. Casa de estilo',
        seoDescription:
          'Retinol or bakuchiol: what each one is, how to choose for your skin and how to introduce retinol gradually, with sunscreen every day.',
      },
      fr: {
        title: 'Rétinol ou bakuchiol : comment je choisirais',
        excerpt:
          'C’est l’une des questions qu’on me pose le plus. Il n’y a pas de réponse universelle, mais il existe une façon sensée de décider selon votre peau et votre rythme.',
        body: `Rétinol ou bakuchiol ? C’est l’une des questions que j’entends le plus.

Et je comprends le doute. L’un a la réputation d’être efficace et exigeant ; l’autre, d’être doux et naturel. Entre les deux, énormément d’informations contradictoires.

Je ne suis pas dermatologue, et je ne vais pas vous dire lequel il faut à votre peau. Ce que je peux faire, c’est vous raconter comment je déciderais, avec bon sens et sans précipitation.

## Ce qu’est chacun

Le **rétinol** est un dérivé de la vitamine A et l’un des actifs les plus connus en cosmétique pour les signes de l’âge. Il fonctionne, mais il demande de la patience : il faut l’introduire progressivement.

Le **bakuchiol** est un actif d’origine végétale que les marques présentent comme une alternative naturelle au rétinol. On le choisit souvent quand on cherche quelque chose de plus doux.

Ce n’est pas la même chose. C’est pourquoi il n’y a pas de gagnant : il y a un choix selon votre peau.

## Comment je choisirais

### Le rétinol, si…

- Votre peau le tolère bien, ou vous voulez l’essayer en douceur.
- Vous pouvez être régulière avec une introduction progressive.
- Vous porterez une protection solaire tous les jours, sans exception.

### Le bakuchiol, si…

- Votre peau est sensible ou réagit facilement.
- Vous préférez commencer par quelque chose de plus doux.
- Vous cherchez un geste du soir simple, sans calendrier.

Si vous êtes enceinte ou allaitez, demandez l’avis de votre médecin avant d’utiliser du rétinol.

## Si vous choisissez le rétinol : comment commencer

Le **Blue Elixir Retinol Serum** de Meisani contient 0,1 % de rétinol pur, avec de la niacinamide et de l’eau d’aloe. La marque recommande :

- De l’utiliser uniquement le soir, quelques gouttes sur une peau propre et sèche.
- De commencer 2 ou 3 fois par semaine et d’augmenter la fréquence selon la tolérance.
- D’utiliser une protection solaire pendant la journée.

C’est pourquoi je l’associerais toujours à l’**Aloe-Lujah Sunscreen Serum SPF50+** le matin.

## Si vous choisissez le bakuchiol : le geste du soir

Le **Squalane & Bakuchiol Sleeping Pack** d’Ondo Beauty 36.5 est un masque de nuit qui associe bakuchiol, squalane et niacinamide. La marque le recommande aussi pour les peaux matures et sensibles. Il s’applique en couche fine sur une peau propre et agit toute la nuit.

Et pour le contour des yeux, la **crème aux peptides et au bakuchiol**, matin et soir, après le sérum.

## Et les deux ?

Je ne les mélangerais pas au début. Si vous commencez par le rétinol, une option sensée est d’alterner : le rétinol les soirs de rétinol ; le sleeping pack les autres. La peau se repose sans que vous perdiez le rythme.

Si vous hésitez sur la façon de les associer dans votre cas, consultez un dermatologue.

## Ce que je ne ferais pas

Je ne commencerais pas le rétinol tous les soirs. La précipitation est le meilleur moyen de l’abandonner.

Je n’utiliserais pas de rétinol sans protection solaire le lendemain.

Et je ne comparerais pas ma peau à celle des autres. La routine qui fonctionne, c’est celle que vous pouvez tenir.

## Questions fréquentes

### Le bakuchiol fait-il la même chose que le rétinol ?

Ce sont des actifs différents. Les marques présentent le bakuchiol comme une alternative au rétinol, mais ce n’est pas un substitut identique. Mieux vaut donc choisir selon votre peau, pas selon la tendance.

### Puis-je utiliser du rétinol en été ?

Oui, avec une protection solaire quotidienne, comme l’indique la marque. Cela dit, l’automne et l’hiver sont un bon moment pour commencer en douceur.

### Que faire si ma peau s’irrite ?

Réduisez la fréquence ou faites une pause. Si l’irritation persiste, arrêtez et consultez un dermatologue.`,
        seoTitle: 'Rétinol ou bakuchiol : comment je choisirais · CARO. Casa de estilo',
        seoDescription:
          'Rétinol ou bakuchiol : ce qu’est chacun, comment choisir selon votre peau et comment introduire le rétinol progressivement, avec une protection solaire chaque jour.',
      },
    },
  },
  {
    id: 'edit-estilo-morfologia-seis-siluetas',
    slug: 'morfologia-las-seis-siluetas',
    title: 'Morfología: las seis siluetas y qué prendas favorecen a cada una',
    excerpt:
      'Qué mira la morfología, cómo reconocer tu silueta frente al espejo (sin medidas ni báscula) y las claves de cada una de las seis: A, V, H, I, O y X. Con el test de silueta gratuito de CARO para empezar.',
    category: 'estilo',
    author: 'Carolina Collaer',
    date: '2026-11-14',
    publishAt: '2026-11-14T08:00:00+01:00',
    heroImage: '/media/caro/edit/morfologia-seis-siluetas-croquis-1200.webp',
    ogImage: '/media/caro/og/edit-morfologia-las-seis-siluetas-4d2c7f55.jpg',
    body: `Hay quien lleva años comprando la misma falda que nunca termina de convencer, y quien encuentra a la primera el vestido que se pondría siempre. Casi nunca es cuestión de talla. Es cuestión de línea.

La morfología es la parte de la asesoría de imagen que estudia esa línea: la proporción entre hombros, cintura y caderas. En [la guía de asesoría de imagen](/the-edit/post/asesoria-de-imagen-color-silueta-y-rostro) la presentamos con dos ejemplos; hoy repasamos las seis siluetas, una a una.

## Qué mira la morfología (y qué no)

No mira el peso, ni la talla, ni la edad. Una talla 36 y una 46 pueden tener la misma silueta. Compara tres anchuras y una estructura:

- **Los hombros**, respecto a las caderas.
- **La cintura**: si se marca con claridad o poco.
- **Dónde se concentra el volumen**: arriba, en el centro, abajo o repartido.
- **La estructura**: fina y alargada, con curvas suaves o entre las dos.

Con eso, cada silueta recibe la letra que dibuja su forma. Ninguna es mejor que otra y ninguna se «corrige»: se trata de elegir el efecto que quieres.

![Las seis siluetas de la morfología, A, V, H, I, O y X, con las líneas de hombros, cintura y caderas](/media/caro/edit/asesoria-de-imagen-6-siluetas-morfologia-1200.webp "Las seis siluetas. Las líneas marcan hombros, cintura y caderas: lo que se compara es la proporción entre ellas.")

## Cómo reconocer tu silueta frente al espejo

Lo más fiable no es la cinta métrica, sino el espejo. Ponte de frente, con ropa ajustada y buena luz. Mira tus hombros y tus caderas: ¿cuáles son más anchos, o son parecidos? Mira tu cintura: ¿se marca o casi no? Y fíjate en dónde se reparte el volumen.

Es lo que hace el [test de silueta de CARO](/estudio-de-estilo/morfologia): cuatro preguntas visuales, sin medidas, sin peso y sin fotos. En un par de minutos te propone tu silueta, con su ficha PDF gratis y anónima, y nada sale de tu dispositivo.

## Las seis siluetas, una a una

### Silueta en A

Caderas más anchas que los hombros, a menudo con la cintura bien marcada. La clave: luz y estructura arriba, líneas fluidas abajo. Escotes barco o cuadrados, hombros con estructura y el color cerca del rostro; faldas en A y pantalones rectos o anchos, en tonos más sobrios que arriba. [La silueta en A, en detalle](/estudio-de-estilo/morfologia/silueta-en-a).

### Silueta en V

Hombros o busto más anchos que las caderas. La clave es la contraria: líneas limpias arriba, volumen y color abajo. Escotes en V, tejidos fluidos y hombros sin hombreras ni volantes; faldas con vuelo o plisadas y pantalones anchos o acampanados, que es donde van los estampados. [La silueta en V, en detalle](/estudio-de-estilo/morfologia/silueta-en-v).

### Silueta en H

Hombros, cintura y caderas de anchuras parecidas, con la cintura poco marcada. Hay dos caminos: crear un punto de cintura (tiro alto, cinturón ancho, peplum) o subrayar la vertical con chaquetas rectas de corte limpio. [La silueta en H, en detalle](/estudio-de-estilo/morfologia/silueta-en-h).

### Silueta en I

Como la H, pero en una estructura fina y alargada, de huesos finos. Aquí se puede sumar sin miedo: volumen, capas y textura. Mangas con volumen, escotes barco o redondos, faldas con vuelo y pantalones anchos; los estampados le sientan bien. [La silueta en I, en detalle](/estudio-de-estilo/morfologia/silueta-en-i).

### Silueta en O

El volumen se concentra en el centro y la cintura se marca poco, a menudo con piernas finas y un bonito escote. La clave: líneas verticales, escote abierto y tejidos con caída. Escotes en V, chaquetas largas y abiertas, vestidos imperio; mejor sin cinturón en la cintura. [La silueta en O, en detalle](/estudio-de-estilo/morfologia/silueta-en-o).

### Silueta en X

Hombros y caderas en equilibrio y la cintura bien marcada. La clave es seguir tu línea: cruzados, entallados, cinturón a la cintura, faldas lápiz o en A y pantalones de tiro alto. Lo que la apaga son las prendas sin forma, que la convierten en una H. [La silueta en X, en detalle](/estudio-de-estilo/morfologia/silueta-en-x).

## Tres ideas que sirven a todas

- **La mirada va a la luz y al detalle.** Lo claro, lo brillante y lo estampado atraen el ojo; lo oscuro y liso lo calma. Pon el interés donde quieres que se mire.
- **El largo cuenta tanto como el corte.** Una chaqueta que termina justo en la parte más ancha la subraya; unos centímetros más arriba o más abajo cambian el efecto.
- **La tela decide.** Un tejido con caída acompaña el cuerpo; uno rígido lo construye. Sirve igual en una A que en una O.

## Lo que no haría

- No me tomaría la letra como un juicio: describe una proporción, no una talla, y no dice nada de lo bien o mal que te ves.
- No tiraría lo que «no toca» según la tabla. Muchas veces basta con cambiar el largo, el cinturón o con qué lo combinas.
- No me quedaría solo con la silueta: el color y el rostro también cuentan, y tu estilo pesa más que cualquier regla.

## Y si quieres una mirada profesional

El test te da una pista y un vocabulario. Pero entre dos siluetas vecinas, y con tu armario delante, la diferencia la marca un ojo entrenado. Carolina lo trabaja en persona, con tus propias prendas, y lo cruza con tu color y tu rostro. Lo tienes todo en [los servicios de asesoría de imagen](/caro/servicios).

## Preguntas frecuentes

### ¿Mi silueta cambia si cambia mi peso?

La proporción de base depende mucho de la estructura (la anchura de hombros y caderas), así que suele mantenerse. Lo que sí puede cambiar es dónde se concentra el volumen: si ya no te reconoces en tu letra de hace años, vuelve a mirarte.

### ¿Y si me reconozco en dos siluetas?

Es muy habitual: las letras son referencias, no cajas. Quédate con la más cercana y toma ideas de la otra. En el test, si no te reconoces en la propuesta, puedes elegir otra silueta.

### ¿El test me pide medidas o fotos?

No. Son cuatro preguntas frente al espejo, sin medidas, sin peso y sin fotos, y la ficha PDF es gratis y anónima: sin email y sin registro.`,
    seoTitle: 'Morfología femenina: las 6 siluetas · CARO. Casa de estilo',
    seoDescription:
      'Silueta en A, V, H, I, O o X: cómo reconocer la tuya frente al espejo y qué escotes, cinturas, chaquetas, faldas y vestidos la equilibran. Con test gratis.',
    relatedProductSlugs: [],
    endLinks: ['silhouette', 'services'],
    i18n: {
      en: {
        title: 'Body shape: the six silhouettes and what suits each one',
        excerpt:
          'What body shape analysis looks at, how to recognise your silhouette in the mirror (no tape measure, no scales) and the keys to each of the six: A, V, H, I, O and X. With CARO’s free silhouette quiz to start.',
        body: `Some of us spend years buying the same skirt that never quite works, while others find the dress they would wear forever at the first try. It is rarely a question of size. It is a question of line.

Body shape analysis is the part of image consulting that studies that line: the proportion between shoulders, waist and hips. In [our image consulting guide](/the-edit/post/asesoria-de-imagen-color-silueta-y-rostro) we introduced it with two examples; today we go through the six silhouettes, one by one.

## What body shape looks at (and what it doesn’t)

It doesn’t look at weight, size or age. A size 8 and a size 18 can share the same silhouette. It compares three widths and a frame:

- **The shoulders**, compared with the hips.
- **The waist**: clearly defined or barely.
- **Where the volume sits**: on top, in the middle, below or evenly spread.
- **The frame**: slender and elongated, softly curved or somewhere in between.

From that, each silhouette gets the letter that draws its shape. None is better than another and none needs “correcting”: it is about choosing the effect you want.

![The six body shapes, A, V, H, I, O and X, with the shoulder, waist and hip lines](/media/caro/edit/asesoria-de-imagen-6-siluetas-morfologia-1200.webp "The six silhouettes. The lines mark shoulders, waist and hips: what matters is the proportion between them.")

## How to recognise your silhouette in the mirror

The most reliable tool isn’t a tape measure, it is the mirror. Stand facing it, in close-fitting clothes and good light. Look at your shoulders and hips: which are wider, or are they similar? Look at your waist: is it defined or hardly at all? And notice where the volume sits.

That is what [CARO’s silhouette quiz](/estudio-de-estilo/morfologia) does: four visual questions, with no measurements, no weight and no photos. In a couple of minutes it suggests your silhouette, with its PDF, free and anonymous, and nothing leaves your device.

## The six silhouettes, one by one

### The A silhouette

Hips wider than the shoulders, often with a well-defined waist. The key: light and structure on top, fluid lines below. Boat or square necklines, structured shoulders and colour near the face; A-line skirts and straight or wide trousers, in quieter shades than on top. [The A silhouette in detail](/estudio-de-estilo/morfologia/silueta-en-a).

### The V silhouette

Shoulders or bust wider than the hips. The key is the opposite: clean lines on top, volume and colour below. V-necks, fluid fabrics and shoulders with no pads or ruffles; full or pleated skirts and wide or flared trousers, which is where the prints go. [The V silhouette in detail](/estudio-de-estilo/morfologia/silueta-en-v).

### The H silhouette

Shoulders, waist and hips of similar widths, with little waist definition. There are two routes: create a waist (high rise, a wide belt, a peplum) or underline the vertical with straight, clean-cut jackets. [The H silhouette in detail](/estudio-de-estilo/morfologia/silueta-en-h).

### The I silhouette

Like the H, but on a slender, elongated, fine-boned frame. Here you can add without fear: volume, layers and texture. Full sleeves, boat or round necklines, full skirts and wide trousers; prints suit it well. [The I silhouette in detail](/estudio-de-estilo/morfologia/silueta-en-i).

### The O silhouette

The volume gathers in the middle and the waist is barely defined, often with slim legs and a lovely neckline. The key: vertical lines, an open neckline and fabrics that drape. V-necks, long open jackets, empire dresses; better without a belt at the waist. [The O silhouette in detail](/estudio-de-estilo/morfologia/silueta-en-o).

### The X silhouette

Shoulders and hips in balance and a well-defined waist. The key is to follow your line: wrap styles, fitted cuts, a belt at the waist, pencil or A-line skirts and high-rise trousers. What dims it is shapeless clothing, which turns it into an H. [The X silhouette in detail](/estudio-de-estilo/morfologia/silueta-en-x).

## Three ideas that work for all of them

- **The eye goes to light and detail.** Light, shiny and printed pieces draw the eye; dark, plain ones calm it. Put the interest where you want people to look.
- **Length matters as much as cut.** A jacket that ends right at the widest part underlines it; a few centimetres higher or lower changes the effect.
- **Fabric decides.** A fabric that drapes follows the body; a stiff one builds it. That works for an A as much as for an O.

## What I wouldn’t do

- I wouldn’t take the letter as a verdict: it describes a proportion, not a size, and says nothing about how good you look.
- I wouldn’t throw out what the chart says is “not for me”. Changing the length, the belt or what you pair it with is often enough.
- I wouldn’t stop at the silhouette: colour and face count too, and your style weighs more than any rule.

## And if you’d like a professional eye

The quiz gives you a clue and a vocabulary. But between two neighbouring silhouettes, with your wardrobe in front of you, the difference comes from a trained eye. Carolina works on it in person, with your own clothes, and crosses it with your colouring and your face. You’ll find everything in [our image consulting services](/caro/servicios).

## Frequently asked questions

### Does my silhouette change if my weight changes?

The basic proportion depends a lot on your frame (the width of shoulders and hips), so it usually stays. What can change is where the volume sits: if you no longer recognise yourself in the letter you had years ago, take another look.

### What if I recognise myself in two silhouettes?

That is very common: the letters are references, not boxes. Go with the closest and borrow ideas from the other. In the quiz, if the suggestion doesn’t feel like you, you can choose another silhouette.

### Does the quiz ask for measurements or photos?

No. It is four questions in front of the mirror, with no measurements, no weight and no photos, and the PDF is free and anonymous: no email and no sign-up.`,
        seoTitle: 'Body shape types: the 6 silhouettes · CARO. Casa de estilo',
        seoDescription:
          'A, V, H, I, O or X silhouette: how to recognise yours in the mirror and which necklines, waists, jackets, skirts and dresses balance it. With a free quiz.',
      },
      fr: {
        title: 'Morphologie : les six silhouettes et ce qui va à chacune',
        excerpt:
          'Ce que regarde la morphologie, comment reconnaître votre silhouette devant le miroir (sans mètre ni balance) et les clés de chacune des six : A, V, H, I, O et X. Avec le test de silhouette gratuit de CARO pour commencer.',
        body: `Certaines achètent depuis des années la même jupe qui ne les convainc jamais, d’autres trouvent du premier coup la robe qu’elles porteraient toujours. C’est rarement une question de taille. C’est une question de ligne.

La morphologie est la partie du conseil en image qui étudie cette ligne : la proportion entre épaules, taille et hanches. Dans [notre guide du conseil en image](/the-edit/post/asesoria-de-imagen-color-silueta-y-rostro), nous l’avons présentée avec deux exemples ; aujourd’hui, nous passons en revue les six silhouettes, une par une.

## Ce que regarde la morphologie (et ce qu’elle ne regarde pas)

Elle ne regarde ni le poids, ni la taille de vêtement, ni l’âge. Un 36 et un 46 peuvent avoir la même silhouette. Elle compare trois largeurs et une ossature :

- **Les épaules**, par rapport aux hanches.
- **La taille** : nettement marquée ou peu.
- **Où se concentre le volume** : en haut, au centre, en bas ou réparti.
- **L’ossature** : fine et allongée, en courbes douces ou entre les deux.

Chaque silhouette reçoit ainsi la lettre qui dessine sa forme. Aucune n’est meilleure qu’une autre et aucune ne se « corrige » : il s’agit de choisir l’effet que vous voulez.

![Les six silhouettes de la morphologie, A, V, H, I, O et X, avec les lignes des épaules, de la taille et des hanches](/media/caro/edit/asesoria-de-imagen-6-siluetas-morfologia-1200.webp "Les six silhouettes. Les lignes marquent les épaules, la taille et les hanches : c’est la proportion entre elles que l’on compare.")

## Comment reconnaître votre silhouette devant le miroir

L’outil le plus fiable n’est pas le mètre ruban, c’est le miroir. Placez-vous de face, en vêtements près du corps et avec une bonne lumière. Regardez vos épaules et vos hanches : lesquelles sont les plus larges, ou sont-elles proches ? Regardez votre taille : se marque-t-elle ou presque pas ? Et observez où se répartit le volume.

C’est ce que fait le [test de silhouette de CARO](/estudio-de-estilo/morfologia) : quatre questions visuelles, sans mesures, sans poids et sans photo. En quelques minutes, il vous propose votre silhouette, avec sa fiche PDF gratuite et anonyme, et rien ne quitte votre appareil.

## Les six silhouettes, une par une

### La silhouette en A

Des hanches plus larges que les épaules, souvent avec une taille bien marquée. La clé : de la lumière et de la structure en haut, des lignes fluides en bas. Encolures bateau ou carrées, épaules structurées et la couleur près du visage ; jupes trapèze et pantalons droits ou larges, dans des tons plus sobres qu’en haut. [La silhouette en A en détail](/estudio-de-estilo/morfologia/silueta-en-a).

### La silhouette en V

Des épaules ou une poitrine plus larges que les hanches. La clé est inverse : des lignes nettes en haut, du volume et de la couleur en bas. Décolletés en V, matières fluides et épaules sans épaulettes ni volants ; jupes amples ou plissées et pantalons larges ou évasés, là où vont les imprimés. [La silhouette en V en détail](/estudio-de-estilo/morfologia/silueta-en-v).

### La silhouette en H

Épaules, taille et hanches de largeurs proches, avec une taille peu marquée. Deux chemins : créer une taille (taille haute, ceinture large, basque) ou souligner la verticale avec des vestes droites à la coupe nette. [La silhouette en H en détail](/estudio-de-estilo/morfologia/silueta-en-h).

### La silhouette en I

Comme le H, mais sur une ossature fine et allongée. Ici, on peut ajouter sans crainte : du volume, des superpositions et de la texture. Manches volumineuses, encolures bateau ou rondes, jupes amples et pantalons larges ; les imprimés lui vont bien. [La silhouette en I en détail](/estudio-de-estilo/morfologia/silueta-en-i).

### La silhouette en O

Le volume se concentre au centre et la taille se marque peu, souvent avec des jambes fines et un joli décolleté. La clé : des lignes verticales, une encolure ouverte et des matières fluides. Décolletés en V, vestes longues et ouvertes, robes empire ; plutôt sans ceinture à la taille. [La silhouette en O en détail](/estudio-de-estilo/morfologia/silueta-en-o).

### La silhouette en X

Des épaules et des hanches en équilibre et une taille bien marquée. La clé est de suivre votre ligne : cache-cœur, coupes ajustées, ceinture à la taille, jupes crayon ou trapèze et pantalons taille haute. Ce qui l’éteint, ce sont les pièces sans forme, qui la transforment en H. [La silhouette en X en détail](/estudio-de-estilo/morfologia/silueta-en-x).

## Trois idées qui valent pour toutes

- **Le regard va vers la lumière et le détail.** Le clair, le brillant et l’imprimé attirent l’œil ; le foncé et l’uni l’apaisent. Placez l’intérêt là où vous voulez qu’on regarde.
- **La longueur compte autant que la coupe.** Une veste qui s’arrête pile sur la partie la plus large la souligne ; quelques centimètres plus haut ou plus bas changent l’effet.
- **La matière décide.** Une matière fluide accompagne le corps ; une matière rigide le construit. C’est vrai pour un A comme pour un O.

## Ce que je ne ferais pas

- Je ne prendrais pas la lettre pour un jugement : elle décrit une proportion, pas une taille, et ne dit rien de votre allure.
- Je ne jetterais pas ce qui « ne va pas » selon le tableau. Souvent, il suffit de changer la longueur, la ceinture ou ce avec quoi vous l’associez.
- Je ne m’arrêterais pas à la silhouette : la couleur et le visage comptent aussi, et votre style pèse plus que n’importe quelle règle.

## Et si vous souhaitez un regard professionnel

Le test vous donne une piste et un vocabulaire. Mais entre deux silhouettes voisines, avec votre garde-robe devant vous, la différence vient d’un œil exercé. Carolina la travaille en personne, avec vos propres vêtements, et la croise avec votre couleur et votre visage. Vous trouverez tout dans [nos services de conseil en image](/caro/servicios).

## Questions fréquentes

### Ma silhouette change-t-elle si mon poids change ?

La proportion de base dépend beaucoup de l’ossature (la largeur des épaules et des hanches), elle reste donc le plus souvent. Ce qui peut changer, c’est l’endroit où se concentre le volume : si vous ne vous reconnaissez plus dans votre lettre d’il y a quelques années, regardez-vous de nouveau.

### Et si je me reconnais dans deux silhouettes ?

C’est très fréquent : les lettres sont des repères, pas des cases. Gardez la plus proche et empruntez des idées à l’autre. Dans le test, si la proposition ne vous ressemble pas, vous pouvez choisir une autre silhouette.

### Le test demande-t-il des mesures ou des photos ?

Non. Ce sont quatre questions devant le miroir, sans mesures, sans poids et sans photo, et la fiche PDF est gratuite et anonyme : sans e-mail et sans inscription.`,
        seoTitle: 'Morphologie femme : les 6 silhouettes · CARO. Casa de estilo',
        seoDescription:
          'Silhouette en A, V, H, I, O ou X : comment reconnaître la vôtre au miroir et quelles encolures, tailles, vestes, jupes et robes l’équilibrent. Test gratuit.',
      },
    },
  },
  {
    id: 'edit-estilo-guia-regalos-2026',
    slug: 'guia-de-regalos-con-intencion',
    title: 'Regalos con intención: la guía de CARO',
    excerpt:
      'Regalar bien no es regalar más. Cuatro maneras de acertar, de un pequeño detalle a una pieza para toda la vida, elegidas con calma.',
    category: 'estilo',
    author: 'Carolina Collaer',
    date: '2026-11-07',
    publishAt: '2026-11-07T08:00:00+01:00',
    heroImage: '/media/caro/belleza-reloj-y-pulseras-caro-casa-de-estilo.webp',
    ogImage: '/media/caro/og/edit-guia-de-regalos-con-intencion-23137df8.jpg',
    body: `Cada año llega el mismo momento: la lista de nombres, las prisas, y esa tentación de comprar algo, lo que sea, para cumplir.

Yo prefiero otra idea. Regalar menos, pero elegir mejor.

Un buen regalo dice «te he mirado». Dice que sabes qué le gusta a esa persona, cómo vive y qué le haría ilusión, aunque nunca lo hubiera pedido.

Por eso esta guía no es una lista interminable. Son cuatro maneras de regalar, de un pequeño detalle a una pieza para toda la vida.

## Antes de empezar: elige con margen

Noviembre es el mejor momento para regalar con calma. Algunas piezas de CARO están en reasortimiento y su ficha lo indica: llegan, pero con un plazo mayor. Si necesitas un regalo para una fecha concreta, escríbenos antes de pedirlo y te decimos cuándo puede llegar.

## 1. Un detalle

Para acompañar una tarjeta, para el amigo invisible o para ese «me he acordado de ti».

Las **mascarillas de tejido de Ondo Beauty 36.5** son perfectas: pequeñas, bonitas y fáciles de acertar. Mi idea es regalar tres, una para cada momento:

- **Hyaluronic Acid & Algae**, hidratante. La marca la cuenta entre sus bestsellers.
- **Adenosine & Zhi Mu**, de acción reafirmante según la marca.
- **Probiotics & Cica Water**, calmante, indicada también para piel sensible.

Un regalo pequeño que invita a parar quince minutos. Y eso, en diciembre, ya es mucho.

## 2. Para quien se cuida

Para la mujer que tiene su rutina y disfruta de ella.

- El **contorno de ojos de péptidos y bakuchiol** de Ondo Beauty 36.5, rico y nutritivo.
- La **Squalane & Bakuchiol Sleeping Pack**, una mascarilla de noche de textura gelatinosa que se transforma en líquido al masajearla.
- El **Plump Drops** de Meisani, un sérum con ocho tipos de ácido hialurónico.

Juntos, o por separado, forman un pequeño ritual de noche. Es un regalo que se usa cada día y que recuerda a quien lo hizo.

## 3. Para quien se viste con intención

Para la mujer que sabe lo que le gusta y no sigue cualquier tendencia.

- Una **pulsera Lind** de cordón trenzado con piedras naturales y una piedra central con borde dorado. Se lleva sola o junto a otras, a su manera.
- El **jersey de cuello Perkins verde**, en punto grueso con relieve en zigzag: color y textura para los días fríos.
- El **bolso de hombro de charol negro**, depurado y con brillo, para el día y para la noche.

Un consejo: si no conoces su talla, elige un accesorio. Una pulsera o un bolso siempre aciertan.

## 4. Una pieza para siempre

Hay regalos que no se guardan en un cajón. Se viven.

- Un **Teddy de NAOR**: escultura original en resina, firmada y numerada, con certificado del artista. Pop, divertida y de coleccionista.
- El **taburete Deni de Hamed Ouattara**, hecho con bidones de aceite de motor y chapa metálica. Diseño de autor con una historia detrás.
- Las **tassabas de Hamed Ouattara**, tres piezas de tres tamaños y colores variados.

Son piezas para elegir con calma. Si tienes dudas sobre medidas, colores o envío, escríbenos y lo vemos juntas.

## Lo que no haría

No regalaría una crema «antiedad» a quien no la ha pedido. El cuidado se ofrece, no se insinúa.

No compraría por cumplir. Un regalo sin intención se olvida antes de abrirse.

Y no me olvidaría de mí. Si este año el regalo es para ti, también vale.

## Preguntas frecuentes

### ¿Hacéis envíos fuera de España?

Sí: a España, al resto de Europa y a otros países. El envío es gratuito en la zona euro a partir de 50 €, salvo las piezas grandes de Casa, que tienen su propio envío indicado en su ficha.

### ¿Llegará a tiempo?

Las piezas disponibles siguen el plazo de envío habitual. Las que están en reasortimiento lo indican en su ficha y tardan más. Si es para una fecha concreta, escríbenos antes de pedir.

### ¿Puedo regalar una pieza de Casa si no sé dónde la pondrá?

Las piezas de Casa tienen carácter propio y conviven bien con estilos muy distintos. Aun así, si dudas, escríbenos: te contamos medidas y detalles para que elijas con tranquilidad.`,
    seoTitle: 'Regalos con intención: la guía de CARO · CARO. Casa de estilo',
    seoDescription:
      'Guía de regalos de CARO: cuatro maneras de regalar con intención, de mascarillas como detalle a cosmética, accesorios y piezas de diseño de autor.',
    relatedProductSlugs: [
      'ondo-hyaluronic-acid-algae-moisture-boosting-mask',
      'ondo-adenosine-zhi-mu-lifting-mask',
      'ondo-probiotics-cica-water-soothing-mask',
      'ondo-peptide-complex-bakuchiol-eye-cream',
      'ondo-squalane-bakuchiol-sleeping-pack',
      'meisani-plump-drops-hyaluronic-acid-serum',
      'pulsera-lind-16lind201',
      'surkana-jersey-cuello-perkins-relieve-verde',
      'surkana-bolso-hombro-charol-negro',
      'naor-porsche-teddy',
      'hamed-ouattara-taburete',
      'hamed-ouattara-tassaba-set',
    ],
    i18n: {
      en: {
        title: 'Gifts with intention: the CARO guide',
        excerpt:
          'Giving well isn’t about giving more. Four ways to get it right, from a small token to a piece for life, chosen with care.',
        body: `Every year the same moment comes: the list of names, the rush, and the temptation to buy something, anything, just to tick a box.

I prefer another idea. Give less, but choose better.

A good gift says “I see you”. It says you know what that person likes, how she lives and what would delight her, even if she never asked for it.

That is why this guide isn’t an endless list. These are four ways of giving, from a small token to a piece for life.

## Before you start: choose early

November is the best time to choose gifts calmly. Some CARO pieces are restocking and their page says so: they arrive, but take longer. If you need a gift for a specific date, write to us before ordering and we’ll tell you when it can arrive.

## 1. A small token

To go with a card, for a Secret Santa or for that “I thought of you”.

**Ondo Beauty 36.5 sheet masks** are perfect: small, pretty and easy to get right. My idea is to give three, one for each moment:

- **Hyaluronic Acid & Algae**, moisturising. The brand counts it among its bestsellers.
- **Adenosine & Zhi Mu**, firming according to the brand.
- **Probiotics & Cica Water**, soothing, also suitable for sensitive skin.

A small gift that invites you to stop for fifteen minutes. And in December, that is already a lot.

## 2. For someone who cares for herself

For the woman who has her routine and enjoys it.

- Ondo Beauty 36.5’s **peptide and bakuchiol eye cream**, rich and nourishing.
- The **Squalane & Bakuchiol Sleeping Pack**, a jelly-textured overnight mask that turns liquid as you massage it in.
- Meisani’s **Plump Drops**, a serum with eight types of hyaluronic acid.

Together or separately, they make a small night ritual. It is a gift used every day, one that brings the giver to mind.

## 3. For someone who dresses with intention

For the woman who knows what she likes and doesn’t follow every trend.

- A **Lind bracelet** in braided cord with natural stones and a central stone with a golden rim. Worn alone or stacked, her way.
- The **green Perkins-collar jumper**, in chunky knit with a zigzag relief: colour and texture for cold days.
- The **black patent shoulder bag**, clean and glossy, for day and evening.

A tip: if you don’t know her size, choose an accessory. A bracelet or a bag is always right.

## 4. A piece for life

Some gifts don’t end up in a drawer. They are lived with.

- A **NAOR Teddy**: an original resin sculpture, signed and numbered, with the artist’s certificate. Pop, playful and collectable.
- Hamed Ouattara’s **Deni stool**, made from engine oil drums and sheet metal. Designer craft with a story behind it.
- Hamed Ouattara’s **tassabas**, three pieces in three sizes and assorted colours.

These are pieces to choose calmly. If you have questions about sizes, colours or shipping, write to us and we’ll look at it together.

## What I wouldn’t do

I wouldn’t give an “anti-ageing” cream to someone who hasn’t asked for one. Care is offered, not hinted at.

I wouldn’t buy just to tick a box. A gift without intention is forgotten before it’s unwrapped.

And I wouldn’t forget myself. If this year the gift is for you, that counts too.

## Frequently asked questions

### Do you ship outside Spain?

Yes: to Spain, the rest of Europe and other countries. Shipping is free in the eurozone from €50, except for large Casa pieces, which have their own shipping shown on their page.

### Will it arrive in time?

Available pieces follow the usual shipping time. Those restocking say so on their page and take longer. If it’s for a specific date, write to us before ordering.

### Can I give a Casa piece if I don’t know where it will go?

Casa pieces have their own character and sit well with very different styles. Even so, if in doubt, write to us: we’ll share sizes and details so you can choose with peace of mind.`,
        seoTitle: 'Gifts with intention: the CARO guide · CARO. Casa de estilo',
        seoDescription:
          'CARO’s gift guide: four ways to give with intention, from sheet masks as a small token to skincare, accessories and designer pieces.',
      },
      fr: {
        title: 'Des cadeaux avec intention : le guide de CARO',
        excerpt:
          'Bien offrir, ce n’est pas offrir plus. Quatre façons de viser juste, d’une petite attention à une pièce pour la vie, choisies avec soin.',
        body: `Chaque année revient le même moment : la liste de prénoms, la course, et la tentation d’acheter quelque chose, n’importe quoi, pour être quitte.

Je préfère une autre idée. Offrir moins, mais mieux choisir.

Un beau cadeau dit « je t’ai regardée ». Il dit que vous savez ce que cette personne aime, comment elle vit et ce qui lui ferait plaisir, même si elle ne l’a jamais demandé.

C’est pourquoi ce guide n’est pas une liste interminable. Ce sont quatre façons d’offrir, d’une petite attention à une pièce pour la vie.

## Avant de commencer : choisissez à l’avance

Novembre est le meilleur moment pour choisir ses cadeaux avec calme. Certaines pièces de CARO sont en réassort et leur fiche l’indique : elles arrivent, mais avec un délai plus long. Si vous avez besoin d’un cadeau pour une date précise, écrivez-nous avant de commander et nous vous dirons quand il peut arriver.

## 1. Une petite attention

Pour accompagner une carte, pour un Secret Santa ou pour ce « j’ai pensé à toi ».

Les **masques en tissu d’Ondo Beauty 36.5** sont parfaits : petits, jolis et faciles à offrir. Mon idée est d’en offrir trois, un pour chaque moment :

- **Hyaluronic Acid & Algae**, hydratant. La marque le compte parmi ses best-sellers.
- **Adenosine & Zhi Mu**, à l’action raffermissante selon la marque.
- **Probiotics & Cica Water**, apaisant, adapté aussi aux peaux sensibles.

Un petit cadeau qui invite à s’arrêter quinze minutes. Et en décembre, c’est déjà beaucoup.

## 2. Pour celle qui prend soin d’elle

Pour la femme qui a sa routine et qui l’apprécie.

- Le **contour des yeux aux peptides et au bakuchiol** d’Ondo Beauty 36.5, riche et nourrissant.
- Le **Squalane & Bakuchiol Sleeping Pack**, un masque de nuit à la texture gélifiée qui devient liquide au massage.
- Le **Plump Drops** de Meisani, un sérum à huit types d’acide hyaluronique.

Ensemble ou séparément, ils forment un petit rituel du soir. C’est un cadeau qui s’utilise chaque jour et qui rappelle qui l’a offert.

## 3. Pour celle qui s’habille avec intention

Pour la femme qui sait ce qu’elle aime et ne suit pas n’importe quelle tendance.

- Un **bracelet Lind** en cordon tressé avec des pierres naturelles et une pierre centrale au bord doré. Il se porte seul ou avec d’autres, à sa façon.
- Le **pull col Perkins vert**, en grosse maille avec un relief en zigzag : couleur et texture pour les jours froids.
- Le **sac porté épaule en vernis noir**, épuré et brillant, pour le jour et pour le soir.

Un conseil : si vous ne connaissez pas sa taille, choisissez un accessoire. Un bracelet ou un sac, c’est toujours juste.

## 4. Une pièce pour toujours

Certains cadeaux ne finissent pas dans un tiroir. On vit avec eux.

- Un **Teddy de NAOR** : sculpture originale en résine, signée et numérotée, avec le certificat de l’artiste. Pop, ludique et de collection.
- Le **tabouret Deni de Hamed Ouattara**, réalisé avec des bidons d’huile moteur et de la tôle. Un design d’auteur avec une histoire.
- Les **tassabas de Hamed Ouattara**, trois pièces de trois tailles et de couleurs variées.

Ce sont des pièces à choisir avec calme. Si vous avez des questions sur les dimensions, les couleurs ou la livraison, écrivez-nous et nous verrons ensemble.

## Ce que je ne ferais pas

Je n’offrirais pas une crème « anti-âge » à quelqu’un qui ne l’a pas demandée. Le soin s’offre, il ne se suggère pas.

Je n’achèterais pas pour être quitte. Un cadeau sans intention s’oublie avant d’être ouvert.

Et je ne m’oublierais pas. Si cette année le cadeau est pour vous, cela compte aussi.

## Questions fréquentes

### Livrez-vous hors d’Espagne ?

Oui : en Espagne, dans le reste de l’Europe et dans d’autres pays. La livraison est offerte dans la zone euro dès 50 €, sauf pour les grandes pièces Casa, qui ont leur propre livraison indiquée sur leur fiche.

### Arrivera-t-il à temps ?

Les pièces disponibles suivent le délai de livraison habituel. Celles en réassort l’indiquent sur leur fiche et prennent plus de temps. Si c’est pour une date précise, écrivez-nous avant de commander.

### Puis-je offrir une pièce Casa sans savoir où elle sera posée ?

Les pièces Casa ont leur propre caractère et s’accordent avec des styles très différents. Malgré tout, en cas de doute, écrivez-nous : nous vous donnerons les dimensions et les détails pour choisir sereinement.`,
        seoTitle: 'Des cadeaux avec intention : le guide de CARO · CARO. Casa de estilo',
        seoDescription:
          'Le guide des cadeaux de CARO : quatre façons d’offrir avec intention, des masques en tissu aux soins, accessoires et pièces de design d’auteur.',
      },
    },
  },
  {
    id: 'edit-estilo-asesoria-de-imagen',
    slug: 'asesoria-de-imagen-color-silueta-y-rostro',
    title: 'Asesoría de imagen: color, silueta y rostro, explicados sin recetas',
    excerpt:
      'Qué mira de verdad una asesoría de imagen: las 12 estaciones del color, las seis siluetas y la forma del rostro (gafas y contouring). Con ilustraciones y dos ejemplos de cada, no un catálogo de reglas.',
    category: 'estilo',
    author: 'Carolina Collaer',
    date: '2026-10-24',
    publishAt: '2026-10-24T08:00:00+02:00',
    heroImage: '/media/caro/edit/asesoria-de-imagen-portada-color-silueta-rostro-1200.webp',
    ogImage: '/media/caro/og/edit-asesoria-de-imagen-color-silueta-y-rostro-ed8a1aeb.jpg',
    body: `Hay prendas que te pones y la cara se enciende. Y hay otras, preciosas en la percha, que en ti se apagan sin que sepas decir por qué.

Casi siempre hay un motivo, y casi nunca es la prenda. Es el color junto a tu piel, la línea del corte sobre tu cuerpo o la forma de la montura frente a tu rostro.

Una asesoría de imagen sirve para poner nombre a ese motivo. No para cambiarte, sino para que elijas con criterio.

## Qué es (y qué no es) una asesoría de imagen

No es un uniforme ni una lista de prohibiciones. Es una lectura de tres cosas que no cambian de una temporada a otra y de una que sí: tu momento.

Las tres primeras tienen técnicas propias. Aquí te explico las más conocidas y compartidas, con ilustraciones y ejemplos:

- **El color**: qué tonos iluminan tu piel, tus ojos y tu pelo (la colorimetría).
- **La silueta**: la proporción entre hombros, cintura y caderas (la morfología).
- **El rostro**: su forma, que orienta las gafas, el maquillaje y el peinado (el visagismo).
- **Tu estilo y tu momento**: lo que te representa hoy, que ninguna tabla sabe por ti.

## 1. El color: las 12 estaciones

La colorimetría mira tres cosas de un color. Su **temperatura**: si tira a cálido (dorado) o a frío (azulado). Su **valor**: si es claro o profundo. Y su **intensidad**: si es brillante y limpio o suave y empolvado.

Cruzando esas tres miradas salen cuatro familias, primavera, verano, otoño e invierno, y cada una se divide en tres. Son las 12 estaciones.

![Rueda de las 12 estaciones de la colorimetría, con los colores de cada estación: cálidas a la izquierda, frías a la derecha](/media/caro/edit/asesoria-de-imagen-rueda-12-estaciones-colorimetria-es-1200.webp "La rueda de las 12 estaciones con los colores de las fichas de CARO. A la izquierda, las cálidas; a la derecha, las frías. Arriba, las más brillantes; abajo, las más suaves.")

Se lee así: cuanto más a la izquierda, más cálido; cuanto más a la derecha, más frío. Las estaciones de arriba piden colores limpios y con contraste; las de abajo, colores mezclados, con un punto gris.

Dos ejemplos opuestos. Un **Invierno brillante** se ve en su mejor versión con contraste alto: rojo limpio, verde esmeralda, azul intenso, fucsia, blanco y negro juntos. Un **Otoño suave** gana con lo contrario: camel, arena, salmón suave, verdes y azules apagados, nada que grite. Si los intercambias, el primero parece cansado y el segundo queda tapado por la ropa.

El toque profesional está en cómo se mira: con luz natural, la cara sin maquillaje y las telas de prueba bajo el rostro, una a una. Si quieres una primera pista desde casa, juega a [COLORIMETRY ME!](/estudio-de-estilo/colorimetria), el juego de CARO: te propone una estación y te enseña su ficha. Es un punto de partida, no un diagnóstico.

## 2. La silueta: seis formas de proporción

La morfología no habla de talla ni de peso. Compara tres anchuras, hombros, cintura y caderas, y les da la forma de una letra. Ninguna es mejor que otra: cada una tiene sus prendas aliadas.

![Las seis siluetas de la morfología, A, V, H, I, O y X, con las líneas de hombros, cintura y caderas](/media/caro/edit/asesoria-de-imagen-6-siluetas-morfologia-1200.webp "Las seis siluetas. Las líneas marcan hombros, cintura y caderas: lo que se compara es la proporción entre ellas.")

- **A**: caderas más anchas que los hombros, a menudo con cintura fina.
- **V**: hombros o pecho más anchos que las caderas.
- **H**: hombros y caderas alineados, cintura poco marcada.
- **I**: como la H, pero en una estructura menuda y longilínea.
- **O**: las curvas se concentran en el centro, con la cintura como parte más ancha.
- **X**: hombros y caderas equilibrados y la cintura bien marcada.

La idea no es «corregir» el cuerpo, sino equilibrar líneas: llevar la mirada a donde quieres y dar calma a lo demás. Dos ejemplos, para que se entienda la lógica.

![Dos ejemplos de prendas: silueta en A con blusa de mangas abullonadas y falda recta oscura; silueta en X con vestido cruzado y cinturón](/media/caro/edit/asesoria-de-imagen-siluetas-a-y-x-prendas-1200.webp "A la izquierda, una silueta en A: interés arriba, sencillez abajo. A la derecha, una silueta en X: la cintura, siempre marcada.")

### Ejemplo 1: la silueta en A

Aquí todo el interés va arriba, para equilibrar unas caderas más anchas que los hombros. Una blusa de color con mangas abullonadas y escote barco ensancha visualmente los hombros; un collar con presencia sube la mirada al rostro. Abajo, lo contrario: una falda recta o ligeramente evasé, en un tono oscuro y liso, en un tejido que caiga sin pegarse, sin bolsillos laterales ni volantes. Y la blusa por dentro: una túnica larga que termina justo en la cadera señala la zona que querías calmar.

### Ejemplo 2: la silueta en X

Si tu cintura está marcada, el trabajo es no esconderla. El vestido cruzado es su gran aliado: el cruce dibuja la cintura y el escote en V alarga el cuello. Un cinturón en el talle natural y faldas o pantalones de tiro alto siguen la misma lógica. En abrigo, mejor un trench anudado a la cintura que un abrigo recto: las prendas sin forma, por bonitas que sean, convierten una X en una H.

## 3. El rostro: gafas y contouring

El visagismo parte de la forma del rostro. En las fichas de CARO trabajamos siete: ovalado, redondo, cuadrado, rectangular, triangular, triángulo invertido y diamante. El principio es casi siempre el mismo: la montura aporta lo que el rostro no tiene.

![Las siete formas de rostro del visagismo: ovalado, redondo, cuadrado, rectangular, triangular, triángulo invertido y diamante](/media/caro/edit/asesoria-de-imagen-7-formas-de-rostro-visagismo-es-1200.webp "Las siete formas de rostro. El contorno es el de una cara real; la línea de puntos encima marca la forma geométrica que la resume.")

![Gafas según la forma del rostro: rostro redondo con montura fina y angulosa; rostro cuadrado con montura redondeada](/media/caro/edit/asesoria-de-imagen-gafas-cara-redonda-y-cuadrada-1200.webp "Rostro redondo con una montura angulosa (izquierda) y rostro cuadrado con una montura redondeada (derecha). La línea de puntos marca la forma del rostro.")

En un **rostro redondo**, de líneas curvas y casi tan ancho como largo, una montura fina y angulosa, algo más ancha que alta, afina y alarga; un puente claro abre la mirada. En un **rostro cuadrado**, de frente y mandíbula anchas, funciona lo contrario: monturas redondeadas u ovaladas, de metal ligero, que suavizan los ángulos, y nunca más anchas que el propio rostro.

Tres reglas sirven para todas las formas: la montura no tapa las cejas; no sobrepasa la parte más ancha de la cara; y el puente se elige con la nariz y los ojos (oscuro y bajo si los ojos están separados o la nariz es larga, claro y más alto en el caso contrario).

### Contouring: luz y sombra

El maquillaje sigue la misma idea con dos herramientas: la sombra hunde y la luz adelanta. La luz va casi siempre a los mismos sitios: el centro de la frente, debajo de los ojos, el tabique de la nariz y el mentón. La sombra es la que cambia con la forma del rostro.

![Contouring según la forma del rostro: sombra en los laterales del rostro redondo y en las esquinas del rostro cuadrado, luz en el centro](/media/caro/edit/asesoria-de-imagen-contouring-cara-redonda-y-cuadrada-1200.webp "Las zonas oscuras son de sombra y las claras, de luz. Rostro redondo a la izquierda, rostro cuadrado a la derecha.")

En el **rostro redondo**, la sombra baja por los laterales, de la sien a la mandíbula, y marca un poco bajo el pómulo: el rostro se alarga. En el **rostro cuadrado**, la sombra va a las cuatro esquinas, las sienes y el ángulo de la mandíbula, para redondearlas. En los dos casos, un tono mate algo más oscuro que tu piel y muy difuminado: si se ve la raya, sobra producto. La sombra de ojos, en cambio, depende de la forma de los ojos, no de la del rostro.

## Claves para guiarte (y una mirada profesional)

Estas son las técnicas más conocidas y compartidas de la asesoría de imagen, y ya te dan muchas claves. Con un poco de práctica y algo de lectura, te permiten guiarte de forma sencilla y eficaz: mirar tu armario con otros ojos, elegir una montura con criterio, entender por qué un color te sienta tan bien.

Pero la asesoría de imagen es algo más que una suma de técnicas, y ninguna sustituye la mirada real de una asesora. Ella las cruza entre sí (una silueta en A con rostro cuadrado y paleta de Otoño suave no se viste igual que otra A de Invierno brillante) y con lo que ninguna tabla mide: tu estilo, tu trabajo, cómo te quieres sentir.

Y si en algún momento quieres ese apoyo, aquí está CARO. Carolina asesora desde 2017: ha acompañado a clientas en Bélgica y en Estados Unidos, y hoy lo hace desde España. Lo trabajamos en persona, con luz, telas y tu propio armario, en la Sesión CARO · Imagen & Presencia o en una Sesión esencial, centrada en una sola pregunta. Lo tienes todo en [los servicios de asesoría](/caro/servicios).

## Lo que no haría

- No seguiría una tabla al pie de la letra: es un mapa, no una ley.
- No vaciaría el armario después de descubrir mi estación. Casi siempre basta con mover un color de sitio: lejos de la cara, en un pantalón o un bolso.
- No me quedaría con una etiqueta para siempre: el pelo, la piel y la vida cambian, y la lectura puede cambiar con ellos.

## Preguntas frecuentes

### ¿Puedo saber mi estación sola, con un test?

Un test o un juego como COLORIMETRY ME! da una buena pista y te enseña a mirar. La confirmación pide luz natural, telas de prueba y un ojo entrenado: entre dos estaciones vecinas, la diferencia es sutil.

### ¿Y si estoy entre dos siluetas?

Es muy habitual. Las letras son referencias, no cajas. Se toma la que más se parece y se ajusta con la otra: una H con algo de cintura, por ejemplo, puede tomar ideas de la X.

### ¿La asesoría es solo para comprar ropa nueva?

No. La mayor parte del trabajo se hace con lo que ya tienes: nuevas combinaciones, prendas recuperadas y una lista corta y honesta de lo que de verdad falta.`,
    seoTitle: 'Asesoría de imagen: color, silueta y rostro · CARO. Casa de estilo',
    seoDescription:
      'Las 12 estaciones de la colorimetría, las seis siluetas (A, V, H, I, O, X), gafas y contouring según el rostro. Explicado con ilustraciones y ejemplos por Carolina Collaer.',
    relatedProductSlugs: [],
    endLinks: ['colorimetry', 'services', 'guide'],
    i18n: {
      en: {
        title: 'Image consulting: colour, silhouette and face, explained without rules',
        excerpt:
          'What an image consultation really looks at: the 12 colour seasons, the six silhouettes and the shape of the face (glasses and contouring). With illustrations and two examples of each, not a catalogue of rules.',
        body: `Some clothes light up your face the moment you put them on. Others, lovely on the hanger, dim you down and you can’t quite say why.

There is almost always a reason, and it is rarely the garment. It is the colour next to your skin, the line of the cut on your body or the shape of the frame in front of your face.

An image consultation is there to name that reason. Not to change you, but so you can choose with judgement.

## What an image consultation is (and isn’t)

It isn’t a uniform or a list of things you can’t wear. It is a reading of three things that don’t change from one season to the next, and one that does: where you are in life.

The first three have their own techniques. Here I explain the best known and most widely shared, with illustrations and examples:

- **Colour**: which shades light up your skin, your eyes and your hair (colour analysis).
- **Silhouette**: the proportion between shoulders, waist and hips (body shape).
- **Face**: its shape, which guides glasses, make-up and hair (face shape analysis).
- **Your style and your moment**: what represents you today, which no chart can know for you.

## 1. Colour: the 12 seasons

Colour analysis looks at three things in a colour. Its **temperature**: whether it leans warm (golden) or cool (bluish). Its **value**: whether it is light or deep. And its **intensity**: whether it is bright and clear or soft and dusty.

Crossing those three gives four families, spring, summer, autumn and winter, and each one splits into three. Those are the 12 seasons.

![The 12-season colour wheel, with each season’s colours: warm seasons on the left, cool seasons on the right](/media/caro/edit/asesoria-de-imagen-rueda-12-estaciones-colorimetria-en-1200.webp "The 12-season wheel with the colours of CARO’s colour cards. Warm seasons on the left, cool on the right. The brightest at the top, the softest at the bottom.")

You read it like this: the further left, the warmer; the further right, the cooler. The seasons at the top want clear colours with contrast; those at the bottom, blended colours with a touch of grey.

Two opposite examples. A **Bright Winter** looks her best in high contrast: clear red, emerald green, intense blue, fuchsia, black and white together. A **Soft Autumn** wins with the opposite: camel, sand, soft salmon, muted greens and blues, nothing that shouts. Swap them and the first looks tired while the second disappears behind her clothes.

The professional touch is in how you look: in daylight, with a bare face and the test fabrics under the face, one by one. If you’d like a first clue from home, play [COLORIMETRY ME!](/estudio-de-estilo/colorimetria), CARO’s game: it suggests a season and shows you its colour card. It is a starting point, not a diagnosis.

## 2. Silhouette: six shapes of proportion

Body shape isn’t about size or weight. It compares three widths, shoulders, waist and hips, and gives them the shape of a letter. None is better than another: each has its allies.

![The six body shapes, A, V, H, I, O and X, with the shoulder, waist and hip lines](/media/caro/edit/asesoria-de-imagen-6-siluetas-morfologia-1200.webp "The six silhouettes. The lines mark shoulders, waist and hips: what matters is the proportion between them.")

- **A**: hips wider than the shoulders, often with a small waist.
- **V**: shoulders or bust wider than the hips.
- **H**: shoulders and hips aligned, little waist definition.
- **I**: like the H, on a slender, narrow frame.
- **O**: curves gathered in the middle, with the waist as the widest part.
- **X**: balanced shoulders and hips with a well-defined waist.

The idea is not to “correct” the body but to balance lines: draw the eye where you want it and quieten the rest. Two examples, so the logic is clear.

![Two outfit examples: an A silhouette in a puff-sleeved blouse and a dark straight skirt; an X silhouette in a belted wrap dress](/media/caro/edit/asesoria-de-imagen-siluetas-a-y-x-prendas-1200.webp "On the left, an A silhouette: interest on top, simplicity below. On the right, an X silhouette: the waist, always defined.")

### Example 1: the A silhouette

Here all the interest goes up top, to balance hips wider than the shoulders. A coloured blouse with puff sleeves and a boat neckline visually widens the shoulders; a statement necklace draws the eye up to the face. Below, the opposite: a straight or slightly A-line skirt, in a dark plain shade, in a fabric that falls without clinging, with no side pockets or ruffles. And the blouse tucked in: a long tunic that ends right at the hip points to the very area you wanted to quieten.

### Example 2: the X silhouette

If your waist is defined, the job is not to hide it. The wrap dress is its great ally: the wrap draws the waist and the V-neck lengthens the neck. A belt at the natural waist and high-rise skirts or trousers follow the same logic. For a coat, a trench tied at the waist rather than a straight coat: shapeless pieces, however beautiful, turn an X into an H.

## 3. The face: glasses and contouring

Face shape analysis starts from the shape of the face. CARO’s cards cover seven: oval, round, square, rectangular, triangular, inverted triangle and diamond. The principle is nearly always the same: the frame brings what the face doesn’t have.

![The seven face shapes: oval, round, square, rectangular, triangular, inverted triangle and diamond](/media/caro/edit/asesoria-de-imagen-7-formas-de-rostro-visagismo-en-1200.webp "The seven face shapes. The outline is that of a real face; the dotted line on top marks the geometric shape that sums it up.")

![Glasses by face shape: a round face with a slim angular frame; a square face with a rounded frame](/media/caro/edit/asesoria-de-imagen-gafas-cara-redonda-y-cuadrada-1200.webp "A round face with an angular frame (left) and a square face with a rounded frame (right). The dotted line marks the shape of the face.")

On a **round face**, with curved lines and almost as wide as it is long, a slim angular frame, slightly wider than it is high, slims and lengthens; a light bridge opens up the eyes. On a **square face**, with a wide forehead and jaw, the opposite works: rounded or oval frames in light metal, which soften the angles, and never wider than the face itself.

Three rules work for every shape: the frame doesn’t hide the eyebrows; it doesn’t go beyond the widest part of the face; and the bridge is chosen with the nose and eyes in mind (dark and low if the eyes are wide-set or the nose is long, light and higher the other way round).

### Contouring: light and shadow

Make-up follows the same idea with two tools: shadow recedes and light brings forward. Light almost always goes to the same places: the centre of the forehead, under the eyes, the bridge of the nose and the chin. Shadow is what changes with the shape of the face.

![Contouring by face shape: shadow on the sides of a round face and on the corners of a square face, light in the centre](/media/caro/edit/asesoria-de-imagen-contouring-cara-redonda-y-cuadrada-1200.webp "The dark areas are shadow and the light ones, highlight. Round face on the left, square face on the right.")

On a **round face**, the shadow runs down the sides, from temple to jaw, with a touch under the cheekbone: the face looks longer. On a **square face**, the shadow goes to the four corners, the temples and the angle of the jaw, to round them off. In both cases, a matte shade slightly darker than your skin and very well blended: if you can see the line, there is too much product. Eyeshadow, on the other hand, depends on the shape of the eyes, not of the face.

## Keys to guide you (and a professional eye)

These are the best known and most widely shared techniques of image consulting, and they already give you plenty of keys. With a little practice and some reading, they let you guide yourself simply and effectively: look at your wardrobe with fresh eyes, choose a frame with judgement, understand why a colour suits you so well.

But image consulting is more than a sum of techniques, and none of them replaces the real eye of an image consultant. She crosses them with each other (an A silhouette with a square face and a Soft Autumn palette doesn’t dress like another A who is a Bright Winter) and with what no chart measures: your style, your work, how you want to feel.

And if at some point you’d like that support, CARO is here. Carolina has been advising since 2017: she has worked with clients in Belgium and the United States, and today she does so from Spain. We work in person, with light, fabrics and your own wardrobe, in the CARO Session · Image & Presence or in an Essential session, focused on a single question. You’ll find everything in [our image consulting services](/caro/servicios).

## What I wouldn’t do

- I wouldn’t follow a chart to the letter: it is a map, not a law.
- I wouldn’t empty my wardrobe after discovering my season. Moving a colour is usually enough: away from the face, into trousers or a bag.
- I wouldn’t keep a label forever: hair, skin and life change, and the reading can change with them.

## Frequently asked questions

### Can I find my season on my own, with a test?

A test or a game like COLORIMETRY ME! gives a good clue and teaches you to look. Confirming it takes daylight, test fabrics and a trained eye: between two neighbouring seasons, the difference is subtle.

### What if I’m between two silhouettes?

That is very common. The letters are references, not boxes. You take the closest one and adjust with the other: an H with some waist, for example, can borrow ideas from the X.

### Is image consulting only about buying new clothes?

No. Most of the work is done with what you already own: new combinations, pieces brought back to life and a short, honest list of what is really missing.`,
        seoTitle: 'Image consulting: colour, silhouette and face · CARO. Casa de estilo',
        seoDescription:
          'The 12 colour seasons, the six body shapes (A, V, H, I, O, X), glasses and contouring by face shape. Explained with illustrations and examples by Carolina Collaer.',
      },
      fr: {
        title: 'Le conseil en image : couleur, silhouette et visage, sans recettes',
        excerpt:
          'Ce que regarde vraiment un conseil en image : les 12 saisons de la colorimétrie, les six silhouettes et la forme du visage (lunettes et contouring). Avec des illustrations et deux exemples pour chacun, pas un catalogue de règles.',
        body: `Il y a des vêtements qui vous illuminent le visage dès que vous les portez. Et d’autres, superbes sur le cintre, qui vous éteignent sans que vous sachiez dire pourquoi.

Il y a presque toujours une raison, et c’est rarement le vêtement. C’est la couleur près de votre peau, la ligne de la coupe sur votre corps ou la forme de la monture devant votre visage.

Un conseil en image sert à nommer cette raison. Non pas pour vous changer, mais pour que vous choisissiez en connaissance de cause.

## Ce qu’est (et ce que n’est pas) un conseil en image

Ce n’est ni un uniforme ni une liste d’interdits. C’est la lecture de trois choses qui ne changent pas d’une saison à l’autre, et d’une qui change : votre moment de vie.

Les trois premières ont leurs propres techniques. Je vous explique ici les plus connues et les plus partagées, avec des illustrations et des exemples :

- **La couleur** : les teintes qui illuminent votre peau, vos yeux et vos cheveux (la colorimétrie).
- **La silhouette** : la proportion entre épaules, taille et hanches (la morphologie).
- **Le visage** : sa forme, qui oriente les lunettes, le maquillage et la coiffure (le visagisme).
- **Votre style et votre moment** : ce qui vous représente aujourd’hui, qu’aucun tableau ne sait à votre place.

## 1. La couleur : les 12 saisons

La colorimétrie regarde trois choses dans une couleur. Sa **température** : chaude (dorée) ou froide (bleutée). Sa **valeur** : claire ou profonde. Et son **intensité** : vive et nette, ou douce et poudrée.

En croisant ces trois regards, on obtient quatre familles, printemps, été, automne et hiver, et chacune se divise en trois. Ce sont les 12 saisons.

![La roue des 12 saisons de la colorimétrie, avec les couleurs de chaque saison : chaudes à gauche, froides à droite](/media/caro/edit/asesoria-de-imagen-rueda-12-estaciones-colorimetria-fr-1200.webp "La roue des 12 saisons avec les couleurs des fiches de CARO. À gauche, les saisons chaudes ; à droite, les froides. En haut, les plus vives ; en bas, les plus douces.")

Elle se lit ainsi : plus on va à gauche, plus c’est chaud ; plus on va à droite, plus c’est froid. Les saisons du haut demandent des couleurs nettes et contrastées ; celles du bas, des couleurs mélangées, avec une pointe de gris.

Deux exemples opposés. Un **Hiver clair** est à son meilleur avec un contraste fort : rouge franc, vert émeraude, bleu intense, fuchsia, noir et blanc ensemble. Un **Automne pastel** gagne avec l’inverse : camel, sable, saumon doux, verts et bleus éteints, rien qui crie. Inversez-les, et le premier a l’air fatigué tandis que le second disparaît derrière ses vêtements.

La touche professionnelle est dans la façon de regarder : à la lumière du jour, le visage démaquillé et les tissus d’essai sous le visage, un par un. Pour une première piste depuis chez vous, jouez à [COLORIMETRY ME!](/estudio-de-estilo/colorimetria), le jeu de CARO : il vous propose une saison et vous montre sa fiche. C’est un point de départ, pas un diagnostic.

## 2. La silhouette : six formes de proportion

La morphologie ne parle ni de taille de vêtement ni de poids. Elle compare trois largeurs, épaules, taille et hanches, et leur donne la forme d’une lettre. Aucune n’est meilleure qu’une autre : chacune a ses alliées.

![Les six silhouettes de la morphologie, A, V, H, I, O et X, avec les lignes des épaules, de la taille et des hanches](/media/caro/edit/asesoria-de-imagen-6-siluetas-morfologia-1200.webp "Les six silhouettes. Les lignes marquent les épaules, la taille et les hanches : c’est la proportion entre elles que l’on compare.")

- **A** : hanches plus larges que les épaules, souvent avec une taille fine.
- **V** : épaules ou poitrine plus larges que les hanches.
- **H** : épaules et hanches alignées, taille peu marquée.
- **I** : comme le H, sur une ossature menue et longiligne.
- **O** : les rondeurs se concentrent au centre, la taille est la partie la plus large.
- **X** : épaules et hanches équilibrées, taille bien marquée.

L’idée n’est pas de « corriger » le corps, mais d’équilibrer les lignes : attirer le regard là où vous le souhaitez et apaiser le reste. Deux exemples, pour comprendre la logique.

![Deux exemples de tenues : silhouette en A avec un chemisier à manches ballon et une jupe droite foncée ; silhouette en X avec une robe portefeuille ceinturée](/media/caro/edit/asesoria-de-imagen-siluetas-a-y-x-prendas-1200.webp "À gauche, une silhouette en A : de l’intérêt en haut, de la simplicité en bas. À droite, une silhouette en X : la taille, toujours marquée.")

### Exemple 1 : la silhouette en A

Ici, tout l’intérêt va en haut, pour équilibrer des hanches plus larges que les épaules. Un chemisier coloré à manches ballon et encolure bateau élargit visuellement les épaules ; un collier plastron remonte le regard vers le visage. En bas, l’inverse : une jupe droite ou légèrement évasée, dans un ton foncé et uni, dans une matière qui tombe sans coller, sans poches latérales ni volants. Et le chemisier rentré : une tunique longue qui s’arrête pile sur la hanche désigne justement la zone que vous vouliez apaiser.

### Exemple 2 : la silhouette en X

Si votre taille est marquée, il s’agit de ne pas la cacher. La robe portefeuille est sa grande alliée : le croisé dessine la taille et le décolleté en V allonge le cou. Une ceinture à la taille naturelle et des jupes ou pantalons taille haute suivent la même logique. Côté manteau, plutôt un trench noué à la taille qu’un manteau droit : les pièces sans forme, aussi belles soient-elles, transforment un X en H.

## 3. Le visage : lunettes et contouring

Le visagisme part de la forme du visage. Dans les fiches de CARO, nous en travaillons sept : ovale, rond, carré, rectangulaire, triangulaire, pyramide inversée et losange. Le principe est presque toujours le même : la monture apporte ce que le visage n’a pas.

![Les sept formes de visage : ovale, rond, carré, rectangulaire, triangulaire, pyramide inversée et losange](/media/caro/edit/asesoria-de-imagen-7-formas-de-rostro-visagismo-fr-1200.webp "Les sept formes de visage. Le contour est celui d’un vrai visage ; la ligne de points par-dessus marque la forme géométrique qui le résume.")

![Lunettes selon la forme du visage : visage rond avec une monture fine et anguleuse ; visage carré avec une monture arrondie](/media/caro/edit/asesoria-de-imagen-gafas-cara-redonda-y-cuadrada-1200.webp "Visage rond avec une monture anguleuse (à gauche) et visage carré avec une monture arrondie (à droite). Le pointillé marque la forme du visage.")

Sur un **visage rond**, aux lignes courbes et presque aussi large que long, une monture fine et anguleuse, un peu plus large que haute, affine et allonge ; un pont clair ouvre le regard. Sur un **visage carré**, au front et à la mâchoire larges, c’est l’inverse qui fonctionne : des montures arrondies ou ovales, en métal léger, qui adoucissent les angles, et jamais plus larges que le visage lui-même.

Trois règles valent pour toutes les formes : la monture ne cache pas les sourcils ; elle ne dépasse pas la partie la plus large du visage ; et le pont se choisit avec le nez et les yeux (foncé et bas si les yeux sont écartés ou le nez long, clair et plus haut dans le cas contraire).

### Contouring : ombre et lumière

Le maquillage suit la même idée avec deux outils : l’ombre creuse et la lumière avance. La lumière va presque toujours aux mêmes endroits : le centre du front, sous les yeux, l’arête du nez et le menton. C’est l’ombre qui change avec la forme du visage.

![Contouring selon la forme du visage : ombre sur les côtés du visage rond et aux coins du visage carré, lumière au centre](/media/caro/edit/asesoria-de-imagen-contouring-cara-redonda-y-cuadrada-1200.webp "Les zones foncées sont l’ombre et les claires, la lumière. Visage rond à gauche, visage carré à droite.")

Sur un **visage rond**, l’ombre descend sur les côtés, de la tempe à la mâchoire, avec une touche sous la pommette : le visage s’allonge. Sur un **visage carré**, l’ombre va aux quatre coins, les tempes et l’angle de la mâchoire, pour les arrondir. Dans les deux cas, un ton mat un peu plus foncé que votre peau et très estompé : si l’on voit le trait, il y a trop de produit. Le fard à paupières, en revanche, dépend de la forme des yeux, pas de celle du visage.

## Des clés pour vous guider (et un regard professionnel)

Ce sont les techniques les plus connues et les plus partagées du conseil en image, et elles vous donnent déjà beaucoup de clés. Avec un peu de pratique et quelques lectures, elles vous permettent de vous guider de façon simple et efficace : regarder votre garde-robe autrement, choisir une monture en connaissance de cause, comprendre pourquoi une couleur vous va si bien.

Mais le conseil en image est plus qu’une somme de techniques, et aucune ne remplace le vrai regard d’une conseillère en image. Elle les croise entre elles (une silhouette en A au visage carré et à la palette Automne pastel ne s’habille pas comme une autre A Hiver clair) et avec ce qu’aucun tableau ne mesure : votre style, votre travail, la façon dont vous voulez vous sentir.

Et si, à un moment, vous souhaitez cet accompagnement, CARO est là. Carolina conseille depuis 2017 : elle a accompagné des clientes en Belgique et aux États-Unis, et aujourd’hui depuis l’Espagne. Nous travaillons en personne, avec la lumière, des tissus et votre propre garde-robe, dans la Séance CARO · Image & Présence ou dans une Séance essentielle, centrée sur une seule question. Vous trouverez tout dans [nos services de conseil en image](/caro/servicios).

## Ce que je ne ferais pas

- Je ne suivrais pas un tableau à la lettre : c’est une carte, pas une loi.
- Je ne viderais pas ma garde-robe après avoir découvert ma saison. Il suffit souvent de déplacer une couleur : loin du visage, sur un pantalon ou un sac.
- Je ne garderais pas une étiquette pour toujours : les cheveux, la peau et la vie changent, et la lecture peut changer avec eux.

## Questions fréquentes

### Puis-je trouver ma saison seule, avec un test ?

Un test ou un jeu comme COLORIMETRY ME! donne une bonne piste et apprend à regarder. La confirmation demande la lumière du jour, des tissus d’essai et un œil exercé : entre deux saisons voisines, la différence est subtile.

### Et si je suis entre deux silhouettes ?

C’est très fréquent. Les lettres sont des repères, pas des cases. On prend la plus proche et on ajuste avec l’autre : un H avec un peu de taille, par exemple, peut emprunter des idées au X.

### Le conseil en image, c’est seulement pour acheter de nouveaux vêtements ?

Non. L’essentiel du travail se fait avec ce que vous avez déjà : de nouvelles associations, des pièces retrouvées et une liste courte et honnête de ce qui manque vraiment.`,
        seoTitle: 'Conseil en image : couleur, silhouette et visage · CARO. Casa de estilo',
        seoDescription:
          'Les 12 saisons de la colorimétrie, les six silhouettes (A, V, H, I, O, X), lunettes et contouring selon le visage. Expliqué avec des illustrations et des exemples par Carolina Collaer.',
      },
    },
  },
  {
    id: 'edit-estilo-chaqueta-verde',
    slug: 'como-combinar-una-chaqueta-verde-en-otono',
    title: '¿Verde en otoño? Pues sí. Cómo combinar una chaqueta verde',
    excerpt:
      'Cómo combinar una chaqueta verde en otoño, con qué colores funciona y por qué puede ser una de las decisiones más interesantes de tu armario.',
    category: 'estilo',
    author: 'Carolina Collaer',
    date: '2026-10-31',
    publishAt: '2026-10-31T08:00:00+01:00',
    heroImage: '/media/caro/estilo-chaqueta-verde-oliva-look-carolina-collaer-1200.webp',
    ogImage: '/media/caro/og/edit-como-combinar-una-chaqueta-verde-en-otono-2333c4e6.jpg',
    body: `Hay colores que asociamos automáticamente a una estación. El verde suele recordarnos a la primavera, mientras que en otoño pensamos en camel, marrón, gris o burdeos.

Pero ¿quién ha decidido que tenemos que vestir según los colores del paisaje?

A mí me interesa otra pregunta: ¿cómo podemos introducir un color diferente sin complicarnos el armario?

Y aquí es donde entra el verde.

## El verde también tiene su lugar en otoño

El verde tiene una cualidad interesante: según su tonalidad y cómo lo combines, puede resultar discreto, sofisticado o convertirse en el protagonista de un conjunto.

No necesitas vestir de verde de pies a cabeza. Una sola chaqueta puede cambiar por completo prendas que llevas años utilizando.

La clave está en saber acompañarla.

## Tres maneras de combinar una chaqueta verde

### 1. Verde y chocolate: una combinación con carácter

![Bloques de color: verde oliva y marrón chocolate, cada uno con sus tonos claro, medio y profundo](/media/caro/edit/combinar-chaqueta-verde-oliva-chocolate-1200.webp "Verde oliva y chocolate.")

El marrón chocolate tiene profundidad y aporta calidez. Con el verde crea una armonía especialmente interesante para el otoño.

Prueba con un pantalón chocolate, un jersey crudo y la chaqueta verde. Una combinación con personalidad que no resulta excesiva.

### 2. Verde y gris: sofisticación sin esfuerzo

![Bloques de color: verde oliva y gris, cada uno con sus tonos claro, medio y profundo](/media/caro/edit/combinar-chaqueta-verde-oliva-gris-1200.webp "Verde oliva y gris.")

El gris es uno de esos colores que permiten introducir tonos diferentes sin perder elegancia.

Un pantalón gris de corte recto, una camiseta o una camisa blanca y la chaqueta verde. Sencillo, urbano y perfectamente válido para trabajar, salir a comer o un día cualquiera.

### 3. Verde y burdeos: para salir de lo previsible

![Bloques de color: verde oliva y burdeos, cada uno con sus tonos claro, medio y profundo](/media/caro/edit/combinar-chaqueta-verde-oliva-burdeos-1200.webp "Verde oliva y burdeos.")

Esta es la combinación que más me interesa.

El burdeos aporta profundidad y el verde introduce contraste. No necesitas grandes dosis de ninguno de los dos: un bolso burdeos, como el **Rubí Rebelle** de los [hallazgos de CARO](/shop/hallazgos), puede bastar para transformar el conjunto.

Dos colores con presencia, siempre que sepamos equilibrarlos.

## ¿Y si el verde no pertenece a mi paleta?

Aquí conviene aclarar algo: no existe un único verde.

Hay verdes cálidos y fríos, luminosos, profundos y apagados. Un verde oliva no tiene el mismo efecto cerca del rostro que un verde esmeralda o un verde azulado.

Por eso, antes de descartar un color, merece la pena observar su tonalidad y cómo responde a tu piel.

Y conocer tu colorimetría no debería convertirse en una prohibición. Es una herramienta para elegir mejor y usar el color con más intención. Puedes empezar descubriendo tu paleta con [COLORIMETRY ME!](/estudio-de-estilo/colorimetria), el test de color del Estudio de estilo de CARO.

## Antes de comprar otra chaqueta

Hay una pregunta que siempre recomiendo hacerse: ¿puedo crear al menos tres conjuntos con prendas que ya tengo?

Si la respuesta es sí, probablemente estás ante una compra que merece la pena.

No se trata de incorporar un color porque esté de moda, sino de comprobar que de verdad encaja en tu manera de vestir.

## La elección de CARO

Esta temporada incorporo a los [hallazgos de CARO](/shop/hallazgos) una **chaqueta verde oliva**: acabado efecto ante, cuello de camisa, trabillas en los hombros y un bajo recogido que le da volumen. Pieza única.

![Carolina Collaer con la chaqueta verde oliva de los hallazgos de CARO, jersey negro y pantalón negro](/media/caro/edit/estilo-chaqueta-verde-oliva-carolina-collaer-1200.webp "La chaqueta oliva, pieza única de los hallazgos de CARO.")

La he elegido porque me interesa proponer alternativas a los colores habituales del otoño y demostrar que una prenda con personalidad no tiene por qué ser difícil de combinar.

Mi consejo: antes de comprar algo nuevo, imagina cómo convivirá con lo que ya tienes. Porque un buen armario no es el que acumula más prendas. Es el que te ofrece más posibilidades.

**Saber elegir. Saber llevar. Saber estar.**

## Preguntas frecuentes

### ¿Qué colores combinan con una chaqueta verde?

En otoño, marrón chocolate, gris y burdeos son tres apuestas seguras. Los neutros claros, como el crudo o el blanco, la suavizan y la hacen más fácil de llevar a diario.

### ¿Qué verde me favorece?

Depende de tu tonalidad. Los verdes cálidos y apagados, como el oliva, suelen acompañar a las pieles de subtono cálido; los verdes fríos, como el esmeralda o el verde azulado, a las de subtono frío. Un test de colorimetría te da una primera pista.

### ¿Cómo sé si una chaqueta merece la pena?

Si puedes crear al menos tres conjuntos con prendas que ya tienes, es una compra que encaja en tu armario.`,
    seoTitle: 'Cómo combinar una chaqueta verde en otoño · CARO. Casa de estilo',
    seoDescription:
      'Descubre cómo combinar una chaqueta verde en otoño con marrón, gris y burdeos. Consejos de estilo y colorimetría para elegir con criterio.',
    relatedProductSlugs: [
      // Hallazgos (piezas únicas, solo en D1): dejan de salir solos cuando se archivan.
      'chaqueta-oliva',
      'bolso-rubi-rebelle',
      'surkana-jersey-cuello-perkins-relieve-verde',
    ],
    endLinks: ['colorimetry', 'guide'],
    i18n: {
      en: {
        title: 'Green in autumn? Yes. How to wear a green jacket',
        excerpt:
          'How to wear a green jacket in autumn, which colours it works with and why it may be one of the most interesting decisions in your wardrobe.',
        body: `There are colours we automatically link to a season. Green tends to remind us of spring, while in autumn we think of camel, brown, grey or burgundy.

But who decided we have to dress in the colours of the landscape?

I am more interested in another question: how can we bring in a different colour without complicating our wardrobe?

And this is where green comes in.

## Green has its place in autumn too

Green has an interesting quality: depending on its shade and how you pair it, it can be discreet, sophisticated or become the star of an outfit.

You don’t need to wear green from head to toe. A single jacket can completely change pieces you have been wearing for years.

The key is knowing what to wear it with.

## Three ways to wear a green jacket

### 1. Green and chocolate: a pairing with character

![Colour blocks: olive green and chocolate brown, each with its light, medium and deep tones](/media/caro/edit/combinar-chaqueta-verde-oliva-chocolate-1200.webp "Olive green and chocolate.")

Chocolate brown has depth and brings warmth. With green it creates a harmony that is especially interesting in autumn.

Try chocolate trousers, an ecru jumper and the green jacket. A combination with personality that never feels too much.

### 2. Green and grey: effortless sophistication

![Colour blocks: olive green and grey, each with its light, medium and deep tones](/media/caro/edit/combinar-chaqueta-verde-oliva-gris-1200.webp "Olive green and grey.")

Grey is one of those colours that lets you bring in different shades without losing elegance.

Straight grey trousers, a white T-shirt or shirt and the green jacket. Simple, urban and perfect for work, lunch out or any ordinary day.

### 3. Green and burgundy: to step away from the predictable

![Colour blocks: olive green and burgundy, each with its light, medium and deep tones](/media/caro/edit/combinar-chaqueta-verde-oliva-burdeos-1200.webp "Olive green and burgundy.")

This is the pairing I find most interesting.

Burgundy brings depth and green adds contrast. You don’t need large doses of either: a burgundy bag, like the **Rubí Rebelle** in [CARO’s finds](/shop/hallazgos), can be enough to transform the outfit.

Two colours with presence, as long as we know how to balance them.

## What if green isn’t in my palette?

Something worth clarifying here: there is no single green.

There are warm and cool greens, bright, deep and muted ones. Olive green doesn’t have the same effect near the face as emerald or teal.

So before ruling out a colour, it is worth looking at its shade and how it responds to your skin.

And knowing your colour analysis shouldn’t become a ban. It is a tool to choose better and use colour with more intention. You can start by discovering your palette with [COLORIMETRY ME!](/estudio-de-estilo/colorimetria), the colour quiz of CARO’s style studio.

## Before you buy another jacket

There is one question I always recommend asking: can I put together at least three outfits with pieces I already own?

If the answer is yes, it is probably a purchase worth making.

It isn’t about adding a colour because it is in fashion, but about checking that it truly fits the way you dress.

## CARO’s choice

This season I am adding an **olive green jacket** to [CARO’s finds](/shop/hallazgos): suede-effect finish, shirt collar, shoulder tabs and a gathered hem that gives it volume. A one-off piece.

![Carolina Collaer wearing the olive green jacket from CARO’s finds with a black jumper and black trousers](/media/caro/edit/estilo-chaqueta-verde-oliva-carolina-collaer-1200.webp "The olive jacket, a one-off piece from CARO’s finds.")

I chose it because I want to offer alternatives to the usual autumn colours and show that a piece with personality doesn’t have to be hard to wear.

My advice: before buying something new, imagine how it will live with what you already have. Because a good wardrobe isn’t the one with the most pieces. It is the one that gives you the most possibilities.

**Know how to choose. Know how to wear. Know how to be.**

## Frequently asked questions

### Which colours go with a green jacket?

In autumn, chocolate brown, grey and burgundy are three safe choices. Light neutrals, such as ecru or white, soften it and make it easier to wear every day.

### Which green suits me?

It depends on your colouring. Warm, muted greens like olive tend to suit warm undertones; cool greens like emerald or teal tend to suit cool undertones. A colour analysis quiz gives you a first clue.

### How do I know if a jacket is worth it?

If you can put together at least three outfits with pieces you already own, it is a purchase that fits your wardrobe.`,
        seoTitle: 'How to wear a green jacket in autumn · CARO. Casa de estilo',
        seoDescription:
          'How to wear a green jacket in autumn with brown, grey and burgundy. Style and colour analysis tips to choose with intention.',
      },
      fr: {
        title: 'Du vert en automne ? Oui. Comment porter une veste verte',
        excerpt:
          'Comment porter une veste verte en automne, avec quelles couleurs elle fonctionne et pourquoi elle peut être l’une des décisions les plus intéressantes de votre garde-robe.',
        body: `Il y a des couleurs que l’on associe automatiquement à une saison. Le vert nous rappelle souvent le printemps, tandis qu’en automne on pense au camel, au marron, au gris ou au bordeaux.

Mais qui a décidé que nous devions nous habiller aux couleurs du paysage ?

Une autre question m’intéresse davantage : comment introduire une couleur différente sans compliquer notre garde-robe ?

Et c’est là qu’intervient le vert.

## Le vert a aussi sa place en automne

Le vert a une qualité intéressante : selon sa nuance et la façon de l’associer, il peut être discret, sophistiqué ou devenir le personnage principal d’une tenue.

Inutile de s’habiller en vert de la tête aux pieds. Une seule veste peut transformer complètement des pièces que vous portez depuis des années.

Tout est dans la façon de l’accompagner.

## Trois façons de porter une veste verte

### 1. Vert et chocolat : une association de caractère

![Blocs de couleur : vert olive et brun chocolat, chacun avec ses tons clair, moyen et profond](/media/caro/edit/combinar-chaqueta-verde-oliva-chocolate-1200.webp "Vert olive et chocolat.")

Le brun chocolat a de la profondeur et apporte de la chaleur. Avec le vert, il crée une harmonie particulièrement intéressante en automne.

Essayez un pantalon chocolat, un pull écru et la veste verte. Une association qui a de la personnalité sans jamais être excessive.

### 2. Vert et gris : la sophistication sans effort

![Blocs de couleur : vert olive et gris, chacun avec ses tons clair, moyen et profond](/media/caro/edit/combinar-chaqueta-verde-oliva-gris-1200.webp "Vert olive et gris.")

Le gris fait partie de ces couleurs qui permettent d’introduire d’autres nuances sans perdre en élégance.

Un pantalon gris coupe droite, un t-shirt ou une chemise blanche et la veste verte. Simple, urbain et parfait pour travailler, déjeuner dehors ou une journée ordinaire.

### 3. Vert et bordeaux : pour sortir du prévisible

![Blocs de couleur : vert olive et bordeaux, chacun avec ses tons clair, moyen et profond](/media/caro/edit/combinar-chaqueta-verde-oliva-burdeos-1200.webp "Vert olive et bordeaux.")

C’est l’association qui m’intéresse le plus.

Le bordeaux apporte de la profondeur et le vert introduit du contraste. Pas besoin de grandes doses de l’un ou de l’autre : un sac bordeaux, comme le **Rubí Rebelle** des [trouvailles de CARO](/shop/hallazgos), peut suffire à transformer la tenue.

Deux couleurs qui ont de la présence, à condition de savoir les équilibrer.

## Et si le vert n’est pas dans ma palette ?

Une précision s’impose : il n’existe pas un seul vert.

Il y a des verts chauds et froids, lumineux, profonds et éteints. Un vert olive n’a pas le même effet près du visage qu’un vert émeraude ou un vert bleuté.

Avant d’écarter une couleur, il vaut donc la peine d’observer sa nuance et la façon dont elle réagit à votre peau.

Et connaître votre colorimétrie ne devrait pas devenir une interdiction. C’est un outil pour mieux choisir et utiliser la couleur avec plus d’intention. Vous pouvez commencer par découvrir votre palette avec [COLORIMETRY ME!](/estudio-de-estilo/colorimetria), le test de couleur du studio de style de CARO.

## Avant d’acheter une autre veste

Il y a une question que je recommande toujours de se poser : puis-je créer au moins trois tenues avec des pièces que j’ai déjà ?

Si la réponse est oui, c’est probablement un achat qui en vaut la peine.

Il ne s’agit pas d’adopter une couleur parce qu’elle est à la mode, mais de vérifier qu’elle correspond vraiment à votre façon de vous habiller.

## Le choix de CARO

Cette saison, j’ajoute aux [trouvailles de CARO](/shop/hallazgos) une **veste vert olive** : finition effet daim, col chemise, pattes d’épaule et un bas froncé qui lui donne du volume. Pièce unique.

![Carolina Collaer avec la veste vert olive des trouvailles de CARO, un pull noir et un pantalon noir](/media/caro/edit/estilo-chaqueta-verde-oliva-carolina-collaer-1200.webp "La veste olive, pièce unique des trouvailles de CARO.")

Je l’ai choisie parce que j’aime proposer des alternatives aux couleurs habituelles de l’automne et montrer qu’une pièce de caractère n’est pas forcément difficile à porter.

Mon conseil : avant d’acheter quelque chose de nouveau, imaginez comment cela vivra avec ce que vous avez déjà. Car une bonne garde-robe n’est pas celle qui accumule le plus de pièces. C’est celle qui vous offre le plus de possibilités.

**Savoir choisir. Savoir porter. Savoir être.**

## Questions fréquentes

### Quelles couleurs associer à une veste verte ?

En automne, le brun chocolat, le gris et le bordeaux sont trois valeurs sûres. Les neutres clairs, comme l’écru ou le blanc, l’adoucissent et la rendent plus facile à porter au quotidien.

### Quel vert me va ?

Cela dépend de votre carnation. Les verts chauds et éteints, comme l’olive, accompagnent souvent les sous-tons chauds ; les verts froids, comme l’émeraude ou le vert bleuté, les sous-tons froids. Un test de colorimétrie vous donne une première piste.

### Comment savoir si une veste en vaut la peine ?

Si vous pouvez créer au moins trois tenues avec des pièces que vous avez déjà, c’est un achat qui a sa place dans votre garde-robe.`,
        seoTitle: 'Comment porter une veste verte en automne · CARO. Casa de estilo',
        seoDescription:
          'Comment porter une veste verte en automne avec du marron, du gris et du bordeaux. Conseils de style et de colorimétrie pour choisir avec intention.',
      },
    },
  },
  {
    id: 'edit-estilo-bolso-rojo',
    slug: 'bolso-rojo-como-llevar-el-color-de-la-temporada',
    title: 'El rojo de la temporada empieza por un bolso',
    excerpt:
      'Si el color te da respeto, empieza por un accesorio. Un bolso rojo y tres maneras de llevarlo con lo que ya tienes en el armario.',
    category: 'estilo',
    author: 'Carolina Collaer',
    date: '2027-03-06',
    publishAt: '2027-03-06T08:00:00+01:00',
    heroImage: '/media/caro/estilo-bolso-burdeos-rubi-rebelle-carolina-collaer-1040.webp',
    ogImage: '/media/caro/og/edit-bolso-rojo-como-llevar-el-color-de-la-temporada-51709a4c.jpg',
    body: `Hay mujeres que llevan años vistiendo de negro, gris y camel. Y lo hacen muy bien.

Pero a veces apetece un poco de color y no sabemos por dónde empezar.

Mi propuesta es sencilla: empieza por un bolso.

## Por qué el rojo, por qué ahora

Este otoño-invierno el rojo vuelve a ocupar un lugar protagonista. No como algo pasajero, sino como uno de esos colores que nunca terminan de irse y que, cada cierto tiempo, recuperan fuerza.

Y tiene algo que me gusta mucho: el rojo no pide permiso. Da energía a un look sencillo sin necesidad de añadir nada más.

## Por qué en un bolso

- No está junto a tu cara, así que no tienes que preguntarte si te favorece.
- Convive con todo lo que ya tienes: neutros, rayas, cuadros.
- Es una dosis pequeña de color. Suficiente para cambiar un look, sin que te sientas disfrazada.

## El bolso

El que hemos elegido es un **bolso de hombro de charol rojo**: brillo intenso, tacto liso, silueta triangular y desestructurada, asa fina regulable y cierre de cremallera. Un diseño depurado y luminoso, con aire cosmopolita.

## Tres maneras de llevarlo

### 1. Con gris: el color como único acento

Una **cazadora corta de espiga gris**, un vaquero y un jersey fino. Todo tranquilo, todo neutro, y el rojo como único punto de color. Es la forma más fácil de empezar y, para mí, una de las más elegantes.

### 2. Con rayas: rojo y negro

Un **jersey de cuello polo de rayas negro** con un pantalón oscuro. Las rayas aportan ritmo y el rojo lo remata. Rojo y negro es una combinación clásica que no falla nunca.

### 3. Con cuadros: color sobre color

La **bomber de cuadros en camel claro** ya tiene estampado y luz. El bolso rojo no compite con ella: la acompaña. Es la opción más atrevida de las tres, perfecta para un fin de semana.

## Lo que no haría

No buscaría zapatos, cinturón y pendientes rojos a juego. Un solo punto de color fuerte tiene más intención que cinco.

Y no lo reservaría para ocasiones especiales. Un bolso así funciona un martes cualquiera.

## Si prefieres algo más discreto

El mismo bolso existe en **charol negro**. Misma forma, mismo brillo, para quien quiere la pieza sin el color.

## Y si quieres una pieza única

En los [hallazgos de CARO](/shop/hallazgos) hay bolsos que encuentro y elijo uno a uno, como el **Rubí Rebelle** de las fotos: un rojo rubí, más profundo, aquí con una chaqueta color coñac y pantalón negro. De cada uno hay solo una pieza, e iré encontrando más.

![Carolina Collaer con el bolso Rubí Rebelle en rojo rubí, chaqueta color coñac y pantalón negro](/media/caro/edit/estilo-bolso-burdeos-rubi-rebelle-chaqueta-conac-carolina-collaer-1200.webp "El bolso Rubí Rebelle, pieza única de los hallazgos de CARO.")

## Preguntas frecuentes

### ¿El rojo le queda bien a todo el mundo?

En un bolso, el rojo no está junto a tu cara, así que depende mucho menos de tu tono de piel que una prenda. Es la manera más sencilla de llevarlo.

### ¿Con qué zapatos lo combino?

Con neutros: negro, camel, blanco o gris. Deja que el bolso sea el único punto de color.

### ¿Sirve también por la noche?

Sí. El charol tiene brillo propio, así que pasa del día a la noche sin cambiar nada más.`,
    seoTitle: 'El rojo de la temporada empieza por un bolso · CARO. Casa de estilo',
    seoDescription:
      'Cómo llevar el rojo de este otoño-invierno sin miedo: un bolso de charol rojo y tres maneras de combinarlo con gris, rayas y cuadros.',
    relatedProductSlugs: [
      // Hallazgos (piezas únicas, solo en D1): dejan de salir solos cuando se archivan.
      'bolso-rubi-rebelle',
      'surkana-bolso-hombro-charol-rojo',
      'surkana-cazadora-corta-espiga-lana-gris',
      'surkana-jersey-cuello-polo-rayas-negro',
      'surkana-cazadora-bomber-cuadros-camel',
      'surkana-bolso-hombro-charol-negro',
      'bolso-caro-mokka',
      'bolso-caro-bicolor',
    ],
    i18n: {
      en: {
        title: 'This season’s red starts with a bag',
        excerpt:
          'If colour feels daunting, start with an accessory. A red bag and three ways to wear it with what is already in your wardrobe.',
        body: `Some women have dressed in black, grey and camel for years. And they do it beautifully.

But sometimes we want a little colour and don’t know where to begin.

My suggestion is simple: start with a bag.

## Why red, why now

This autumn-winter, red takes centre stage again. Not as a passing fad, but as one of those colours that never quite leave and, every so often, come back stronger.

And it has something I really like: red doesn’t ask for permission. It brings energy to a simple look without adding anything else.

## Why a bag

- It isn’t next to your face, so you don’t have to wonder whether it suits you.
- It works with everything you already own: neutrals, stripes, checks.
- It is a small dose of colour. Enough to change a look, without feeling like you are in costume.

## The bag

The one we chose is a **red patent shoulder bag**: intense shine, smooth to the touch, a triangular, unstructured silhouette, a thin adjustable strap and a zip fastening. A clean, luminous design with a cosmopolitan air.

## Three ways to wear it

### 1. With grey: colour as the only accent

A **short grey herringbone jacket**, jeans and a fine knit. Everything calm, everything neutral, and red as the only touch of colour. It is the easiest way to start and, to me, one of the most elegant.

### 2. With stripes: red and black

A **black striped polo-collar jumper** with dark trousers. The stripes add rhythm and the red finishes it off. Red and black is a classic combination that never fails.

### 3. With checks: colour on colour

The **light camel check bomber** already has pattern and light. The red bag doesn’t compete with it: it keeps it company. It is the boldest of the three, perfect for a weekend.

## What I wouldn’t do

I wouldn’t look for matching red shoes, belt and earrings. A single strong touch of colour has more intention than five.

And I wouldn’t save it for special occasions. A bag like this works on any ordinary Tuesday.

## If you prefer something more discreet

The same bag comes in **black patent**. Same shape, same shine, for those who want the piece without the colour.

## And if you want a one-off piece

In [CARO’s finds](/shop/hallazgos) there are bags I discover and choose one by one, like the **Rubí Rebelle** in these photos: a ruby red, deeper, worn here with a cognac jacket and black trousers. There is only one of each, and I will keep finding more.

![Carolina Collaer with the Rubí Rebelle bag in ruby red, a cognac jacket and black trousers](/media/caro/edit/estilo-bolso-burdeos-rubi-rebelle-chaqueta-conac-carolina-collaer-1200.webp "The Rubí Rebelle bag, a one-off piece from CARO’s finds.")

## Frequently asked questions

### Does red suit everyone?

In a bag, red isn’t next to your face, so it depends far less on your skin tone than a garment would. It is the easiest way to wear it.

### Which shoes should I wear with it?

Neutrals: black, camel, white or grey. Let the bag be the only touch of colour.

### Does it work in the evening too?

Yes. Patent has its own shine, so it goes from day to evening without changing anything else.`,
        seoTitle: 'This season’s red starts with a bag · CARO. Casa de estilo',
        seoDescription:
          'How to wear this autumn-winter’s red without fear: a red patent bag and three ways to style it with grey, stripes and checks.',
      },
      fr: {
        title: 'Le rouge de la saison commence par un sac',
        excerpt:
          'Si la couleur vous intimide, commencez par un accessoire. Un sac rouge et trois façons de le porter avec ce que vous avez déjà dans votre garde-robe.',
        body: `Certaines femmes s’habillent depuis des années en noir, gris et camel. Et elles le font très bien.

Mais parfois, on a envie d’un peu de couleur sans savoir par où commencer.

Ma proposition est simple : commencez par un sac.

## Pourquoi le rouge, pourquoi maintenant

Cet automne-hiver, le rouge reprend une place de premier plan. Pas comme une mode passagère, mais comme l’une de ces couleurs qui ne partent jamais vraiment et qui, de temps en temps, reviennent avec force.

Et il a quelque chose que j’aime beaucoup : le rouge ne demande pas la permission. Il donne de l’énergie à une tenue simple sans rien ajouter d’autre.

## Pourquoi un sac

- Il n’est pas près de votre visage, vous n’avez donc pas à vous demander s’il vous va.
- Il s’accorde avec tout ce que vous avez déjà : neutres, rayures, carreaux.
- C’est une petite dose de couleur. Assez pour changer une tenue, sans avoir l’impression d’être déguisée.

## Le sac

Celui que nous avons choisi est un **sac porté épaule en vernis rouge** : brillance intense, toucher lisse, silhouette triangulaire et déstructurée, anse fine réglable et fermeture éclair. Un design épuré et lumineux, à l’allure cosmopolite.

## Trois façons de le porter

### 1. Avec du gris : la couleur comme seul accent

Une **veste courte à chevrons grise**, un jean et un pull fin. Tout est calme, tout est neutre, et le rouge est la seule touche de couleur. C’est la façon la plus simple de commencer et, pour moi, l’une des plus élégantes.

### 2. Avec des rayures : rouge et noir

Un **pull col polo à rayures noir** avec un pantalon foncé. Les rayures donnent du rythme et le rouge les termine. Rouge et noir, c’est une association classique qui ne déçoit jamais.

### 3. Avec des carreaux : couleur sur couleur

Le **blouson bomber à carreaux camel clair** a déjà du motif et de la lumière. Le sac rouge ne rivalise pas avec lui : il l’accompagne. C’est l’option la plus audacieuse des trois, parfaite pour un week-end.

## Ce que je ne ferais pas

Je ne chercherais pas des chaussures, une ceinture et des boucles d’oreilles rouges assorties. Une seule touche de couleur forte a plus d’intention que cinq.

Et je ne le réserverais pas aux grandes occasions. Un sac comme celui-ci fonctionne un mardi ordinaire.

## Si vous préférez plus de discrétion

Le même sac existe en **vernis noir**. Même forme, même brillance, pour celles qui veulent la pièce sans la couleur.

## Et si vous voulez une pièce unique

Dans les [trouvailles de CARO](/shop/hallazgos), il y a des sacs que je déniche et choisis un par un, comme le **Rubí Rebelle** de ces photos, un rouge rubis plus profond, porté ici avec une veste couleur cognac et un pantalon noir. Il n’y en a qu’un de chaque, et j’en trouverai d’autres.

![Carolina Collaer avec le sac Rubí Rebelle rouge rubis, une veste couleur cognac et un pantalon noir](/media/caro/edit/estilo-bolso-burdeos-rubi-rebelle-chaqueta-conac-carolina-collaer-1200.webp "Le sac Rubí Rebelle, pièce unique des trouvailles de CARO.")

## Questions fréquentes

### Le rouge va-t-il à tout le monde ?

Sur un sac, le rouge n’est pas près de votre visage, il dépend donc beaucoup moins de votre teint qu’un vêtement. C’est la façon la plus simple de le porter.

### Avec quelles chaussures l’associer ?

Avec des neutres : noir, camel, blanc ou gris. Laissez le sac être la seule touche de couleur.

### Fonctionne-t-il aussi le soir ?

Oui. Le vernis a sa propre brillance, il passe donc du jour au soir sans rien changer d’autre.`,
        seoTitle: 'Le rouge de la saison commence par un sac · CARO. Casa de estilo',
        seoDescription:
          'Comment porter le rouge de cet automne-hiver sans crainte : un sac en vernis rouge et trois façons de l’associer au gris, aux rayures et aux carreaux.',
      },
    },
  },
  {
    id: 'edit-belleza-manchas-despues-del-verano',
    slug: 'manchas-despues-del-verano',
    title: 'Manchas después del verano: lo que haría en octubre',
    excerpt:
      'El verano se va y algunas manchas se quedan. No soy dermatóloga, pero sí sé ordenar una rutina: menos productos, bien elegidos y con constancia.',
    category: 'belleza',
    author: 'Carolina Collaer',
    date: '2026-10-10',
    publishAt: '2026-10-10T08:00:00+02:00',
    heroImage: '/media/caro/belleza-piel-que-cambia-carolina-collaer-960.webp',
    ogImage: '/media/caro/og/edit-manchas-despues-del-verano-c04e3729.jpg',
    body: `Octubre tiene algo de espejo sincero.

Se acaba el bronceado, vuelve la luz más baja y, de pronto, aparecen en la cara pequeñas manchas que en agosto no veíamos. O que no queríamos ver.

No soy dermatóloga y no voy a decirte qué tiene tu piel. Lo que sí puedo hacer es contarte cómo ordenaría yo una rutina para este momento del año, con pocos productos y mucho criterio.

## Primero, lo importante

Si una mancha es nueva, cambia de forma, de color o de tamaño, no es un tema de cosmética: es una visita al dermatólogo. Esta rutina es para lo de siempre: el tono algo desigual, las marcas que deja el sol, esa luminosidad que se apaga al final del verano.

## Para quién es esta rutina

- Para ti si notas el tono menos uniforme después del verano.
- Para ti si quieres algo sencillo, que puedas mantener semanas sin pensarlo.
- No es para ti si buscas un resultado inmediato. El cuidado de las manchas es cuestión de constancia, no de un fin de semana.

## La rutina que propongo: tres productos

### Por la mañana: luz y protección

Sobre la piel limpia, unas gotas de **Glow Drops Vitamin C Serum** de Meisani. Lleva vitamina C encapsulada y la marca lo indica para pieles apagadas o con manchas, preferiblemente de día. Es ligero y va antes de la crema.

Después, tu hidratante.

Y al final, siempre, **Aloe-Lujah Sunscreen Serum SPF50+**. Es el paso que no se negocia: sin protección solar, todo lo demás pierde sentido. Su textura es fluida, sin efecto blanco, y por eso es fácil de llevar a diario, también debajo del maquillaje.

### Por la noche: el cuidado antimanchas

Sobre la piel limpia, el **Tranexamic Acid & Licorice Dark Spot Treatment** de Ondo Beauty 36.5, en las zonas con manchas o en todo el rostro, antes de la crema. Combina ácido tranexámico y regaliz, y la marca lo describe como apto incluso para piel sensible. También se puede usar por la mañana, antes de la hidratante; si lo haces, el protector solar sigue siendo imprescindible.

## Lo que no haría

No mezclaría diez activos a la vez. No cambiaría de producto cada semana. Y no esperaría milagros en días.

Elegir tres productos y darles tiempo es, en el fondo, la misma idea que defiendo para el armario: menos, mejor y con intención.

## Preguntas frecuentes

### ¿Necesito protector solar en otoño e invierno?

Sí. La luz del día sigue ahí aunque haga frío o esté nublado, y cualquier rutina para las manchas pierde sentido sin protección diaria.

### ¿Cuándo notaré cambios?

Depende de cada piel y de cada mancha. Lo honesto es pensar en semanas de uso constante, no en días. Si algo te preocupa, consulta a un dermatólogo.

### ¿Sirve para piel sensible?

Según la marca, el cuidado antimanchas y el protector solar son aptos para piel sensible. Aun así, si tu piel reacciona con facilidad, introduce un producto nuevo cada vez.`,
    seoTitle: 'Manchas después del verano: lo que haría en octubre · CARO. Casa de estilo',
    seoDescription:
      'Cómo ordenar una rutina sencilla para las manchas después del verano: vitamina C por la mañana, cuidado antimanchas por la noche y protector solar cada día.',
    relatedProductSlugs: [
      'meisani-glow-drops-vitamin-c-serum',
      'ondo-tranexamic-acid-licorice-dark-spot-treatment',
      'meisani-aloe-lujah-sunscreen-serum-spf50',
    ],
    i18n: {
      en: {
        title: 'Dark spots after summer: what I would do in October',
        excerpt:
          'Summer leaves and some dark spots stay. I am not a dermatologist, but I do know how to put a routine in order: fewer products, well chosen, used consistently.',
        body: `October is an honest mirror.

The tan fades, the light gets lower and, suddenly, small dark spots appear on the face that we didn’t see in August. Or didn’t want to see.

I am not a dermatologist and I won’t tell you what your skin has. What I can do is tell you how I would put a routine in order for this time of year, with few products and a lot of judgement.

## First, what matters most

If a spot is new, or changes shape, colour or size, it isn’t a cosmetics question: it’s a visit to the dermatologist. This routine is for the usual things: a slightly uneven tone, the marks the sun leaves, that radiance that fades at the end of summer.

## Who this routine is for

- For you if your tone looks less even after summer.
- For you if you want something simple you can keep up for weeks without thinking about it.
- Not for you if you want an instant result. Caring for dark spots is about consistency, not a weekend.

## The routine I suggest: three products

### In the morning: light and protection

On clean skin, a few drops of Meisani’s **Glow Drops Vitamin C Serum**. It contains encapsulated vitamin C and the brand recommends it for dull skin or skin with dark spots, preferably during the day. It is light and goes on before your cream.

Then, your moisturiser.

And last, always, **Aloe-Lujah Sunscreen Serum SPF50+**. This is the step that isn’t up for negotiation: without sun protection, everything else loses its point. The texture is fluid, with no white cast, which makes it easy to wear every day, under make-up too.

### At night: dark spot care

On clean skin, Ondo Beauty 36.5’s **Tranexamic Acid & Licorice Dark Spot Treatment**, on the areas with spots or over the whole face, before your cream. It combines tranexamic acid and licorice, and the brand describes it as suitable even for sensitive skin. It can also be used in the morning, before moisturiser; if you do, sunscreen is still essential.

## What I wouldn’t do

I wouldn’t mix ten actives at once. I wouldn’t switch products every week. And I wouldn’t expect miracles in days.

Choosing three products and giving them time is, at heart, the same idea I stand for in the wardrobe: less, better and with intention.

## Frequently asked questions

### Do I need sunscreen in autumn and winter?

Yes. Daylight is still there when it’s cold or cloudy, and any routine for dark spots loses its point without daily protection.

### When will I notice changes?

It depends on each skin and each spot. The honest answer is to think in weeks of consistent use, not days. If something worries you, see a dermatologist.

### Is it suitable for sensitive skin?

According to the brand, the dark spot care and the sunscreen are suitable for sensitive skin. Even so, if your skin reacts easily, introduce one new product at a time.`,
        seoTitle: 'Dark spots after summer: what I would do in October · CARO. Casa de estilo',
        seoDescription:
          'A simple routine for dark spots after summer: vitamin C in the morning, dark spot care at night and sunscreen every day.',
      },
      fr: {
        title: 'Les taches après l’été : ce que je ferais en octobre',
        excerpt:
          'L’été s’en va et certaines taches restent. Je ne suis pas dermatologue, mais je sais ordonner une routine : moins de produits, bien choisis, utilisés avec constance.',
        body: `Octobre a quelque chose d’un miroir sincère.

Le bronzage s’efface, la lumière baisse et, soudain, de petites taches apparaissent sur le visage que nous ne voyions pas en août. Ou que nous ne voulions pas voir.

Je ne suis pas dermatologue et je ne vais pas vous dire ce qu’a votre peau. Ce que je peux faire, c’est vous raconter comment j’ordonnerais une routine pour ce moment de l’année, avec peu de produits et beaucoup de discernement.

## D’abord, l’essentiel

Si une tache est nouvelle, ou change de forme, de couleur ou de taille, ce n’est pas une question de cosmétique : c’est une visite chez le dermatologue. Cette routine concerne l’habituel : un teint un peu irrégulier, les marques que laisse le soleil, cet éclat qui s’éteint à la fin de l’été.

## Pour qui est cette routine

- Pour vous si votre teint vous paraît moins uniforme après l’été.
- Pour vous si vous voulez quelque chose de simple, à suivre pendant des semaines sans y penser.
- Pas pour vous si vous cherchez un résultat immédiat. Prendre soin des taches est une affaire de constance, pas d’un week-end.

## La routine que je propose : trois produits

### Le matin : lumière et protection

Sur la peau propre, quelques gouttes de **Glow Drops Vitamin C Serum** de Meisani. Il contient de la vitamine C encapsulée et la marque le recommande pour les peaux ternes ou avec des taches, de préférence le jour. Il est léger et s’applique avant la crème.

Ensuite, votre crème hydratante.

Et pour finir, toujours, **Aloe-Lujah Sunscreen Serum SPF50+**. C’est l’étape qui ne se négocie pas : sans protection solaire, tout le reste perd son sens. Sa texture est fluide, sans traces blanches, et c’est pourquoi il est facile à porter chaque jour, même sous le maquillage.

### Le soir : le soin anti-taches

Sur la peau propre, le **Tranexamic Acid & Licorice Dark Spot Treatment** d’Ondo Beauty 36.5, sur les zones avec des taches ou sur tout le visage, avant la crème. Il associe acide tranexamique et réglisse, et la marque le décrit comme adapté même aux peaux sensibles. Il peut aussi s’utiliser le matin, avant la crème hydratante ; dans ce cas, la protection solaire reste indispensable.

## Ce que je ne ferais pas

Je ne mélangerais pas dix actifs à la fois. Je ne changerais pas de produit chaque semaine. Et je n’attendrais pas de miracles en quelques jours.

Choisir trois produits et leur laisser du temps, c’est au fond la même idée que je défends pour la garde-robe : moins, mieux et avec intention.

## Questions fréquentes

### Ai-je besoin d’une protection solaire en automne et en hiver ?

Oui. La lumière du jour est toujours là, même quand il fait froid ou que le ciel est couvert, et toute routine contre les taches perd son sens sans protection quotidienne.

### Quand vais-je remarquer des changements ?

Cela dépend de chaque peau et de chaque tache. Le plus honnête est de penser en semaines d’utilisation régulière, pas en jours. Si quelque chose vous inquiète, consultez un dermatologue.

### Est-ce adapté aux peaux sensibles ?

Selon la marque, le soin anti-taches et la protection solaire conviennent aux peaux sensibles. Malgré tout, si votre peau réagit facilement, introduisez un seul nouveau produit à la fois.`,
        seoTitle: 'Les taches après l’été : ce que je ferais en octobre · CARO. Casa de estilo',
        seoDescription:
          'Une routine simple pour les taches après l’été : vitamine C le matin, un soin anti-taches le soir et une protection solaire chaque jour.',
      },
    },
  },
  {
    id: 'edit-estilo-evoluciona',
    slug: 'estilo-mujer-que-evoluciona',
    title: 'Tu estilo también tiene derecho a evolucionar',
    excerpt:
      'El estilo evoluciona igual que nosotras. No necesitas más ropa: necesitas elegir mejor y vestir de acuerdo con la mujer en la que te estás convirtiendo.',
    category: 'estilo',
    author: 'Carolina Collaer',
    date: '2026-02-10',
    heroImage: '/media/caro/estilo-mujer-que-evoluciona-blazer-y-accesorios-caro-casa-de-estilo-1122.webp',
    ogImage: '/media/caro/og/edit-estilo-mujer-que-evoluciona-2206343c.jpg',
    body: `Hay un momento en el que vestirse deja de consistir en probarlo todo.

No porque perdamos curiosidad, sino porque empezamos a conocernos mejor.

El estilo evoluciona igual que nosotras. Cambia nuestro cuerpo, cambia nuestra vida, cambian nuestras prioridades y también cambia aquello que queremos comunicar cuando entramos en una habitación.

Por eso seguir vistiendo exactamente como hace diez años no siempre es fidelidad a una misma. A veces es simplemente costumbre.

Evolucionar no significa renunciar a lo que te gusta ni empezar de cero. Significa mirar tu armario con otros ojos.

Preguntarte qué sigue representándote. Qué compras por inercia. Qué guardas porque alguna vez funcionó. Y qué prendas, incluso siendo preciosas, ya no tienen nada que decir sobre la mujer que eres hoy.

En CARO no creo en acumular para tener estilo.

Creo en elegir.

Una buena chaqueta. Un pantalón que realmente te siente bien. Un accesorio con carácter. Un color inesperado. Una prenda que quizá no sea tendencia, pero que cuando la llevas parece inevitablemente tuya.

También creo que cumplir años no debería volvernos invisibles.

La elegancia no consiste en neutralizarse.

Puedes llevar volumen, color, grandes gafas, joyas, tacones, hombreras o una pieza extraordinaria y seguir siendo absolutamente sofisticada.

La diferencia está en cómo lo llevas.

Conocer tu estilo no significa encerrarte en una fórmula. Significa tener una base suficientemente sólida para poder jugar sin perderte.

Quizá por eso, con los años, necesito menos prendas y espero mucho más de cada una.

Que funcionen.

Que duren.

Que tengan intención.

Y, sobre todo, que me representen.

Porque el verdadero cambio no consiste en vestir según tu edad.

Consiste en vestir de acuerdo con la mujer en la que te estás convirtiendo.

No necesitas más. Necesitas elegir mejor.`,
    seoTitle: 'Tu estilo también tiene derecho a evolucionar · CARO. Casa de estilo',
    seoDescription:
      'Evolucionar el estilo no es renunciar a lo que te gusta, sino elegir mejor. Una reflexión de CARO sobre vestir de acuerdo con la mujer que eres hoy.',
    relatedProductSlugs: [
      'surkana-cazadora-corta-espiga-lana-gris',
      'surkana-cazadora-bomber-cuadros-camel',
      'pulsera-lind-16lind202',
    ],
    i18n: {
      en: {
        title: 'Your style has the right to evolve, too',
        excerpt:
          'Style evolves just as we do. You don’t need more clothes. You need to choose better, and dress according to the woman you are becoming.',
        body: `There is a moment when getting dressed stops being about trying everything on.

Not because we lose curiosity, but because we start to know ourselves better.

Style evolves just as we do. Our body changes, our life changes, our priorities change, and so does what we want to say when we walk into a room.

That is why dressing exactly as we did ten years ago is not always loyalty to ourselves. Sometimes it is simply habit.

Evolving does not mean giving up what you love or starting from scratch. It means looking at your wardrobe with fresh eyes.

Asking yourself what still represents you. What you buy out of inertia. What you keep because it once worked. And which pieces, however beautiful, no longer have anything to say about the woman you are today.

At CARO I don’t believe in accumulating in order to have style.

I believe in choosing.

A good jacket. Trousers that truly suit you. An accessory with character. An unexpected colour. A piece that may not be on trend, but that, when you wear it, looks inevitably yours.

I also believe that growing older should not make us invisible.

Elegance is not about neutralising yourself.

You can wear volume, colour, big glasses, jewellery, heels, shoulder pads or one extraordinary piece and still be absolutely sophisticated.

The difference is in how you wear it.

Knowing your style does not mean locking yourself into a formula. It means having a base solid enough to play without losing yourself.

Perhaps that is why, over the years, I need fewer pieces and expect much more from each one.

That they work.

That they last.

That they have intention.

And, above all, that they represent me.

Because real change is not about dressing according to your age.

It is about dressing according to the woman you are becoming.

You don’t need more. You need to choose better.`,
        seoTitle: 'Your style has the right to evolve, too · CARO. Casa de estilo',
        seoDescription:
          'Evolving your style isn’t about giving up what you love, but choosing better. A CARO reflection on dressing as the woman you are today.',
      },
      fr: {
        title: 'Votre style aussi a le droit d’évoluer',
        excerpt:
          'Le style évolue, tout comme nous. Vous n’avez pas besoin de plus de vêtements : vous avez besoin de mieux choisir et de vous habiller en accord avec la femme que vous êtes en train de devenir.',
        body: `Il y a un moment où s’habiller cesse de consister à tout essayer.

Non pas parce que nous perdons notre curiosité, mais parce que nous commençons à mieux nous connaître.

Le style évolue, tout comme nous. Notre corps change, notre vie change, nos priorités changent, et ce que nous voulons exprimer lorsque nous entrons dans une pièce change aussi.

C’est pourquoi continuer à s’habiller exactement comme il y a dix ans n’est pas toujours une forme de fidélité à soi-même. Parfois, ce n’est qu’une habitude.

Évoluer ne signifie pas renoncer à ce que vous aimez ni repartir de zéro. Cela signifie regarder votre garde-robe d’un autre œil.

Vous demander ce qui vous représente encore. Ce que vous achetez par inertie. Ce que vous gardez parce que cela a fonctionné un jour. Et quelles pièces, même magnifiques, n’ont plus rien à dire de la femme que vous êtes aujourd’hui.

Chez CARO, je ne crois pas qu’il faille accumuler pour avoir du style.

Je crois au choix.

Une belle veste. Un pantalon qui vous va vraiment bien. Un accessoire qui a du caractère. Une couleur inattendue. Une pièce qui n’est peut-être pas tendance, mais qui, lorsque vous la portez, semble inévitablement vôtre.

Je crois aussi que prendre de l’âge ne devrait pas nous rendre invisibles.

L’élégance ne consiste pas à s’effacer.

Vous pouvez porter du volume, de la couleur, de grandes lunettes, des bijoux, des talons, des épaulettes ou une pièce extraordinaire et rester absolument sophistiquée.

La différence tient à la manière dont vous le portez.

Connaître votre style ne signifie pas vous enfermer dans une formule. Cela signifie avoir une base suffisamment solide pour pouvoir jouer sans vous perdre.

C’est peut-être pour cela qu’avec les années, j’ai besoin de moins de pièces et que j’attends beaucoup plus de chacune.

Qu’elles fonctionnent.

Qu’elles durent.

Qu’elles aient une intention.

Et, surtout, qu’elles me représentent.

Car le vrai changement ne consiste pas à vous habiller selon votre âge.

Il consiste à vous habiller en accord avec la femme que vous êtes en train de devenir.

Vous n’avez pas besoin de plus. Vous avez besoin de mieux choisir.`,
        seoTitle: 'Votre style aussi a le droit d’évoluer · CARO. Casa de estilo',
        seoDescription:
          'Faire évoluer votre style, ce n’est pas renoncer à ce que vous aimez, c’est mieux choisir. Une réflexion de CARO : s’habiller selon la femme que vous êtes.',
      },
    },
  },
  {
    id: 'edit-korean-40',
    slug: 'belleza-reconocerse-y-cuidarse',
    title: 'Tu piel cambia. Tu forma de cuidarte también.',
    excerpt:
      'No soy dermatóloga: mi lugar es la belleza desde la imagen y el cuidado. Belleza no es corregirse, es reconocerse, cuidarse y saber elegir, venga de donde venga.',
    category: 'belleza',
    author: 'Carolina Collaer',
    date: '2026-02-05',
    heroImage: '/media/caro/belleza-piel-carolina-collaer-bufanda-negra-900.webp',
    ogImage: '/media/caro/og/edit-belleza-reconocerse-y-cuidarse-a1a12ef9.jpg',
    body: `No soy dermatóloga. Tampoco quiero convertirme en experta en ingredientes ni decirte qué necesita tu piel.

Mi lugar en esta conversación es otro.

Me interesa la belleza desde la imagen, desde el cuidado y, sobre todo, desde la manera en que una mujer se siente y se presenta al mundo.

Durante años hemos asociado belleza con corregir.

Corregir una arruga.

Una mancha.

Una textura.

Una ojera.

Un signo del tiempo.

Y cuanto más cambia nuestro rostro, más productos parecen querer convencernos de que hay algo que solucionar.

A mí me interesa otra mirada.

La belleza también puede ser aprender a acompañar nuestra evolución.

Personalmente, además, no puedo probar cualquier producto. Tengo una piel especialmente reactiva y eso me ha obligado a ser prudente, a observar más y a consumir de otra manera.

Quizá por eso desconfío de las rutinas interminables, de las recomendaciones universales y de esa necesidad constante de probar “lo último”.

Hay muchas mujeres que viven algo parecido.

Mujeres que reaccionan con facilidad a ciertos cosméticos. Que prefieren introducir un producto poco a poco. Que necesitan preguntar. Leer. Probar. Y, algunas veces, simplemente decir: esto no es para mí.

También ellas tienen un lugar en la conversación sobre belleza.

CARO quiere darle espacio a esa mujer.

No desde el miedo, sino desde la elección.

Porque cuidarse no debería significar utilizar todo lo que existe.

Puede significar conocer aquello que te funciona y respetar aquello que no.

Y aquí es donde para mí belleza e imagen empiezan a encontrarse.

Una buena imagen no depende únicamente de la ropa.

Es una piel que se siente confortable.

Un cabello que reconoces como tuyo.

Un maquillaje que acompaña sin disfrazar.

Un perfume que deja presencia.

Unas gafas que transforman un rostro.

Un color que ilumina.

Una manera de cuidarte que encaja con tu vida.

La belleza no es un departamento separado del estilo.

Forma parte de nuestra presencia.

Por eso en CARO habrá cosmética, pero también hablaremos de maquillaje, cabello, perfumes, gestos, color, cuidado y de todas esas pequeñas decisiones que construyen una imagen.

Y sí, podremos descubrir productos coreanos, franceses, españoles o de cualquier otro lugar.

El origen no será el criterio.

El criterio será si merece estar aquí.

No quiero recomendar por recomendar.

Quiero probar cuando sea posible, preguntar cuando no sepa y contar también cuando algo no me convenza.

Porque no necesito presentarme como experta para tener una mirada.

Y tampoco quiero que CARO sea un lugar donde todo funciona para todas.

Somos diferentes.

Nuestra piel cambia. Nuestro rostro cambia. Nuestro cuerpo cambia. Incluso aquello que consideramos bonito cambia.

Quizá la verdadera sofisticación esté precisamente ahí:

en dejar de perseguir una versión anterior de nosotras mismas y empezar a cuidar con atención la que tenemos delante.

Belleza no es corregirse.

Es reconocerse, cuidarse y saber elegir.`,
    seoTitle: 'Tu piel cambia. Tu forma de cuidarte también · CARO. Casa de estilo',
    seoDescription:
      'La visión de CARO sobre la belleza: no corregirse, sino reconocerse, cuidarse y saber elegir. Cosmética, maquillaje, perfume y color, sin importar el origen.',
    relatedProductSlugs: [
      'meisani-glow-drops-vitamin-c-serum',
      'meisani-plump-drops-hyaluronic-acid-serum',
      'meisani-aloe-lujah-sunscreen-serum-spf50',
    ],
    i18n: {
      en: {
        title: 'Your skin changes. So does the way you care for it.',
        excerpt:
          'I’m not a dermatologist: my place is beauty through image and care. Beauty is not about correcting yourself. It’s recognising, caring and knowing how to choose, wherever it comes from.',
        body: `I’m not a dermatologist. Nor do I want to become an ingredients expert or tell you what your skin needs.

My place in this conversation is another one.

I’m interested in beauty through image, through care and, above all, through the way a woman feels and presents herself to the world.

For years we have associated beauty with correcting.

Correcting a wrinkle.

A spot.

A texture.

A dark circle.

A sign of time.

And the more our face changes, the more products seem to want to convince us that there is something to fix.

I’m interested in another view.

Beauty can also be learning to accompany our evolution.

Personally, moreover, I can’t try just any product. I have particularly reactive skin, and that has forced me to be cautious, to observe more and to consume differently.

Perhaps that is why I’m wary of endless routines, of universal recommendations and of that constant need to try “the latest thing”.

There are many women who experience something similar.

Women who react easily to certain cosmetics. Who prefer to introduce a product little by little. Who need to ask. To read. To try. And, sometimes, simply to say: this is not for me.

They too have a place in the conversation about beauty.

CARO wants to make space for that woman.

Not from fear, but from choice.

Because caring for yourself should not mean using everything that exists.

It can mean knowing what works for you and respecting what does not.

And this is where, for me, beauty and image begin to meet.

A good image does not depend on clothes alone.

It is skin that feels comfortable.

Hair you recognise as your own.

Make-up that accompanies without disguising.

A perfume that leaves a presence.

Glasses that transform a face.

A colour that lights you up.

A way of caring for yourself that fits your life.

Beauty is not a department separate from style.

It is part of our presence.

That is why at CARO there will be skincare, but we will also talk about make-up, hair, perfumes, gestures, colour, care and all those small decisions that build an image.

And yes, we may discover Korean, French, Spanish or products from anywhere else.

Origin will not be the criterion.

The criterion will be whether it deserves to be here.

I don’t want to recommend for the sake of recommending.

I want to try when possible, to ask when I don’t know, and to say so too when something doesn’t convince me.

Because I don’t need to present myself as an expert to have a point of view.

And I don’t want CARO to be a place where everything works for everyone.

We are different.

Our skin changes. Our face changes. Our body changes. Even what we consider beautiful changes.

Perhaps true sophistication lies precisely there:

in ceasing to chase an earlier version of ourselves and starting to care, with attention, for the one in front of us.

Beauty is not about correcting yourself.

It is about recognising yourself, caring for yourself and knowing how to choose.`,
        seoTitle: 'Your skin changes. So does the way you care for it · CARO',
        seoDescription:
          'CARO’s view on beauty: not correcting yourself, but recognising, caring and knowing how to choose. Skincare, make-up, perfume and colour, whatever the origin.',
      },
      fr: {
        title: 'Votre peau change. Votre façon de prendre soin de vous aussi.',
        excerpt:
          'Je ne suis pas dermatologue : ma place, c’est la beauté vue à travers l’image et le soin. La beauté ne consiste pas à se corriger, mais à se reconnaître, à prendre soin de soi et à savoir choisir, d’où qu’elle vienne.',
        body: `Je ne suis pas dermatologue. Je ne veux pas non plus devenir experte en ingrédients ni vous dire ce dont votre peau a besoin.

Ma place dans cette conversation est ailleurs.

Ce qui m’intéresse, c’est la beauté vue à travers l’image, à travers le soin et, surtout, à travers la manière dont une femme se sent et se présente au monde.

Pendant des années, nous avons associé la beauté à l’idée de corriger.

Corriger une ride.

Une tache.

Une texture.

Un cerne.

Un signe du temps.

Et plus notre visage change, plus les produits semblent vouloir nous convaincre qu’il y a quelque chose à réparer.

Ce qui m’intéresse, c’est un autre regard.

La beauté peut aussi consister à apprendre à accompagner notre évolution.

Personnellement, je ne peux d’ailleurs pas essayer n’importe quel produit. J’ai une peau particulièrement réactive, ce qui m’a obligée à être prudente, à observer davantage et à consommer autrement.

C’est peut-être pour cela que je me méfie des routines interminables, des recommandations universelles et de ce besoin constant d’essayer « la dernière nouveauté ».

Beaucoup de femmes vivent quelque chose de semblable.

Des femmes qui réagissent facilement à certains cosmétiques. Qui préfèrent introduire un produit petit à petit. Qui ont besoin de demander. De lire. D’essayer. Et, parfois, de dire simplement : ce n’est pas pour moi.

Elles aussi ont leur place dans la conversation sur la beauté.

CARO veut faire de la place à cette femme.

Non pas par peur, mais par choix.

Car prendre soin de soi ne devrait pas signifier utiliser tout ce qui existe.

Cela peut signifier connaître ce qui vous convient et respecter ce qui ne vous convient pas.

Et c’est là que, pour moi, beauté et image commencent à se rencontrer.

Une belle image ne dépend pas uniquement des vêtements.

C’est une peau qui se sent bien.

Des cheveux que vous reconnaissez comme les vôtres.

Un maquillage qui accompagne sans déguiser.

Un parfum qui laisse une présence.

Des lunettes qui transforment un visage.

Une couleur qui illumine.

Une façon de prendre soin de vous qui s’accorde à votre vie.

La beauté n’est pas un domaine séparé du style.

Elle fait partie de notre présence.

C’est pourquoi, chez CARO, il y aura de la cosmétique, mais nous parlerons aussi de maquillage, de cheveux, de parfums, de gestes, de couleur, de soin et de toutes ces petites décisions qui construisent une image.

Et oui, nous pourrons découvrir des produits coréens, français, espagnols ou de n’importe quel autre endroit.

L’origine ne sera pas le critère.

Le critère sera de savoir s’il mérite d’être ici.

Je ne veux pas recommander pour recommander.

Je veux essayer lorsque c’est possible, demander lorsque je ne sais pas, et le dire aussi lorsque quelque chose ne me convainc pas.

Car je n’ai pas besoin de me présenter comme experte pour avoir un regard.

Et je ne veux pas non plus que CARO soit un lieu où tout fonctionne pour toutes.

Nous sommes différentes.

Notre peau change. Notre visage change. Notre corps change. Même ce que nous trouvons beau change.

La véritable sophistication se trouve peut-être précisément là :

dans le fait de cesser de poursuivre une version antérieure de nous-mêmes et de commencer à prendre soin, avec attention, de celle que nous avons devant nous.

La beauté ne consiste pas à se corriger.

Elle consiste à se reconnaître, à prendre soin de soi et à savoir choisir.`,
        seoTitle: 'Votre peau change. Votre façon d’en prendre soin aussi · CARO',
        seoDescription:
          'Pour CARO, la beauté ne consiste pas à se corriger, mais à se reconnaître, prendre soin de soi et savoir choisir. Cosmétique, maquillage, parfum et couleur.',
      },
    },
  },
  {
    id: 'edit-bienestar',
    slug: 'bienestar-sin-obsesion',
    title: 'Cuidarse sin convertirlo en una obsesión',
    excerpt:
      'Nunca se ha hablado tanto de bienestar. Una mirada serena: cuidarse sin perfección ni competición, creando mejores condiciones para estar bien.',
    category: 'bienestar',
    author: 'Carolina Collaer',
    date: '2026-02-01',
    heroImage: '/media/caro/bienestar-cuidarse-sin-obsesion-carolina-collaer-1200.webp',
    ogImage: '/media/caro/og/edit-bienestar-sin-obsesion-5e9284e1.jpg',
    body: `Nunca se ha hablado tanto de bienestar.

Dormir mejor. Comer mejor. Entrenar. Meditar. Respirar. Caminar. Suplementarse. Organizarse. Desconectar.

Incluso descansar parece haberse convertido en otra tarea que debemos hacer correctamente.

Y quizá por eso me interesa plantear el bienestar desde otro lugar.

Sin perfección.

Sin competición.

Sin convertir cada aspecto de nuestra vida en un proyecto de optimización.

Cuidarse también puede ser algo mucho más sencillo.

Dormir cuando estás cansada.

Mover tu cuerpo porque te hace sentir bien.

Sentarte a comer sin mirar constantemente el teléfono.

Ordenar un espacio porque vivir entre cierto orden te da tranquilidad.

Ponerte perfume aunque no tengas ningún acontecimiento especial.

Utilizar las cosas bonitas que guardas para “algún día”.

Salir a caminar.

Decir que no.

Vestirte bien para ti.

Cerrar una puerta cuando necesitas silencio.

No todo lo que nos hace bien necesita convertirse en método.

En CARO el bienestar no pretende enseñarte a vivir ni ofrecer una fórmula para convertirte en una versión mejorada de ti misma.

Me interesa mucho más recordar aquello que, en medio del ritmo diario, olvidamos con facilidad.

Que nuestro tiempo importa.

Que nuestra casa influye.

Que la manera en que nos presentamos también puede modificar cómo nos sentimos.

Que cuidarnos no debería ser otro motivo para exigirnos más.

Hay etapas en las que nuestro cuerpo, nuestra energía y nuestras prioridades cambian. Y quizá precisamente entonces necesitamos menos discursos sobre control y más capacidad para escucharnos.

No todo tiene que ser resuelto inmediatamente.

No todo tiene que producir resultados.

A veces cuidarse consiste simplemente en crear mejores condiciones para estar bien.

Una casa que puedas habitar.

Un armario que puedas utilizar.

Una rutina que puedas mantener.

Personas con las que puedas ser tú.

Momentos que no necesiten ser fotografiados.

Puede parecer poco.

Para mí es muchísimo.

Porque quizá vivir bien no sea llenar los días de cosas que debemos hacer.

Quizá sea aprender a habitarlos.

Bienestar con sentido. Sin ruido y sin fórmulas perfectas.`,
    seoTitle: 'Cuidarse sin convertirlo en una obsesión · CARO. Casa de estilo',
    seoDescription:
      'Bienestar con sentido, sin ruido ni fórmulas perfectas. Cuidarse desde la sencillez: crear mejores condiciones para estar bien, no otra lista de tareas.',
    relatedProductSlugs: [],
    i18n: {
      en: {
        title: 'Caring for yourself without turning it into an obsession',
        excerpt:
          'We have never talked so much about wellbeing. A calm take: caring for yourself without perfection or competition, creating better conditions to feel well.',
        body: `We have never talked so much about wellbeing.

Sleep better. Eat better. Train. Meditate. Breathe. Walk. Take supplements. Get organised. Disconnect.

Even resting seems to have become another task we are supposed to do correctly.

And perhaps that is why I like to approach wellbeing from another place.

Without perfection.

Without competition.

Without turning every aspect of our life into an optimisation project.

Caring for yourself can also be something much simpler.

Sleeping when you are tired.

Moving your body because it makes you feel good.

Sitting down to eat without constantly checking your phone.

Tidying a space because living with a certain order gives you calm.

Wearing perfume even when there is no special occasion.

Using the beautiful things you save for “some day”.

Going out for a walk.

Saying no.

Dressing well for yourself.

Closing a door when you need silence.

Not everything that does us good needs to become a method.

At CARO, wellbeing does not aim to teach you how to live or offer a formula to turn you into an improved version of yourself.

I am far more interested in remembering what, in the middle of the daily pace, we forget so easily.

That our time matters.

That our home has an influence.

That the way we present ourselves can also change how we feel.

That caring for ourselves should not be one more reason to demand more of ourselves.

There are stages in which our body, our energy and our priorities change. And perhaps it is precisely then that we need fewer speeches about control and more ability to listen to ourselves.

Not everything has to be solved immediately.

Not everything has to produce results.

Sometimes caring for yourself is simply about creating better conditions to feel well.

A home you can live in.

A wardrobe you can use.

A routine you can keep.

People with whom you can be yourself.

Moments that do not need to be photographed.

It may seem like little.

To me it is a great deal.

Because perhaps living well is not filling our days with things we must do.

Perhaps it is learning to inhabit them.

Wellbeing with meaning. Without noise and without perfect formulas.`,
        seoTitle: 'Caring for yourself without turning it into an obsession · CARO',
        seoDescription:
          'Wellbeing with meaning, without noise or perfect formulas. Caring for yourself from simplicity: creating better conditions to feel well, not another to-do list.',
      },
      fr: {
        title: 'Prendre soin de soi sans en faire une obsession',
        excerpt:
          'On n’a jamais autant parlé de bien-être. Un regard serein : prendre soin de soi sans perfection ni compétition, en créant de meilleures conditions pour se sentir bien.',
        body: `On n’a jamais autant parlé de bien-être.

Mieux dormir. Mieux manger. S’entraîner. Méditer. Respirer. Marcher. Prendre des compléments. S’organiser. Déconnecter.

Même se reposer semble être devenu une tâche de plus qu’il faudrait accomplir correctement.

Et c’est peut-être pour cela que j’ai envie d’aborder le bien-être autrement.

Sans perfection.

Sans compétition.

Sans transformer chaque aspect de notre vie en projet d’optimisation.

Prendre soin de soi peut aussi être quelque chose de beaucoup plus simple.

Dormir quand vous êtes fatiguée.

Bouger votre corps parce que cela vous fait du bien.

Vous asseoir pour manger sans regarder sans cesse votre téléphone.

Ranger un espace parce que vivre dans un certain ordre vous apaise.

Mettre du parfum même sans occasion particulière.

Utiliser les belles choses que vous gardez pour « un jour ».

Sortir marcher.

Dire non.

Bien vous habiller pour vous.

Fermer une porte quand vous avez besoin de silence.

Tout ce qui nous fait du bien n’a pas besoin de devenir une méthode.

Chez CARO, le bien-être ne prétend pas vous apprendre à vivre ni vous offrir une formule pour devenir une version améliorée de vous-même.

Ce qui m’intéresse bien davantage, c’est de rappeler ce que, dans le rythme du quotidien, nous oublions si facilement.

Que notre temps compte.

Que notre maison a une influence.

Que la manière dont nous nous présentons peut aussi changer ce que nous ressentons.

Que prendre soin de nous ne devrait pas être une raison de plus d’exiger davantage de nous-mêmes.

Il y a des étapes où notre corps, notre énergie et nos priorités changent. Et c’est peut-être justement alors que nous avons besoin de moins de discours sur le contrôle et de plus de capacité à nous écouter.

Tout n’a pas à être résolu immédiatement.

Tout n’a pas à produire des résultats.

Parfois, prendre soin de soi consiste simplement à créer de meilleures conditions pour se sentir bien.

Une maison que vous pouvez habiter.

Une garde-robe que vous pouvez utiliser.

Une routine que vous pouvez tenir.

Des personnes avec qui vous pouvez être vous-même.

Des moments qui n’ont pas besoin d’être photographiés.

Cela peut sembler peu.

Pour moi, c’est énorme.

Car bien vivre, ce n’est peut-être pas remplir nos journées de choses à faire.

C’est peut-être apprendre à les habiter.

Un bien-être qui a du sens. Sans bruit et sans formules parfaites.`,
        seoTitle: 'Prendre soin de soi sans en faire une obsession · CARO',
        seoDescription:
          'Un bien-être qui a du sens, sans bruit ni formules parfaites. Prendre soin de soi simplement : de meilleures conditions pour être bien, pas une tâche de plus.',
      },
    },
  },
  {
    id: 'edit-casa',
    slug: 'la-casa-como-extension-del-estilo',
    title: 'La casa como extensión del estilo',
    excerpt:
      'Hay casas bonitas que no dicen nada. Decorar bien no es llenar una casa, sino saber qué merece quedarse, y reconocerte en ella.',
    category: 'casa',
    author: 'Carolina Collaer',
    date: '2026-01-28',
    heroImage: '/media/caro/casa-extension-del-estilo-cojines-y-sofa-1024.webp',
    ogImage: '/media/caro/og/edit-la-casa-como-extension-del-estilo-e50824ad.jpg',
    body: `Hay casas bonitas que no dicen nada.

Y hay otras que, sin necesidad de ser perfectas, cuentan inmediatamente quién vive en ellas.

Para mí, ahí empieza el verdadero estilo.

No en tener una casa llena de objetos extraordinarios, sino en saber por qué algo merece entrar en ella.

Durante mucho tiempo hemos entendido la decoración como una suma de decisiones estéticas.

El sofá correcto.

La lámpara correcta.

El color correcto.

La mesa que “combina”.

Pero una casa con personalidad no se construye únicamente haciendo que todo combine.

Se construye eligiendo.

Un objeto encontrado durante un viaje.

Una pieza de arte que no deja indiferente.

Una cerámica imperfecta.

Una silla extraordinaria.

Un libro que seguimos abriendo.

Una mesa que envejece con nosotros.

Son esas decisiones las que terminan construyendo una casa que se siente propia.

Me interesa mucho más una habitación donde existe una pieza con carácter que un espacio perfectamente decorado pero completamente previsible.

Porque el buen gusto no consiste en borrar la personalidad.

Todo lo contrario.

Una casa puede ser sofisticada y tener color.

Puede ser minimalista y contener objetos poderosos.

Puede mezclar épocas, materiales y procedencias.

Puede tener una obra contemporánea junto a una pieza heredada y seguir teniendo absoluta coherencia.

La coherencia no siempre está en los objetos.

Está en la mirada de quien los eligió.

También creo que vivimos en una época en la que acumulamos demasiado.

Compramos decoración igual que compramos ropa: porque algo está de moda, porque lo vemos repetido, porque sentimos que un espacio todavía “necesita algo”.

Y quizá muchas veces necesite exactamente lo contrario.

Espacio.

Silencio visual.

Tiempo para descubrir qué queremos realmente conservar.

En CARO me interesa una casa construida poco a poco.

Una casa donde no todo sea inmediatamente sustituible.

Donde pueda convivir el diseño con la artesanía, el arte con los objetos cotidianos, lo nuevo con aquello que lleva años acompañándonos.

No quiero recomendar objetos únicamente porque sean bonitos.

Quiero preguntarme si aportan algo.

Si tienen presencia.

Si provocan una conversación.

Si podemos imaginar seguir mirándolos dentro de diez años.

Porque nuestra casa también forma parte de nuestra imagen.

No en el sentido de mostrarla a los demás.

En el sentido mucho más íntimo de reconocernos en ella.

Cuando cerramos la puerta, todo aquello que hemos elegido permanece.

Y quizá por eso decorar bien no consiste en llenar una casa.

Consiste en saber qué merece quedarse.

La casa también habla de ti. Procura que tenga algo interesante que decir.`,
    seoTitle: 'La casa como extensión del estilo · CARO. Casa de estilo',
    seoDescription:
      'Una casa con personalidad no se construye combinando, sino eligiendo. La mirada de CARO sobre decorar con intención: menos acumular, más reconocerte en ella.',
    relatedProductSlugs: [
      'hamed-ouattara-taburete',
      'hamed-ouattara-tassaba-set',
      'naor-ferrari-teddy',
    ],
    i18n: {
      en: {
        title: 'The home as an extension of style',
        excerpt:
          'There are beautiful homes that say nothing. Decorating well isn’t filling a home, but knowing what deserves to stay, and recognising yourself in it.',
        body: `There are beautiful homes that say nothing.

And there are others that, without needing to be perfect, immediately tell you who lives in them.

For me, that is where real style begins.

Not in having a home full of extraordinary objects, but in knowing why something deserves to come into it.

For a long time we have understood decorating as a sum of aesthetic decisions.

The right sofa.

The right lamp.

The right colour.

The table that “matches”.

But a home with personality is not built simply by making everything match.

It is built by choosing.

An object found on a trip.

A piece of art that leaves no one indifferent.

An imperfect ceramic.

An extraordinary chair.

A book we keep opening.

A table that ages with us.

It is those decisions that end up building a home that feels your own.

I’m far more interested in a room where one piece has character than in a space perfectly decorated but completely predictable.

Because good taste is not about erasing personality.

Quite the opposite.

A home can be sophisticated and have colour.

It can be minimalist and hold powerful objects.

It can mix eras, materials and origins.

It can place a contemporary work beside an inherited piece and still be entirely coherent.

Coherence is not always in the objects.

It is in the eye of the one who chose them.

I also believe we live in an age when we accumulate too much.

We buy décor the way we buy clothes: because something is in fashion, because we see it everywhere, because we feel a space still “needs something”.

And perhaps, very often, it needs exactly the opposite.

Space.

Visual silence.

Time to discover what we really want to keep.

At CARO I’m interested in a home built little by little.

A home where not everything is immediately replaceable.

Where design can live with craft, art with everyday objects, the new with what has kept us company for years.

I don’t want to recommend objects only because they are beautiful.

I want to ask whether they add something.

Whether they have presence.

Whether they spark a conversation.

Whether we can imagine still looking at them ten years from now.

Because our home is also part of our image.

Not in the sense of showing it to others.

In the far more intimate sense of recognising ourselves in it.

When we close the door, everything we have chosen remains.

And perhaps that is why decorating well is not about filling a home.

It is about knowing what deserves to stay.

Your home speaks about you too. Make sure it has something interesting to say.`,
        seoTitle: 'The home as an extension of style · CARO. Casa de estilo',
        seoDescription:
          'A home with personality isn’t built by matching, but by choosing. CARO’s view on decorating with intention: less accumulating, more recognising yourself in it.',
      },
      fr: {
        title: 'La maison comme prolongement du style',
        excerpt:
          'Il y a de belles maisons qui ne disent rien. Bien décorer, ce n’est pas remplir une maison, c’est savoir ce qui mérite de rester, et vous y reconnaître.',
        body: `Il y a de belles maisons qui ne disent rien.

Et il y en a d’autres qui, sans avoir besoin d’être parfaites, racontent immédiatement qui y vit.

Pour moi, c’est là que commence le vrai style.

Non pas dans le fait d’avoir une maison remplie d’objets extraordinaires, mais dans le fait de savoir pourquoi quelque chose mérite d’y entrer.

Pendant longtemps, nous avons compris la décoration comme une somme de décisions esthétiques.

Le bon canapé.

La bonne lampe.

La bonne couleur.

La table qui « va avec ».

Mais une maison qui a de la personnalité ne se construit pas uniquement en faisant en sorte que tout aille ensemble.

Elle se construit en choisissant.

Un objet trouvé au cours d’un voyage.

Une œuvre d’art qui ne laisse pas indifférent.

Une céramique imparfaite.

Une chaise extraordinaire.

Un livre que nous continuons d’ouvrir.

Une table qui vieillit avec nous.

Ce sont ces décisions qui finissent par construire une maison que l’on sent vraiment sienne.

Je m’intéresse bien davantage à une pièce qui abrite un objet de caractère qu’à un espace parfaitement décoré mais complètement prévisible.

Car le bon goût ne consiste pas à effacer la personnalité.

Bien au contraire.

Une maison peut être sophistiquée et avoir de la couleur.

Elle peut être minimaliste et contenir des objets puissants.

Elle peut mêler les époques, les matières et les provenances.

Elle peut accueillir une œuvre contemporaine à côté d’une pièce héritée et garder une cohérence absolue.

La cohérence n’est pas toujours dans les objets.

Elle est dans le regard de qui les a choisis.

Je crois aussi que nous vivons à une époque où nous accumulons trop.

Nous achetons de la décoration comme nous achetons des vêtements : parce que quelque chose est à la mode, parce que nous le voyons partout, parce que nous avons le sentiment qu’un espace « a encore besoin de quelque chose ».

Et peut-être que, bien souvent, il a besoin exactement du contraire.

D’espace.

De silence visuel.

De temps pour découvrir ce que nous voulons vraiment garder.

Chez CARO, ce qui m’intéresse, c’est une maison construite peu à peu.

Une maison où tout n’est pas immédiatement remplaçable.

Où peuvent cohabiter le design et l’artisanat, l’art et les objets du quotidien, le neuf et ce qui nous accompagne depuis des années.

Je ne veux pas recommander des objets uniquement parce qu’ils sont beaux.

Je veux me demander s’ils apportent quelque chose.

S’ils ont une présence.

S’ils suscitent une conversation.

Si nous pouvons imaginer continuer à les regarder dans dix ans.

Car notre maison fait aussi partie de notre image.

Non pas au sens de la montrer aux autres.

Au sens, bien plus intime, de nous y reconnaître.

Quand nous fermons la porte, tout ce que nous avons choisi demeure.

Et c’est peut-être pour cela que bien décorer ne consiste pas à remplir une maison.

Cela consiste à savoir ce qui mérite de rester.

La maison parle aussi de vous. Faites en sorte qu’elle ait quelque chose d’intéressant à dire.`,
        seoTitle: 'La maison comme prolongement du style · CARO. Casa de estilo',
        seoDescription:
          'Une maison qui a de la personnalité ne se construit pas en assortissant, mais en choisissant. Le regard de CARO : décorer avec intention et s’y reconnaître.',
      },
    },
  },
  {
    id: 'edit-mujer-40',
    slug: 'mujer-40-la-edad-de-la-intencion',
    title: 'Los 40+, la edad de la intención',
    excerpt:
      'Cumplir años no resta posibilidades: filtra. Los 40+ como la edad de la intención: dejar de vestirnos para convertirnos en alguien y empezar a hacerlo para reconocernos.',
    category: 'mujer-40',
    author: 'Carolina Collaer',
    date: '2026-01-22',
    heroImage: '/media/caro/mujer-40-la-edad-de-la-intencion-carolina-collaer-1088.webp',
    ogImage: '/media/caro/og/edit-mujer-40-la-edad-de-la-intencion-4717e923.jpg',
    body: `No recuerdo haber cumplido 40 y haber despertado al día siguiente convertida en otra mujer.

No existe esa frontera.

No cambia de repente nuestro gusto, nuestro cuerpo, nuestra forma de vestir ni aquello que queremos de la vida.

Y, sin embargo, algo sí empieza a suceder.

Comenzamos a seleccionar.

Con más claridad.

Con menos paciencia para aquello que ya no nos interesa.

Y quizá con bastante menos necesidad de explicar nuestras decisiones.

Por eso me incomoda tanto cuando se habla de los 40 como si fueran el comienzo de una larga lista de prohibiciones.

Qué falda ya no deberíamos llevar.

Qué longitud es apropiada.

Qué colores envejecen.

Qué maquillaje deberíamos abandonar.

Qué parte del cuerpo conviene esconder.

No creo que cumplir años nos obligue a volvernos discretamente invisibles.

Creo que debería ocurrir exactamente lo contrario.

Con los años tenemos la oportunidad de conocernos lo suficiente como para poder elegir con mucha más libertad.

Puedes llevar una minifalda.

Puedes llevar un traje masculino.

Puedes vestirte completamente de negro o aparecer con un color extraordinario.

Puedes llevar grandes gafas, joyas importantes, zapatos imposibles o una camisa blanca impecable.

La edad no decide eso.

Lo decide la manera en que tú lo llevas.

Lo interesante de esta etapa no es que aprendamos nuevas reglas.

Es que empezamos a descubrir cuáles ya no necesitamos.

Quizá hemos comprado suficientes prendas que nunca usamos.

Probado suficientes tendencias que no nos representaban.

Aceptado suficientes opiniones sobre nuestro cuerpo.

Conservado suficientes cosas por obligación.

Y llega un momento en que queremos otra cosa.

Menos ruido.

Más intención.

Eso también ocurre con la belleza.

No porque dejemos de querer vernos bien.

Todo lo contrario.

Pero quizá empezamos a preguntarnos para quién estábamos intentando parecernos a una versión más joven de nosotras mismas.

Nuestro rostro cambia.

Nuestro cuerpo cambia.

Nuestra energía cambia.

Y nuestra imagen también puede hacerlo sin convertirse en una batalla contra el tiempo.

Personalmente, me interesa muchísimo más una mujer interesante que una mujer empeñada en parecer eternamente joven.

La primera tiene historia.

Decisiones.

Contradicciones.

Humor.

Presencia.

Y eso no puede comprarse en un frasco.

También hay algo extraordinario que sucede con la experiencia: empezamos a entender que sofisticación y exceso no son sinónimos.

Que podemos comprar menos y elegir mejor.

Que no necesitamos llenar nuestro armario para tener estilo.

Que un buen perfume puede formar parte de nuestra identidad.

Que nuestra casa también nos representa.

Que cuidarnos no debería significar vivir pendientes de corregirnos.

Y que decir “esto no es para mí” puede ser una de las decisiones más elegantes que tomemos.

Por eso me gusta hablar de los 40+ como la edad de la intención.

No porque antes no la tengamos.

Sino porque con el tiempo empezamos a comprender el valor que tiene.

Sabemos mejor dónde queremos estar.

A quién queremos cerca.

Qué queremos vestir.

Qué queremos conservar.

Y también qué estamos perfectamente dispuestas a dejar ir.

Cumplir años no resta posibilidades.

Filtra.

Y cuando aprendemos a elegir desde ahí, ocurre algo muy interesante:

dejamos de vestir, comprar o cuidarnos para convertirnos en alguien.

Empezamos a hacerlo para reconocernos.

Los años no deberían reducir tu estilo. Deberían afinar tu elección.`,
    seoTitle: 'Los 40+, la edad de la intención · CARO. Casa de estilo',
    seoDescription:
      'A partir de los 40 empezamos a seleccionar con más claridad. La mirada de CARO sobre la edad de la intención: menos prohibiciones, más libertad para elegir.',
    relatedProductSlugs: [
      'surkana-bolso-hombro-charol-rojo',
      'surkana-jersey-cuello-perkins-relieve-verde',
      'pulsera-lind-16lind201',
    ],
    i18n: {
      en: {
        title: '40+, the age of intention',
        excerpt:
          'Getting older doesn’t subtract possibilities: it filters. 40+ as the age of intention: we stop dressing to become someone and start doing it to recognise ourselves.',
        body: `I don’t remember turning 40 and waking up the next day as a different woman.

That border does not exist.

Our taste, our body, our way of dressing and what we want from life do not suddenly change.

And yet, something does begin to happen.

We start to select.

With more clarity.

With less patience for what no longer interests us.

And perhaps with far less need to explain our decisions.

That is why it bothers me so much when 40 is talked about as the start of a long list of prohibitions.

Which skirt we should no longer wear.

What length is appropriate.

Which colours age us.

What make-up we should give up.

Which part of the body is best hidden.

I don’t believe getting older obliges us to become discreetly invisible.

I believe exactly the opposite should happen.

With the years we have the chance to know ourselves well enough to choose with far more freedom.

You can wear a miniskirt.

You can wear a masculine suit.

You can dress entirely in black or show up in an extraordinary colour.

You can wear big glasses, important jewellery, impossible shoes or an impeccable white shirt.

Age does not decide that.

The way you carry it does.

What is interesting about this stage is not that we learn new rules.

It is that we start to discover which ones we no longer need.

Perhaps we have bought enough clothes we never wore.

Tried enough trends that did not represent us.

Accepted enough opinions about our body.

Kept enough things out of obligation.

And there comes a moment when we want something else.

Less noise.

More intention.

The same happens with beauty.

Not because we stop wanting to look good.

Quite the opposite.

But perhaps we start to wonder for whom we were trying to resemble a younger version of ourselves.

Our face changes.

Our body changes.

Our energy changes.

And our image can change too, without becoming a battle against time.

Personally, I’m far more interested in an interesting woman than in one determined to look eternally young.

The first has history.

Decisions.

Contradictions.

Humour.

Presence.

And that cannot be bought in a bottle.

There is also something extraordinary that comes with experience: we begin to understand that sophistication and excess are not synonyms.

That we can buy less and choose better.

That we don’t need to fill our wardrobe to have style.

That a good perfume can be part of our identity.

That our home represents us too.

That caring for ourselves should not mean living intent on correcting ourselves.

And that saying “this is not for me” can be one of the most elegant decisions we make.

That is why I like to speak of 40+ as the age of intention.

Not because we didn’t have it before.

But because, with time, we begin to understand its value.

We know better where we want to be.

Who we want close.

What we want to wear.

What we want to keep.

And also what we are perfectly willing to let go.

Getting older does not subtract possibilities.

It filters.

And when we learn to choose from there, something very interesting happens:

we stop dressing, buying or caring for ourselves to become someone.

We start doing it to recognise ourselves.

The years should not shrink your style. They should sharpen your choice.`,
        seoTitle: '40+, the age of intention · CARO. Casa de estilo',
        seoDescription:
          'From 40 onward we start selecting with more clarity. CARO’s view on the age of intention: fewer prohibitions, more freedom to choose.',
      },
      fr: {
        title: 'Les 40+, l’âge de l’intention',
        excerpt:
          'Prendre de l’âge ne retire pas de possibilités : cela filtre. Les 40+ comme l’âge de l’intention : cesser de nous habiller pour devenir quelqu’un et commencer à le faire pour nous reconnaître.',
        body: `Je ne me souviens pas d’avoir eu 40 ans et de m’être réveillée le lendemain transformée en une autre femme.

Cette frontière n’existe pas.

Notre goût, notre corps, notre façon de nous habiller et ce que nous attendons de la vie ne changent pas du jour au lendemain.

Et pourtant, quelque chose commence bel et bien à se produire.

Nous commençons à sélectionner.

Avec plus de clarté.

Avec moins de patience pour ce qui ne nous intéresse plus.

Et peut-être en éprouvant bien moins le besoin d’expliquer nos décisions.

C’est pourquoi cela me dérange tant que l’on parle des 40 ans comme du début d’une longue liste d’interdits.

Quelle jupe nous ne devrions plus porter.

Quelle longueur est appropriée.

Quelles couleurs vieillissent.

Quel maquillage nous devrions abandonner.

Quelle partie du corps il vaudrait mieux cacher.

Je ne crois pas que prendre de l’âge nous oblige à devenir discrètement invisibles.

Je crois que c’est exactement le contraire qui devrait se produire.

Avec les années, nous avons l’occasion de nous connaître suffisamment pour pouvoir choisir avec beaucoup plus de liberté.

Vous pouvez porter une minijupe.

Vous pouvez porter un costume masculin.

Vous pouvez vous habiller entièrement en noir ou apparaître dans une couleur extraordinaire.

Vous pouvez porter de grandes lunettes, des bijoux importants, des chaussures impossibles ou une chemise blanche impeccable.

Ce n’est pas l’âge qui en décide.

C’est la manière dont vous le portez.

Ce qui est intéressant dans cette étape, ce n’est pas que nous apprenions de nouvelles règles.

C’est que nous commencions à découvrir celles dont nous n’avons plus besoin.

Nous avons peut-être acheté assez de vêtements que nous n’avons jamais portés.

Essayé assez de tendances qui ne nous ressemblaient pas.

Accepté assez d’opinions sur notre corps.

Gardé assez de choses par obligation.

Et il arrive un moment où nous voulons autre chose.

Moins de bruit.

Plus d’intention.

C’est aussi vrai pour la beauté.

Non pas parce que nous cessons de vouloir être à notre avantage.

Bien au contraire.

Mais peut-être commençons-nous à nous demander pour qui nous essayions de ressembler à une version plus jeune de nous-mêmes.

Notre visage change.

Notre corps change.

Notre énergie change.

Et notre image peut changer aussi, sans devenir une bataille contre le temps.

Personnellement, je m’intéresse bien plus à une femme intéressante qu’à une femme qui s’obstine à paraître éternellement jeune.

La première a une histoire.

Des décisions.

Des contradictions.

De l’humour.

De la présence.

Et cela ne s’achète pas en flacon.

Il y a aussi quelque chose d’extraordinaire qui arrive avec l’expérience : nous commençons à comprendre que sophistication et excès ne sont pas synonymes.

Que nous pouvons acheter moins et choisir mieux.

Que nous n’avons pas besoin de remplir notre garde-robe pour avoir du style.

Qu’un beau parfum peut faire partie de notre identité.

Que notre maison nous représente aussi.

Que prendre soin de nous ne devrait pas signifier vivre dans le souci permanent de nous corriger.

Et que dire « ce n’est pas pour moi » peut être l’une des décisions les plus élégantes que nous prenions.

C’est pourquoi j’aime parler des 40+ comme de l’âge de l’intention.

Non pas parce que nous n’en avions pas avant.

Mais parce qu’avec le temps, nous commençons à en comprendre la valeur.

Nous savons mieux où nous voulons être.

Qui nous voulons près de nous.

Ce que nous voulons porter.

Ce que nous voulons garder.

Et aussi ce que nous sommes parfaitement prêtes à laisser partir.

Prendre de l’âge ne retire pas de possibilités.

Cela filtre.

Et quand nous apprenons à choisir à partir de là, il se passe quelque chose de très intéressant :

nous cessons de nous habiller, d’acheter ou de prendre soin de nous pour devenir quelqu’un.

Nous commençons à le faire pour nous reconnaître.

Les années ne devraient pas réduire votre style. Elles devraient affiner vos choix.`,
        seoTitle: 'Les 40+, l’âge de l’intention · CARO. Casa de estilo',
        seoDescription:
          'À partir de 40 ans, nous choisissons avec plus de clarté. Le regard de CARO sur l’âge de l’intention : moins d’interdits, plus de liberté pour choisir.',
      },
    },
  },
];

/**
 * Permanent redirects for renamed article slugs (old → new). The Worker issues a
 * 301 for `/the-edit/post/<oldSlug>` so existing links, shares and search-engine
 * indexes keep working after a slug change. Add an entry whenever an article's
 * `slug` above changes; never remove one (the old URL may still be linked).
 */
export const ARTICLE_REDIRECTS: Record<string, string> = {
  // Renamed 2026-09-02 — content no longer about "cosmética coreana / 40+".
  'cosmetica-coreana-mujer-40': 'belleza-reconocerse-y-cuidarse',
  // Renamed 2026-09-02 — content no longer about perimenopausia.
  'bienestar-y-perimenopausia': 'bienestar-sin-obsesion',
};
